import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrency } from '../hooks/useCurrency';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import {
  Settings, Globe, Bell, Shield, RefreshCw, Play,
  Zap, Lock, Key, Server, Clock, Check, X, Save,
  Eye, EyeOff, Copy, Activity, HardDrive, Info, Wallet,
  AlertTriangle, Network, Power, Terminal,
  BarChart3, Mail, MessageSquare, Hash, TrendingDown,
  Layers, BadgeCheck, Radio, Sliders, CreditCard, Crown, ChevronRight, ExternalLink, ShieldCheck,
  User as UserIcon, Plus, Search as SearchIcon,
  Filter, Download, LogIn, Trash2, ChevronDown, Sparkles,
} from 'lucide-react';
import { Spinner } from '../components/ui';
import { useSystemHealth } from '../hooks/useData';
import { useAuth } from '../hooks/useAuth';
import { getTierConfig } from '../constants/tiers';
import MexcDisclaimerModal, { useMexcDisclaimer } from '../components/MexcDisclaimerModal';
import PayTierWithCrypto from '../components/PayTierWithCrypto';
import {
  triggerOrchestrator, triggerHealthCheck,
  getSystemOverview, getMgmtAuditLog, mgmtTriggerForecastResolver, mgmtRefreshCcxt, mgmtResendVerification,
  getAdminConfig, saveAdminConfig,
  getSubscriptionStatus, createCheckoutSession, getBillingPortal,
  getMfaStatus, setupMfa, verifyMfaSetup, disableMfa, setupAdmin,
  changePassword,
  getTelegramStatus, generateTelegramLink, verifyTelegramLink, disconnectTelegram,
} from '../services/api';
import * as api from '../services/api';

// ─── Cloud config helpers ─────────────────────────────────────────────────────
// Settings are persisted server-side via the admin config API.
// Only the wallet display address (a UI preference) uses local browser storage.

async function loadCloudSettings() {
  try {
    const config = await getAdminConfig();
    return config || {};
  } catch {
    return {};
  }
}

async function saveCloudSetting(key, value) {
  await saveAdminConfig(key, String(value));
}

// Wallet address is a UI-only preference — no server storage needed.
const WALLET_KEY = 'mcn_bot_wallet_address';

// ─── Tabs ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'general',       label: 'General',       icon: Settings   },
  { id: 'trading',       label: 'Trading',        icon: Zap        },
  { id: 'risk',          label: 'Risk',           icon: Shield     },
  { id: 'notifications', label: 'Notifications',  icon: Bell       },
  { id: 'connections',   label: 'Connections',    icon: Network    },
  { id: 'security',      label: 'Security',       icon: Lock       },
  { id: 'wallet',        label: 'Payout Wallet',  icon: Wallet     },
  { id: 'subscription',  label: 'Subscription',   icon: CreditCard },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function Toggle({ enabled, onChange, size = 'md' }) {
  const w   = size === 'sm' ? 'w-8 h-4'  : 'w-11 h-6';
  const dot = size === 'sm' ? 'w-3 h-3 translate-x-0.5' : 'w-4 h-4 translate-x-1';
  const on  = size === 'sm' ? 'translate-x-4' : 'translate-x-6';
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex ${w} items-center rounded-full transition-colors duration-200 focus:outline-none ${
        enabled ? 'bg-accent-primary' : 'bg-dark-600'
      }`}
    >
      <span className={`inline-block ${dot} rounded-full bg-white shadow transform transition-transform duration-200 ${enabled ? on : ''}`} />
    </button>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <h3 className="text-white font-semibold text-sm">{title}</h3>
        {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3.5 border-b border-dark-700/50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{label}</p>
        {description && <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{description}</p>}
      </div>
      <div className="flex-shrink-0 flex items-center">{children}</div>
    </div>
  );
}

function NumberInput({ value, onChange, min, max, step = 0.1, unit }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        min={min} max={max} step={step}
        className="w-20 bg-dark-700 border border-dark-600 rounded-lg px-2.5 py-1.5 text-white text-sm text-right focus:outline-none focus:border-accent-primary"
      />
      {unit && <span className="text-gray-400 text-xs">{unit}</span>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text', monospace }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600 ${monospace ? 'font-mono text-xs' : ''}`}
    />
  );
}

function SaveBar({ onSave, saving, saved }) {
  return (
    <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-dark-700/50">
      {saved && (
        <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
          <Check className="w-4 h-4" /> Saved to platform
        </span>
      )}
      <button onClick={onSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
        {saving ? <Spinner size="sm" /> : <Save className="w-3.5 h-3.5" />}
        Save Changes
      </button>
    </div>
  );
}

function Toast({ message, onClose }) {
  if (!message) return null;
  const isError = message.type === 'error';
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border text-sm font-medium animate-fade-in ${
      isError
        ? 'bg-red-500/10 border-red-500/30 text-red-400'
        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
    }`}>
      {isError ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
      {message.text}
      <button onClick={onClose} className="ml-2 text-gray-500 hover:text-gray-300">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function StatPill({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-dark-700 rounded-xl p-4 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-gray-500 text-xs mt-1">{label}</p>
    </div>
  );
}

// ─── Profile section — name editing ─────────────────────────────────────────
//
// Lives at the top of Settings → General. Lets users update their display
// name (single-field "Full name" — same shape as registration). Email is
// shown but not editable here (changing email needs verification flow we
// haven't built yet). For Google OAuth users, the name comes from Google
// initially; this lets them override it.

function ProfileSection({ onToast }) {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [edited, setEdited] = useState(false);

  // Avatar state — preview + pending upload. Same resize-to-256 approach the
  // community profile editor uses, so files land at ~30 KB after JPEG encoding.
  const [avatarPreview, setAvatarPreview] = useState(null);
  // undefined = no pending change; null = remove; string = new data URL
  const [pendingAvatar, setPendingAvatar] = useState(undefined);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Email verification banner state
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Sync local input with the latest user value when it loads / changes.
  useEffect(() => {
    if (user?.name && !edited) setName(user.name);
    if (user?.avatarUrl !== undefined && pendingAvatar === undefined) {
      setAvatarPreview(user.avatarUrl || null);
    }
  }, [user?.name, user?.avatarUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 80;
  const nameDirty = edited && trimmed !== (user?.name || '');
  const avatarDirty = pendingAvatar !== undefined;
  const dirty = nameDirty || avatarDirty;

  function pickAvatar(file) {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) {
      onToast?.({ type: 'error', text: 'Avatar must be a JPEG, PNG, or WebP image' });
      return;
    }
    setAvatarBusy(true);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      // Center-crop to square then resize to 256
      const size = Math.min(img.width, img.height);
      const sx = (img.width - size) / 2;
      const sy = (img.height - size) / 2;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, 256, 256);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setAvatarPreview(dataUrl);
      setPendingAvatar(dataUrl);
      setAvatarBusy(false);
      onToast?.({ type: 'success', text: 'Avatar ready — click Save changes to apply' });
    };
    img.onerror = () => {
      setAvatarBusy(false);
      onToast?.({ type: 'error', text: 'Could not read image file' });
    };
    img.src = URL.createObjectURL(file);
  }

  function removeAvatar() {
    setAvatarPreview(null);
    setPendingAvatar(null);
  }

  async function save() {
    if (!dirty) return;
    setBusy(true);
    try {
      const updates = {};
      if (nameDirty && valid) updates.name = trimmed;
      if (avatarDirty) updates.avatarUrl = pendingAvatar; // string OR null
      const r = await api.updateMyProfile(updates);
      if (r?.success) {
        onToast?.({ type: 'success', text: 'Profile updated' });
        if (typeof refreshUser === 'function') await refreshUser();
        setEdited(false);
        setPendingAvatar(undefined);
      } else {
        onToast?.({ type: 'error', text: r?.error?.message || 'Update failed' });
      }
    } catch (err) {
      onToast?.({ type: 'error', text: err.message || 'Update failed' });
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setName(user?.name || '');
    setEdited(false);
    setAvatarPreview(user?.avatarUrl || null);
    setPendingAvatar(undefined);
  }

  async function sendVerification() {
    setSendingVerification(true);
    try {
      const r = await api.sendEmailVerification();
      if (r?.alreadyVerified) {
        onToast?.({ type: 'success', text: 'Already verified — no action needed' });
      } else {
        setVerificationSent(true);
        onToast?.({ type: 'success', text: 'Verification email sent. Check your inbox.' });
      }
    } catch (err) {
      onToast?.({ type: 'error', text: err.message || 'Could not send verification email' });
    }
    setSendingVerification(false);
  }

  if (!user) return null;

  const needsVerification = !user.emailVerified && user.authMethod !== 'google';

  return (
    <div className="space-y-6">
      {/* ─── Email verification banner ─────────────────────────────── */}
      {needsVerification && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-300">Verify your email address</p>
            <p className="text-[11px] text-gray-300 mt-1">
              Your account is active but your email isn't confirmed. Verifying lets us deliver alerts reliably and is required for some security features.
            </p>
          </div>
          <button onClick={sendVerification} disabled={sendingVerification || verificationSent}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 border border-amber-500/40 flex-shrink-0 disabled:opacity-50">
            {sendingVerification ? <Spinner size="sm" /> : verificationSent ? '✓ Sent' : 'Send verification'}
          </button>
        </div>
      )}

      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={UserIcon} title="Your profile"
          subtitle="How your name and avatar appear across haythix — in chat, the marketplace, and your community profile." />

        <div className="space-y-5">
          {/* Identity card */}
          <div className="flex items-center gap-3 p-3 bg-dark-700/40 rounded-xl border border-dark-600/40">
            <Avatar src={avatarPreview} name={user.name || user.email} size={48} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-400">Signed in as</div>
              <div className="text-sm text-white font-mono truncate flex items-center gap-2">
                {user.email}
                {user.emailVerified ? (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                    <Check className="w-2.5 h-2.5" /> Verified
                  </span>
                ) : user.authMethod !== 'google' ? (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    Unverified
                  </span>
                ) : null}
              </div>
            </div>
            {user.authMethod === 'google' && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-400/15 text-blue-300 border border-blue-400/30">
                Google
              </span>
            )}
          </div>

          {/* ─── Avatar upload ─────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Avatar</label>
            <div className="flex items-start gap-4">
              <div className="relative">
                <Avatar src={avatarPreview} name={user.name || user.email} size={96} />
                {avatarBusy && (
                  <div className="absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => pickAvatar(e.target.files?.[0])} />
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => fileInputRef.current?.click()} disabled={avatarBusy}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 border border-violet-500/30 flex items-center gap-1.5 disabled:opacity-50">
                    <Plus className="w-3.5 h-3.5" /> {avatarPreview ? 'Change image' : 'Upload image'}
                  </button>
                  {avatarPreview && (
                    <button onClick={removeAvatar} disabled={avatarBusy}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-dark-700 text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-dark-600">
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500">
                  JPEG, PNG, or WebP. Auto-cropped to a square and resized to 256×256. Shown across the platform — chat, public profile, the avatar menu.
                </p>
              </div>
            </div>
          </div>

          {/* Name input */}
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-300 mb-1.5">Full name</label>
            <input
              id="profile-name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setEdited(true); }}
              placeholder="e.g. Humayun Khan"
              maxLength={80}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {trimmed.length === 0 ? 'Name cannot be empty.' : `${trimmed.length}/80 characters.`}
              {!user.name && (
                <span className="text-amber-400 ml-2">⚠ You don't have a display name yet — please set one.</span>
              )}
            </p>
          </div>

          {/* Actions */}
          {dirty && (
            <div className="flex gap-2">
              <button onClick={save} disabled={(nameDirty && !valid) || busy}
                className="btn-primary flex items-center gap-2 disabled:opacity-50">
                {busy ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
                Save changes
              </button>
              <button onClick={cancel} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white border border-dark-600 hover:border-dark-500 transition-colors">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact avatar renderer — used in the identity card AND the upload preview.
function Avatar({ src, name, size }) {
  const initial = ((name || '?')[0] || '?').toUpperCase();
  const dim = `${size}px`;
  if (src) {
    return <img src={src} alt="" style={{ width: dim, height: dim }} className="rounded-2xl object-cover ring-2 ring-violet-500/20" />;
  }
  return (
    <div className="rounded-2xl bg-gradient-to-br from-violet-500/30 to-purple-500/30 text-violet-100 font-bold flex items-center justify-center ring-2 ring-violet-500/20"
      style={{ width: dim, height: dim, fontSize: Math.round(size * 0.4) }}>
      {initial}
    </div>
  );
}

// ─── Tab Panels ──────────────────────────────────────────────────────────────

function GeneralTab({ health, loading, refresh, onToast }) {
  const [triggering, setTriggering] = useState(null);

  const trigger = async (action) => {
    setTriggering(action);
    try {
      if (action === 'orchestrator') {
        await triggerOrchestrator();
        onToast({ type: 'success', text: 'Trade orchestrator triggered successfully' });
      } else {
        await triggerHealthCheck();
        onToast({ type: 'success', text: 'Health check completed' });
        refresh();
      }
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setTriggering(null);
    }
  };

  const statusColor =
    health?.status === 'healthy' ? 'text-emerald-400' :
    health?.status === 'warning' ? 'text-amber-400'   : 'text-red-400';
  const statusBg =
    health?.status === 'healthy' ? 'bg-emerald-500/10 border-emerald-500/20' :
    health?.status === 'warning' ? 'bg-amber-500/10 border-amber-500/20'     : 'bg-red-500/10 border-red-500/20';

  return (
    <div className="space-y-6">

      {/* Profile — name editing (was a launch gap: users were stuck with the
          email-prefix as their display name) */}
      <ProfileSection onToast={onToast} />

      {/* System Health */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Activity} title="System Health" subtitle="Real-time status of all platform components" />

        {loading ? (
          <div className="flex justify-center py-8"><Spinner className="text-accent-primary" /></div>
        ) : health ? (
          <div className="space-y-4">
            <div className={`flex items-center justify-between p-4 rounded-xl border ${statusBg}`}>
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${
                  health.status === 'healthy' ? 'bg-emerald-400' :
                  health.status === 'warning' ? 'bg-amber-400'   : 'bg-red-400'
                }`} style={health.status === 'healthy' ? { boxShadow: '0 0 8px #34d399' } : undefined} />
                <div>
                  <p className="text-white font-medium text-sm">Overall Platform Status</p>
                  <p className="text-gray-500 text-xs">Last checked: {new Date(health.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-widest ${statusColor}`}>{health.status}</span>
                <button onClick={refresh} disabled={loading} className="btn-ghost p-1.5 ml-2">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatPill label="Total Bots"  value={health.bots?.total   || 0} />
              <StatPill label="Healthy"     value={health.bots?.healthy || 0} color="text-emerald-400" />
              <StatPill label="Warning"     value={health.bots?.warning || 0} color="text-amber-400"   />
              <StatPill label="Paused"      value={health.bots?.paused  || 0} color="text-gray-400"    />
            </div>

            <div className="flex items-center gap-2 text-gray-500 text-xs px-1">
              <Info className="w-3.5 h-3.5" />
              Active sessions: <span className="text-white font-medium ml-1">{health.activeSessions || 0}</span>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8 text-sm">Unable to load health data</p>
        )}
      </div>

      {/* Manual Operations */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Terminal} title="Manual Operations" subtitle="Trigger system functions on-demand" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-dark-700/60 rounded-xl p-5 border border-dark-600/30">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent-primary/15 text-accent-primary flex items-center justify-center flex-shrink-0">
                <Play className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">Trade Orchestrator</p>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                  Manually trigger daily trade planning. Normally scheduled at 09:00 UTC.
                </p>
                <button onClick={() => trigger('orchestrator')} disabled={!!triggering}
                  className="btn-primary text-xs flex items-center gap-1.5 mt-4">
                  {triggering === 'orchestrator'
                    ? <><Spinner size="sm" /> Running...</>
                    : <><Zap className="w-3 h-3" /> Trigger Now</>}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-dark-700/60 rounded-xl p-5 border border-dark-600/30">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0">
                <Activity className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">System Health Check</p>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                  Full platform diagnostic and generate daily summary report.
                </p>
                <button onClick={() => trigger('health')} disabled={!!triggering}
                  className="btn-secondary text-xs flex items-center gap-1.5 mt-4">
                  {triggering === 'health'
                    ? <><Spinner size="sm" /> Checking...</>
                    : <><RefreshCw className="w-3 h-3" /> Run Check</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Info} title="About Platform" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: 'Version',     value: 'v1.0.0' },
            { label: 'Environment', value: import.meta.env.MODE === 'production' ? 'Production' : 'Development' },
            { label: 'Build',       value: __BUILD_DATE__ || 'dev' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-dark-700/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs">{label}</p>
              <p className="text-white text-sm font-medium mt-1">{value}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-600 text-xs mt-4">
          haythix — Enterprise automated crypto trading on Ethereum, Polygon, Arbitrum, and Base.
          Built with React 18, Node.js serverless functions, and ethers.js v6.
        </p>
      </div>

      {/* Tax CSV Export */}
      <TaxExportSection />
    </div>
  );
}

// ─── Tax Export ─────────────────────────────────────────────────────────────
//
// Premium tax-grade export: real fills from Smart Trade / Grid Bot / DCA
// Safety / Algo Orders + tips received as income. Format selector for
// Koinly / CoinTracker / CoinLedger / Generic. Preview-before-download.
// Per-user export history.

function yyyymmdd(d) { return d.toISOString().slice(0, 10); }
function dateNDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return yyyymmdd(d); }
function startOfYear(yr) { return `${yr}-01-01`; }
function endOfYear(yr) { return `${yr}-12-31`; }

const EXPORT_FORMATS = [
  { id: 'koinly',      label: 'Koinly',       hint: 'Most popular — clean Trade rows' },
  { id: 'cointracker', label: 'CoinTracker',  hint: 'Income rows labeled "Income"' },
  { id: 'coinledger',  label: 'CoinLedger',   hint: 'Same shape, accepted' },
  { id: 'generic',     label: 'Generic CSV',  hint: 'Universal format' },
];

function TaxExportSection({ onToast }) {
  const today = yyyymmdd(new Date());
  const thisYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(startOfYear(thisYear));
  const [endDate, setEndDate] = useState(today);
  const [format, setFormat] = useState('koinly');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [history, setHistory] = useState(null);

  const safeToast = onToast || (() => {});

  const fetchPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const p = await api.getTaxExportPreview(startDate, endDate);
      setPreview(p);
    } catch (err) {
      safeToast({ type: 'error', text: `Preview failed: ${err.message}` });
    } finally { setLoadingPreview(false); }
  }, [startDate, endDate, safeToast]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);
  useEffect(() => {
    api.getTaxExportHistory().then(d => setHistory(d?.exports || [])).catch(() => setHistory([]));
  }, []);

  function applyQuickRange(range) {
    if (range === 'this-year')  { setStartDate(startOfYear(thisYear));      setEndDate(today); }
    if (range === 'last-year')  { setStartDate(startOfYear(thisYear - 1));  setEndDate(endOfYear(thisYear - 1)); }
    if (range === 'q1')         { setStartDate(`${thisYear}-01-01`);        setEndDate(`${thisYear}-03-31`); }
    if (range === 'q2')         { setStartDate(`${thisYear}-04-01`);        setEndDate(`${thisYear}-06-30`); }
    if (range === 'q3')         { setStartDate(`${thisYear}-07-01`);        setEndDate(`${thisYear}-09-30`); }
    if (range === 'q4')         { setStartDate(`${thisYear}-10-01`);        setEndDate(`${thisYear}-12-31`); }
    if (range === 'last-30')    { setStartDate(dateNDaysAgo(30));            setEndDate(today); }
    if (range === 'all')        { setStartDate('2020-01-01');                setEndDate(today); }
  }

  async function downloadCsv() {
    setDownloading(true);
    try {
      const token = localStorage.getItem('authToken') || '';
      const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
      const qs = new URLSearchParams({ startDate, endDate, format });
      const url = `${apiBase}/tax-export/csv?${qs.toString()}`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Export failed (HTTP ${r.status})`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `haythix-tax-${format}-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      safeToast({ type: 'success', text: `Downloaded ${preview?.summary?.totalRows || 0} rows` });
      // Refresh history (backend logs the download)
      try { const d = await api.getTaxExportHistory(); setHistory(d?.exports || []); } catch {}
    } catch (err) {
      safeToast({ type: 'error', text: err.message });
    } finally { setDownloading(false); }
  }

  const s = preview?.summary || {};

  return (
    <div className="space-y-6">

      {/* Premium emerald hero */}
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-600/15 via-teal-600/10 to-cyan-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <HardDrive className="w-5 h-5 text-emerald-300" />
            <h2 className="text-xl font-bold text-white">Tax Export</h2>
          </div>
          <p className="text-sm text-emerald-200/80 mb-5">
            Real fills only. Smart Trade · Grid · DCA · Algo Orders · Tips received. Aligned with major tax platforms.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Rows in range"    value={s.totalRows ?? '—'}                                tone={s.totalRows > 0 ? 'emerald' : 'slate'} />
            <KpiTile label="Trade volume"     value={s.totalVolumeUsd != null ? `$${s.totalVolumeUsd.toLocaleString()}` : '—'} tone="cyan" />
            <KpiTile label="Trades"           value={s.byType?.Trade ?? 0}                              tone="violet" />
            <KpiTile label="Income (tips)"    value={s.byType?.Receive ?? 0}                            tone="amber" />
          </div>
        </div>
      </div>

      {/* Date range + quick chips */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Clock} title="Date range" subtitle="Quick chips below, or set exact dates" />
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: 'this-year', label: `This year (${thisYear})` },
            { id: 'last-year', label: `Last year (${thisYear - 1})` },
            { id: 'q1', label: 'Q1' }, { id: 'q2', label: 'Q2' }, { id: 'q3', label: 'Q3' }, { id: 'q4', label: 'Q4' },
            { id: 'last-30', label: 'Last 30 days' },
            { id: 'all', label: 'All time' },
          ].map(r => (
            <button key={r.id} onClick={() => applyQuickRange(r.id)}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold bg-dark-700 hover:bg-dark-600 text-gray-300 border border-dark-600/60">
              {r.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold block mb-1">Start date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold block mb-1">End date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
          </div>
        </div>
      </div>

      {/* Format selector + download */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={HardDrive} title="Export format" subtitle="All formats share the same data — different platforms expect slightly different labels" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {EXPORT_FORMATS.map(f => {
            const active = format === f.id;
            return (
              <button key={f.id} onClick={() => setFormat(f.id)}
                className={`text-left rounded-xl p-3 border transition-colors ${
                  active ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200' : 'bg-dark-700/40 border-dark-700/50 text-gray-300 hover:border-dark-500'
                }`}>
                <p className="text-sm font-bold">{f.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{f.hint}</p>
              </button>
            );
          })}
        </div>
        <button onClick={downloadCsv} disabled={downloading || (s.totalRows || 0) === 0}
          className="w-full py-3 rounded-xl text-sm font-bold bg-emerald-500 hover:bg-emerald-400 text-dark-900 disabled:opacity-40 flex items-center justify-center gap-2">
          {downloading ? <Spinner size="sm" /> : <Download className="w-4 h-4" />}
          Download {format.toUpperCase()} CSV ({s.totalRows || 0} rows)
        </button>
        {s.feeCoverage != null && s.totalRows > 0 && (
          <p className="text-[10px] text-gray-500 mt-2 text-center">
            Fee data present on {s.feeCoverage}% of rows. Empty fees = exchange didn't return fee info on that fill (never fabricated).
          </p>
        )}
      </div>

      {/* Preview pane — top 10 rows */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <SectionHeader icon={Activity} title="Preview" subtitle="First 10 rows of your selection — sanity-check before downloading" />
          <button onClick={fetchPreview} disabled={loadingPreview}
            className="text-[11px] font-semibold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 disabled:opacity-40">
            <RefreshCw className={`w-3 h-3 ${loadingPreview ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        {loadingPreview && !preview ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (preview?.sampleRows?.length || 0) === 0 ? (
          <div className="text-center py-8">
            <HardDrive className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No fills in this range</p>
            <p className="text-[11px] text-gray-600 mt-1">Try expanding the date range or check that you've executed trades on the platform.</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-gray-500 border-b border-dark-700">
                  <th className="py-2 pr-2 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Type</th>
                  <th className="py-2 px-2 font-semibold">From</th>
                  <th className="py-2 px-2 font-semibold">To</th>
                  <th className="py-2 px-2 font-semibold">Fee</th>
                  <th className="py-2 pl-2 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {preview.sampleRows.map((r, i) => (
                  <tr key={i} className="border-b border-dark-700/50 text-gray-300">
                    <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{new Date(r.Date).toLocaleDateString()}</td>
                    <td className="py-1.5 px-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        r.Type === 'Receive' ? 'bg-amber-500/15 text-amber-300' : 'bg-violet-500/15 text-violet-300'
                      }`}>{r.Type}</span>
                    </td>
                    <td className="py-1.5 px-2 font-mono">{r['From Amount'] ? `${parseFloat(r['From Amount']).toFixed(4)} ${r['From Currency']}` : '—'}</td>
                    <td className="py-1.5 px-2 font-mono">{parseFloat(r['To Amount']).toFixed(4)} {r['To Currency']}</td>
                    <td className="py-1.5 px-2 font-mono">{r.Fee ? `${parseFloat(r.Fee).toFixed(4)} ${r['Fee Currency']}` : <span className="text-gray-600">—</span>}</td>
                    <td className="py-1.5 pl-2 text-gray-500 text-[10px]">{r.Description?.split(':')[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(s.totalRows || 0) > 10 && (
              <p className="text-[10px] text-gray-600 mt-3 text-center">
                Showing 10 of {s.totalRows} rows. Full set in the CSV.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Export history */}
      {history && history.length > 0 && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <SectionHeader icon={Clock} title="Recent exports" subtitle="Last 25 downloads — proves audit trail to your accountant" />
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-dark-700/30 border border-dark-700/50 text-[11px]">
                <div className="min-w-0 flex-1">
                  <p className="text-white">
                    <span className="font-semibold">{(h.format || 'koinly').toUpperCase()}</span>
                    <span className="text-gray-500 mx-1.5">·</span>
                    {h.startDate || 'all'} → {h.endDate || 'now'}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {timeAgoShort(h.ts)} · {h.rowCount} rows
                  </p>
                </div>
                <span className="text-[10px] text-gray-500">
                  {Object.entries(h.sources || {}).filter(([_, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(', ') || '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notice about completeness */}
      <div className="bg-amber-500/5 rounded-2xl p-4 border border-amber-500/15">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-300 mb-1">What's included &amp; what isn't</p>
            <ul className="text-[10px] text-gray-400 space-y-0.5">
              <li>• <span className="text-emerald-300">Included:</span> Smart Trade fills, Grid Bot fills, DCA Safety fills, Algo Order slices, confirmed tips as Income rows</li>
              <li>• <span className="text-amber-300">Empty when missing:</span> Fee column on exchanges that don't return fee info per-fill, Net Worth (requires historical USD pricing — coming Q3)</li>
              <li>• <span className="text-gray-400">Not yet:</span> Direct manual exchange trades (not placed via a bot), DEX swap history, on-chain wallet activity</li>
              <li>• Always consult a tax professional. CSV format is import-compatible with Koinly, CoinTracker, CoinLedger.</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── Trading Defaults ───────────────────────────────────────────────────────
//
// Per-user execution preferences. These seed new bot creation across Smart
// Trade / Algo / DEX swap, but never override existing bots — that's the
// "defaults" contract. Stored at account-trading-defaults/{userId}.json.
//
// Replaces the old broken pattern where this page wrote to /api/mgmt/config
// (global env vars, rejected by SAFE_CONFIG_KEYS, never read by any
// execution path).

const NETWORK_LABELS = {
  ethereum_mainnet: 'Ethereum',
  polygon_mainnet:  'Polygon',
  arbitrum_mainnet: 'Arbitrum',
  base_mainnet:     'Base',
  bsc_mainnet:      'BSC',
  avalanche_mainnet:'Avalanche',
  optimism_mainnet: 'Optimism',
};

function TradingTab({ onToast }) {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [bounds, setBounds] = useState(null);

  // Stored as BPS to match execution-path convention; UI displays as %
  const [slippageBps, setSlippageBps]               = useState(50);
  const [gasMultiplier, setGasMultiplier]           = useState(1.1);
  const [maxDailyTradesPerBot, setMaxDailyTradesPerBot] = useState(20);
  const [mevEnabled, setMevEnabled]                 = useState(true);

  const [routingStatus, setRoutingStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [defaults, routing] = await Promise.all([
          api.getTradingDefaults(),
          api.getRoutingStatus().catch(() => null),
        ]);
        const d = defaults?.defaults || {};
        setSlippageBps(d.slippageBps ?? 50);
        setGasMultiplier(d.gasMultiplier ?? 1.1);
        setMaxDailyTradesPerBot(d.maxDailyTradesPerBot ?? 20);
        setMevEnabled(d.mevProtectionEnabled !== false);
        setBounds(defaults?.bounds || null);
        setRoutingStatus(routing);
      } catch (err) {
        onToast({ type: 'error', text: `Couldn't load trading defaults: ${err.message}` });
      } finally {
        setLoaded(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.saveTradingDefaults({
        slippageBps:           Math.round(slippageBps),
        gasMultiplier:         Math.round(gasMultiplier * 100) / 100,
        maxDailyTradesPerBot:  Math.round(maxDailyTradesPerBot),
        mevProtectionEnabled:  mevEnabled,
      });
      setSaved(true);
      onToast({ type: 'success', text: 'Trading defaults saved' });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;
  }

  const slippagePct = slippageBps / 100;
  const gasWarning = gasMultiplier >= 1.5;

  const totalAggregatorsAvailable = routingStatus?.networks?.reduce((sum, n) =>
    sum + (n.aggregators?.filter(a => a.available)?.length || 0), 0) || 0;
  const networksWithRouting = routingStatus?.networks?.filter(n =>
    (n.aggregators?.filter(a => a.available)?.length || 0) > 0).length || 0;
  const mevTxCount = routingStatus?.mev?.protectedTxCount30d || 0;
  const mevVolume  = routingStatus?.mev?.protectedVolumeUsd30d || 0;

  return (
    <div className="space-y-6">

      {/* Premium violet hero with live KPIs */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative mb-5">
          <div className="flex items-center gap-2 mb-1">
            <Sliders className="w-5 h-5 text-violet-300" />
            <h2 className="text-xl font-bold text-white">Trading Defaults</h2>
          </div>
          <p className="text-sm text-violet-200/80">
            These defaults seed every <span className="text-white font-medium">new</span> bot, smart trade, and DEX swap.
            Existing bots keep the settings they were created with.
          </p>
        </div>
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiTile label="Default slippage" value={`${slippagePct.toFixed(2)}%`} tone="violet" />
          <KpiTile label="Aggregators live" value={`${totalAggregatorsAvailable}`} tone="emerald" />
          <KpiTile label="MEV-protected 30d" value={`${mevTxCount}`} tone="cyan" />
          <KpiTile label="MEV volume 30d" value={`$${Math.round(mevVolume).toLocaleString()}`} tone="violet" />
        </div>
      </div>

      {/* Execution Parameters */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Sliders} title="Execution Parameters" subtitle="Applied to new bots, trades, and DEX swaps you create" />
        <div className="space-y-0">
          <SettingRow label="Default Slippage Tolerance" description="Maximum acceptable price slippage per trade. Lower = tighter execution, more failures on volatile pairs.">
            <NumberInput
              value={slippagePct}
              onChange={v => setSlippageBps(Math.round(v * 100))}
              min={(bounds?.slippageBps?.min || 5) / 100}
              max={(bounds?.slippageBps?.max || 500) / 100}
              step={0.05}
              unit="%" />
          </SettingRow>
          <SettingRow label="Gas Price Multiplier" description="Multiply estimated gas to improve inclusion speed. Capped at 2× to prevent fee-bomb mistakes.">
            <div className="flex items-center gap-2">
              <NumberInput
                value={gasMultiplier}
                onChange={setGasMultiplier}
                min={bounds?.gasMultiplier?.min || 1.0}
                max={bounds?.gasMultiplier?.max || 2.0}
                step={0.05}
                unit="×" />
              {gasWarning && (
                <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-lg whitespace-nowrap">
                  high cost
                </span>
              )}
            </div>
          </SettingRow>
          <SettingRow label="Max Trades per Day per Bot" description="Daily trade cap applied when new bots are created. Existing bots keep their own cap.">
            <NumberInput
              value={maxDailyTradesPerBot}
              onChange={setMaxDailyTradesPerBot}
              min={bounds?.maxDailyTradesPerBot?.min || 1}
              max={bounds?.maxDailyTradesPerBot?.max || 200}
              step={1}
              unit="trades" />
          </SettingRow>
        </div>
        <div className="mt-4 p-3 bg-violet-500/5 border border-violet-500/15 rounded-xl">
          <p className="text-[11px] text-violet-200/80">
            <Info className="w-3 h-3 inline-block -mt-0.5 mr-1" />
            To change settings for an <button onClick={() => navigate('/bots')} className="underline hover:text-violet-100">existing bot</button>, edit it from your Bots page.
          </p>
        </div>
      </div>

      {/* MEV Protection — REAL toggle */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Shield} title="MEV Protection" subtitle="Route DEX trades through private mempools to prevent sandwich attacks" />
        <div className="space-y-0">
          <SettingRow label="Enable MEV Protection" description="When enabled, your bot-executed DEX trades route through Flashbots Protect (or fallback private RPCs). If all private RPCs fail, trades fall back to the public mempool and a warning is logged. Applies to server-signed bot trades; manual swaps go through your own wallet's RPC.">
            <Toggle enabled={mevEnabled} onChange={setMevEnabled} size="sm" />
          </SettingRow>
          <SettingRow label="Protected Networks" description="MEV protection is available on these chains">
            <div className="flex flex-wrap gap-1.5">
              {['Ethereum', 'Polygon', 'BSC', 'Arbitrum', 'Base'].map(n => (
                <span key={n} className="text-[10px] font-medium px-2 py-1 rounded-lg bg-dark-700 text-gray-400">{n}</span>
              ))}
            </div>
          </SettingRow>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 bg-cyan-500/5 border border-cyan-500/15 rounded-xl">
            <p className="text-[10px] uppercase tracking-wider text-cyan-300/80 font-semibold">Protected trades (30d)</p>
            <p className="text-xl font-bold text-cyan-200 mt-1">{mevTxCount}</p>
          </div>
          <div className="p-3 bg-cyan-500/5 border border-cyan-500/15 rounded-xl">
            <p className="text-[10px] uppercase tracking-wider text-cyan-300/80 font-semibold">Protected volume (30d)</p>
            <p className="text-xl font-bold text-cyan-200 mt-1">${Math.round(mevVolume).toLocaleString()}</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          Networks without MEV protection (Avalanche, Optimism) use standard public RPC.
          MEV protection is most critical on Ethereum mainnet where sandwich attacks are most common.
        </p>
      </div>

      {/* Smart Order Routing — LIVE aggregator status */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Zap} title="Smart Order Routing" subtitle="Live status of DEX aggregators we query for each swap" />
        {routingStatus ? (
          <div className="space-y-2">
            {routingStatus.networks?.map(net => (
              <div key={net.network} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-dark-700/30 border border-dark-700/50">
                <div className="flex items-center gap-2 min-w-0">
                  <Network className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm text-white font-medium truncate">
                    {NETWORK_LABELS[net.network] || net.network}
                  </span>
                  <span className="text-[10px] text-gray-600">·</span>
                  <span className="text-[10px] text-gray-500">chain {net.chainId}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {net.aggregators?.map(a => (
                    <span
                      key={a.name}
                      className={`text-[10px] font-medium px-2 py-1 rounded-lg flex items-center gap-1 ${
                        a.available
                          ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                          : 'bg-dark-700 text-gray-500 border border-dark-600'
                      }`}>
                      {a.available ? <Check className="w-2.5 h-2.5" /> : null}
                      {a.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-[10px] text-gray-600 mt-3">
              {networksWithRouting} of {routingStatus.networks?.length || 0} networks have at least one live aggregator.
              Every DEX swap queries all configured aggregators in parallel and picks the best price.
            </p>
          </div>
        ) : (
          <p className="text-xs text-gray-500">Couldn't reach routing status — try again later.</p>
        )}
      </div>

      {/* Paper Trading link */}
      <button
        onClick={() => navigate('/paper-trading')}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-dark-800 hover:bg-dark-700 border border-dark-700/50 text-left transition-colors">
        <div className="flex items-center gap-3">
          <Play className="w-5 h-5 text-violet-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white">Want to test strategies risk-free?</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Paper Trading has its own dedicated page with full simulation against real market data.
            </p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
      </button>

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}

// ─── Risk Rules ─────────────────────────────────────────────────────────────
//
// Real per-user risk limits with live P&L tracking, one-tap presets, an
// emergency halt button, and a 30-day backtest preview. Backend enforces
// auto-halt via the portfolioRiskWorker (15-min timer) when daily loss or
// drawdown limits are crossed.

const RISK_PRESET_META = {
  conservative: { label: 'Conservative', color: 'emerald', desc: 'Low loss tolerance, tight stops, auto-halt on. Best for capital preservation.' },
  moderate:     { label: 'Moderate',     color: 'amber',   desc: 'Balanced loss limits with auto-halt. Default for most users.' },
  aggressive:   { label: 'Aggressive',   color: 'red',     desc: 'High loss tolerance, no auto-halt, wide stops. For experienced traders only.' },
};

function RiskTab({ onToast }) {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [presets, setPresets] = useState(null);

  const [maxDailyLossPct,     setMaxDailyLossPct]     = useState(5);
  const [maxDrawdownPct,      setMaxDrawdownPct]      = useState(15);
  const [autoHaltOnLoss,      setAutoHaltOnLoss]      = useState(true);
  const [maxPositionPct,      setMaxPositionPct]      = useState(10);
  const [defaultStopLossPct,  setDefaultStopLossPct]  = useState(3);
  const [trailingStopEnabled, setTrailingStopEnabled] = useState(false);

  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [halting, setHalting] = useState(false);
  const [haltConfirm, setHaltConfirm] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [r, s, p] = await Promise.all([
        api.getRiskRules(),
        api.getRiskStatus().catch(() => null),
        api.getRiskPreview().catch(() => null),
      ]);
      const rules = r?.rules || {};
      setMaxDailyLossPct(rules.maxDailyLossPct ?? 5);
      setMaxDrawdownPct(rules.maxDrawdownPct ?? 15);
      setAutoHaltOnLoss(rules.autoHaltOnLoss !== false);
      setMaxPositionPct(rules.maxPositionPct ?? 10);
      setDefaultStopLossPct(rules.defaultStopLossPct ?? 3);
      setTrailingStopEnabled(rules.trailingStopEnabled === true);
      setBounds(r?.bounds || null);
      setPresets(r?.presets || null);
      setRecentEvents(r?.recentEvents || []);
      setStatus(s);
      setPreview(p);
    } catch (err) {
      onToast({ type: 'error', text: `Couldn't load risk rules: ${err.message}` });
    } finally {
      setLoaded(true);
    }
  }, [onToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveRiskRules({
        maxDailyLossPct, maxDrawdownPct, autoHaltOnLoss,
        maxPositionPct, defaultStopLossPct, trailingStopEnabled,
      });
      setSaved(true);
      onToast({ type: 'success', text: 'Risk rules saved' });
      setTimeout(() => setSaved(false), 3000);
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async (preset) => {
    setApplyingPreset(preset);
    try {
      const result = await api.applyRiskPreset(preset);
      const rules = result?.rules || {};
      setMaxDailyLossPct(rules.maxDailyLossPct);
      setMaxDrawdownPct(rules.maxDrawdownPct);
      setAutoHaltOnLoss(rules.autoHaltOnLoss);
      setMaxPositionPct(rules.maxPositionPct);
      setDefaultStopLossPct(rules.defaultStopLossPct);
      setTrailingStopEnabled(rules.trailingStopEnabled);
      onToast({ type: 'success', text: `Applied ${preset} preset` });
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setApplyingPreset(null);
    }
  };

  const triggerEmergencyHalt = async () => {
    setHalting(true);
    try {
      const result = await api.emergencyHalt('manual-from-settings');
      const s = result?.summary || {};
      const total = (s.smartTradesPending || 0) + (s.algoOrdersCancelled || 0) + (s.conditionalPaused || 0);
      onToast({ type: 'success', text: total > 0 ? `Halted: ${total} active bots stopped` : 'No active bots to halt' });
      setHaltConfirm(false);
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setHalting(false);
    }
  };

  if (!loaded) {
    return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;
  }

  const todayPnl = status?.todayPnl?.realizedUsd ?? 0;
  const todayPnlPct = status?.todayPnl?.lossPctOfPortfolio;
  const drawdownPct = status?.drawdown?.currentPct ?? 0;
  const portfolioValue = status?.portfolio?.valueUsd;
  const distanceToHaltPct = status?.limits?.distanceToHaltPct;
  const haltDays = preview?.haltDays ?? 0;
  const previewDays = preview?.days || [];

  // Color the loss bar by how close we are to halt
  const lossBarPct = portfolioValue && maxDailyLossPct
    ? Math.min(100, Math.max(0, ((todayPnlPct || 0) / maxDailyLossPct) * 100))
    : 0;
  const lossBarColor = lossBarPct >= 80 ? 'bg-red-500' : lossBarPct >= 50 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="space-y-6">

      {/* Premium violet hero with live P&L bar */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-violet-300" />
                <h2 className="text-xl font-bold text-white">Risk Rules</h2>
              </div>
              <p className="text-sm text-violet-200/80">
                Real auto-halt when limits breach. Checked every 15 min by the risk worker.
              </p>
            </div>
            <button
              onClick={() => haltConfirm ? triggerEmergencyHalt() : setHaltConfirm(true)}
              disabled={halting}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 border transition-colors ${
                haltConfirm
                  ? 'bg-red-500 hover:bg-red-400 text-white border-red-400'
                  : 'bg-red-500/15 hover:bg-red-500/25 text-red-200 border-red-500/40'
              }`}>
              {halting ? <Spinner size="sm" /> : <Power className="w-4 h-4" />}
              {haltConfirm ? 'Confirm Halt All Bots' : 'Emergency Halt'}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile
              label="Today P&L"
              value={portfolioValue ? `$${Math.round(todayPnl).toLocaleString()}` : '—'}
              tone={todayPnl < 0 ? 'red' : 'emerald'} />
            <KpiTile
              label="Drawdown"
              value={portfolioValue ? `${drawdownPct.toFixed(1)}%` : '—'}
              tone={drawdownPct >= maxDrawdownPct * 0.8 ? 'red' : drawdownPct >= maxDrawdownPct * 0.5 ? 'amber' : 'slate'} />
            <KpiTile
              label="Open positions"
              value={status?.todayPnl?.openTradeCount ?? 0}
              tone="cyan" />
            <KpiTile
              label="Distance to halt"
              value={distanceToHaltPct != null ? `${distanceToHaltPct.toFixed(2)}%` : '—'}
              tone={distanceToHaltPct != null && distanceToHaltPct < 1 ? 'red' : 'violet'} />
          </div>

          {/* Live loss bar */}
          {portfolioValue ? (
            <div className="mt-4">
              <div className="flex items-center justify-between text-[10px] text-violet-200/80 mb-1.5">
                <span>Today's loss vs daily limit</span>
                <span className="font-mono">
                  {todayPnlPct != null ? `${todayPnlPct.toFixed(2)}%` : '0%'} / {maxDailyLossPct}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                <div className={`h-full ${lossBarColor} transition-all`} style={{ width: `${lossBarPct}%` }} />
              </div>
            </div>
          ) : (
            <button
              onClick={() => navigate('/settings?tab=connections')}
              className="mt-4 w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-xl bg-violet-500/10 hover:bg-violet-500/15 border border-violet-500/30 transition-colors">
              <span className="text-[11px] text-violet-200">
                <Info className="w-3 h-3 inline-block -mt-0.5 mr-1" />
                Connect a CEX or wallet to enable live P&L tracking & auto-halt.
              </span>
              <ChevronRight className="w-3 h-3 text-violet-300 flex-shrink-0" />
            </button>
          )}
        </div>
      </div>

      {/* One-tap presets */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Sliders} title="Risk Presets" subtitle="One tap applies a complete set of limits + defaults" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(presets || RISK_PRESET_META).map(([key, vals]) => {
            const meta = RISK_PRESET_META[key];
            if (!meta) return null;
            const isApplying = applyingPreset === key;
            const colorClass = {
              emerald: 'border-emerald-500/30 hover:border-emerald-400/60 bg-emerald-500/5 hover:bg-emerald-500/10',
              amber:   'border-amber-500/30 hover:border-amber-400/60 bg-amber-500/5 hover:bg-amber-500/10',
              red:     'border-red-500/30 hover:border-red-400/60 bg-red-500/5 hover:bg-red-500/10',
            }[meta.color];
            const labelColor = { emerald: 'text-emerald-300', amber: 'text-amber-300', red: 'text-red-300' }[meta.color];
            return (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                disabled={!!applyingPreset}
                className={`rounded-xl p-4 border text-left transition-all disabled:opacity-50 ${colorClass}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className={`font-bold text-sm ${labelColor}`}>{meta.label}</p>
                  {isApplying && <Spinner size="sm" className={labelColor} />}
                </div>
                <p className="text-[11px] text-gray-400 leading-snug mb-3">{meta.desc}</p>
                <div className="grid grid-cols-2 gap-1 text-[10px] text-gray-500">
                  <span>Daily loss: <span className="text-gray-300">{vals.maxDailyLossPct}%</span></span>
                  <span>Drawdown: <span className="text-gray-300">{vals.maxDrawdownPct}%</span></span>
                  <span>Stop loss: <span className="text-gray-300">{vals.defaultStopLossPct}%</span></span>
                  <span>Trailing: <span className="text-gray-300">{vals.trailingStopEnabled ? 'On' : 'Off'}</span></span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loss Limits */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={TrendingDown} title="Loss Limits" subtitle="Automatic halt thresholds enforced every 15 minutes" />
        <div className="space-y-0">
          <SettingRow label="Max Daily Loss" description="Halt all bots when realized P&L drops below this % of portfolio in a single day.">
            <NumberInput value={maxDailyLossPct} onChange={setMaxDailyLossPct}
              min={bounds?.maxDailyLossPct?.min || 0.5} max={bounds?.maxDailyLossPct?.max || 50} step={0.5} unit="%" />
          </SettingRow>
          <SettingRow label="Max Drawdown" description="Halt all bots when portfolio drops this % from its all-time high.">
            <NumberInput value={maxDrawdownPct} onChange={setMaxDrawdownPct}
              min={bounds?.maxDrawdownPct?.min || 5} max={bounds?.maxDrawdownPct?.max || 50} step={1} unit="%" />
          </SettingRow>
          <SettingRow label="Auto-Halt on Loss" description="When OFF, limits become advisory — you'll be alerted but bots keep running.">
            <Toggle enabled={autoHaltOnLoss} onChange={setAutoHaltOnLoss} size="sm" />
          </SettingRow>
        </div>
      </div>

      {/* Position Controls */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={BarChart3} title="Position & Stop Defaults" subtitle="Seeded into new Smart Trades; existing trades unaffected" />
        <div className="space-y-0">
          <SettingRow label="Max Position Size" description="Advisory: cap on % of portfolio per new position. Not auto-enforced yet — unified portfolio aggregator in design.">
            <NumberInput value={maxPositionPct} onChange={setMaxPositionPct}
              min={bounds?.maxPositionPct?.min || 1} max={bounds?.maxPositionPct?.max || 100} step={1} unit="%" />
          </SettingRow>
          <SettingRow label="Default Stop Loss" description="Auto-seeded into every new Smart Trade you open without specifying a stop. Live now.">
            <NumberInput value={defaultStopLossPct} onChange={setDefaultStopLossPct}
              min={bounds?.defaultStopLossPct?.min || 0.5} max={bounds?.defaultStopLossPct?.max || 20} step={0.5} unit="%" />
          </SettingRow>
          <SettingRow label="Trailing Stop by Default" description="When on, the seeded stop-loss is configured to trail price upward and lock in profits.">
            <Toggle enabled={trailingStopEnabled} onChange={setTrailingStopEnabled} size="sm" />
          </SettingRow>
        </div>
      </div>

      {/* 30-day backtest preview */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Activity} title="30-Day Backtest" subtitle="How many days in the last 30 would have triggered your current daily-loss limit" />
        {previewDays.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No closed trades in the last 30 days yet.</p>
        ) : (
          <>
            <div className="flex items-baseline gap-3 mb-3">
              <p className="text-3xl font-bold text-white">{haltDays}</p>
              <p className="text-sm text-gray-400">day{haltDays === 1 ? '' : 's'} would have auto-halted with current limit</p>
            </div>
            <div className="grid grid-cols-15 sm:grid-cols-30 gap-0.5">
              {previewDays.slice(-30).map(d => (
                <div
                  key={d.day}
                  title={`${d.day}: $${d.pnlUsd.toLocaleString()} (${d.fills} fills)`}
                  className={`h-6 rounded ${
                    d.wouldHaveHalted ? 'bg-red-500'
                    : d.pnlUsd < 0 ? 'bg-red-500/30'
                    : d.pnlUsd > 0 ? 'bg-emerald-500/30'
                    : 'bg-dark-700'
                  }`} />
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-3">
              Red bars = would have triggered halt. Lighter shades = losing/winning days that didn't cross the limit.
              {preview?.haltThresholdUsd != null && (
                <> Halt threshold: <span className="text-red-300 font-mono">−${Math.abs(preview.haltThresholdUsd).toLocaleString()}</span> per day.</>
              )}
            </p>
          </>
        )}
      </div>

      {/* Recent risk events */}
      {recentEvents.length > 0 && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <SectionHeader icon={Clock} title="Recent Risk Events" subtitle="Halts and preset changes from this account" />
          <div className="space-y-1.5">
            {recentEvents.map((ev, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-dark-700/30 border border-dark-700/50">
                <div className="flex items-center gap-3">
                  {ev.type === 'auto_halt'      && <AlertTriangle className="w-4 h-4 text-red-400" />}
                  {ev.type === 'manual_halt'    && <Power className="w-4 h-4 text-red-400" />}
                  {ev.type === 'preset_applied' && <Sliders className="w-4 h-4 text-violet-400" />}
                  <div>
                    <p className="text-sm text-white font-medium">
                      {ev.type === 'auto_halt' ? 'Auto-halt triggered'
                        : ev.type === 'manual_halt' ? 'Manual emergency halt'
                        : ev.type === 'preset_applied' ? `Applied ${ev.preset} preset`
                        : ev.type}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {timeAgoShort(ev.ts)} {ev.reason ? `· ${ev.reason}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}

// ─── Notifications & Alerts ─────────────────────────────────────────────────
//
// Per-user channel preferences with real cross-channel delivery via the
// backend notificationDispatcher. Channels: email (ACS), Discord (webhook),
// Telegram (linked chat), outbound HTTPS webhook (HMAC-signed).
//
// Replaces the broken legacy pattern (saveCloudSetting → /api/mgmt/config →
// 403 + global shared alert configs visible across users).

const SEVERITY_META = {
  critical: { label: 'Critical', color: 'red',     desc: 'Auto-halt fires, security incidents, account-deletion confirmations.' },
  warning:  { label: 'Warning',  color: 'amber',   desc: 'Risk approaching limit, exchange disconnects, failed deliveries.' },
  info:     { label: 'Info',     color: 'emerald', desc: 'Trade executed, daily summary, balance changes.' },
};

function NotificationsTab({ onToast }) {
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [channels, setChannels] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyFilter, setHistoryFilter] = useState({ severity: '', sinceDays: 7 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.getAlertChannels(),
        api.getMyAlertHistory({ sinceDays: 7 }).catch(() => null),
      ]);
      setChannels(c?.channels || null);
      setHistory(h);
    } catch (err) {
      onToast({ type: 'error', text: `Couldn't load notifications: ${err.message}` });
    } finally {
      setLoaded(true);
    }
  }, [onToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const update = (path, value) => {
    setChannels(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const keys = path.split('.');
      let target = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (target[keys[i]] == null || typeof target[keys[i]] !== 'object') target[keys[i]] = {};
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await api.saveAlertChannels(channels);
      setChannels(result?.channels || channels);
      setSaved(true);
      onToast({ type: 'success', text: 'Notification settings saved' });
      setTimeout(() => setSaved(false), 3000);
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (severity = 'info') => {
    setTesting(true);
    setLastTest(null);
    try {
      const result = await api.testAlertChannels(severity);
      setLastTest(result);
      const sent = Object.entries(result?.delivered || {})
        .filter(([_, v]) => v?.sent)
        .map(([k]) => k);
      const msg = sent.length > 0
        ? `Sent to: ${sent.join(', ')}`
        : (result?.skipped?.length ? `Skipped (${result.skipped.join(', ')})` : 'No channels configured to fire at this severity');
      onToast({ type: sent.length > 0 ? 'success' : 'info', text: msg });
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setTesting(false);
    }
  };

  const reloadHistory = useCallback(async (filter) => {
    try {
      const h = await api.getMyAlertHistory(filter || historyFilter);
      setHistory(h);
    } catch {}
  }, [historyFilter]);

  if (!loaded || !channels) {
    return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;
  }

  const sevTotals = history?.totals || {};
  const totalAlerts = sevTotals.all || 0;

  const channelStatusIcon = (chKey) => {
    if (!lastTest) return null;
    const res = lastTest?.delivered?.[chKey];
    if (!res) return null;
    return res.sent
      ? <Check className="w-3 h-3 text-emerald-400 inline ml-1" />
      : <X className="w-3 h-3 text-red-400 inline ml-1" />;
  };

  return (
    <div className="space-y-6">

      {/* Premium violet hero */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-5 h-5 text-violet-300" />
              <h2 className="text-xl font-bold text-white">My Alerts</h2>
            </div>
            <p className="text-sm text-violet-200/80">
              Your <span className="text-white font-medium">personal</span> notification inbox — where alerts about your account, bots, and trades get delivered.
              For the public community broadcast feed, see <span className="text-violet-100">Community → Official Channels</span>.
            </p>
          </div>
          <button
            onClick={() => runTest('info')}
            disabled={testing}
            className="px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-100 text-sm font-bold flex items-center gap-2 disabled:opacity-40 transition-colors">
            {testing ? <Spinner size="sm" /> : <Radio className="w-4 h-4" />}
            Send test
          </button>
        </div>
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiTile label="Alerts (7d)" value={totalAlerts}                 tone="violet" />
          <KpiTile label="Critical"    value={sevTotals.critical || 0}     tone={(sevTotals.critical||0) > 0 ? 'red' : 'slate'} />
          <KpiTile label="Warnings"    value={sevTotals.warning || 0}      tone={(sevTotals.warning||0) > 0 ? 'amber' : 'slate'} />
          <KpiTile label="Info"        value={sevTotals.info || 0}         tone="emerald" />
        </div>

        {lastTest && (
          <div className="relative mt-4 p-3 rounded-xl bg-black/30 border border-violet-400/30">
            <p className="text-[11px] uppercase tracking-wider text-violet-300 font-semibold mb-2">Last test fire</p>
            <div className="flex flex-wrap gap-2 text-[11px]">
              {Object.entries(lastTest.delivered || {}).map(([ch, res]) => res && (
                <span key={ch} className={`px-2 py-1 rounded-lg border ${
                  res.sent ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-red-500/10 text-red-300 border-red-500/30'
                }`}>
                  {res.sent ? '✓' : '✗'} {ch}{res.sent ? '' : ` · ${res.reason || 'failed'}`}
                </span>
              ))}
              {lastTest.skipped?.length > 0 && (
                <span className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  skipped: {lastTest.skipped.join(', ')}
                </span>
              )}
            </div>
            {lastTest.hint && <p className="text-[11px] text-amber-200 mt-2">{lastTest.hint}</p>}
          </div>
        )}
      </div>

      {/* Email channel */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5">
          <SectionHeader icon={Mail} title={<>Email{channelStatusIcon('email')}</>} subtitle="Goes to your account email by default; override for a dedicated alerts inbox." />
          <Toggle enabled={!!channels.email?.enabled} onChange={v => update('email.enabled', v)} />
        </div>
        {channels.email?.enabled && (
          <div>
            <label className="text-xs text-gray-500 block mb-1.5 flex items-center justify-between">
              <span>Recipient address</span>
              {user?.email && channels.email?.address !== user.email && (
                <button onClick={() => update('email.address', user.email)} className="text-[10px] text-violet-300 hover:text-violet-200">
                  Reset to account email
                </button>
              )}
            </label>
            <TextInput
              value={channels.email?.address || user?.email || ''}
              onChange={v => update('email.address', v)}
              placeholder="alerts@yourcompany.com"
              type="email" />
            <div className="flex justify-end mt-3">
              <button onClick={() => runTest('info')} disabled={testing} className="text-[11px] text-violet-300 hover:text-violet-200 underline">
                Send test email
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Telegram channel */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5">
          <SectionHeader icon={MessageSquare} title={<>Telegram{channelStatusIcon('telegram')}</>} subtitle="Alerts arrive as DMs from the platform's @haythixBot to your linked Telegram account. You don't run your own bot — link your personal Telegram once below, and the dispatcher routes your alerts to that chat (and only that chat)." />
          <Toggle enabled={!!channels.telegram?.enabled} onChange={v => update('telegram.enabled', v)} />
        </div>
        <div className="mb-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
          <p className="text-[11px] text-blue-200/90 leading-relaxed">
            <Info className="w-3 h-3 inline-block -mt-0.5 mr-1" />
            <span className="font-semibold text-blue-200">How this works:</span> haythix runs <span className="font-mono">@haythixBot</span> as the single platform bot.
            Each user links their personal Telegram chat to their haythix account using a one-time 6-digit code.
            The bot DMs you privately — different users sharing the same bot see only their own alerts.
            <br />
            <span className="text-blue-200/60">If you have multiple haythix accounts (e.g. admin + a test user), each one links separately from its own Telegram chat.</span>
          </p>
        </div>
        <TelegramConnection onToast={onToast} />
      </div>

      {/* Discord channel */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5">
          <SectionHeader icon={Hash} title={<>Discord{channelStatusIcon('discord')}</>} subtitle="Posts a rich embed to any Discord channel you provide a webhook for. Severity color-coded automatically." />
          <Toggle enabled={!!channels.discord?.enabled} onChange={v => update('discord.enabled', v)} />
        </div>
        {channels.discord?.enabled && (
          <div>
            <label className="text-xs text-gray-500 block mb-1.5">Webhook URL</label>
            <TextInput
              value={channels.discord?.webhookUrl || ''}
              onChange={v => update('discord.webhookUrl', v)}
              placeholder="https://discord.com/api/webhooks/..."
              monospace />
            <p className="text-[10px] text-gray-600 mt-1.5">Discord: Edit Channel → Integrations → Webhooks → New Webhook → Copy URL.</p>
            <div className="flex justify-end mt-3">
              <button onClick={() => runTest('info')} disabled={testing || !channels.discord?.webhookUrl} className="text-[11px] text-violet-300 hover:text-violet-200 disabled:opacity-40 underline">
                Send test to Discord
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Outbound webhook channel */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5">
          <SectionHeader icon={Network} title={<>Outbound Webhook{channelStatusIcon('webhook')}</>} subtitle="For power users: POST signed JSON events to your own HTTPS endpoint. Body is HMAC-SHA256 signed with your secret in the X-Haythix-Signature header." />
          <Toggle enabled={!!channels.webhook?.enabled} onChange={v => update('webhook.enabled', v)} />
        </div>
        {channels.webhook?.enabled && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Endpoint URL (HTTPS required)</label>
              <TextInput
                value={channels.webhook?.url || ''}
                onChange={v => update('webhook.url', v)}
                placeholder="https://your-server.example.com/haythix-events"
                monospace />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Shared secret (min 16 chars)</label>
              <TextInput
                value={channels.webhook?.secret || ''}
                onChange={v => update('webhook.secret', v)}
                placeholder="A random secret only your server knows"
                monospace />
              <p className="text-[10px] text-gray-600 mt-1.5">
                Verify on your end: HMAC-SHA256 over the raw request body using this secret. Compare against the <span className="font-mono">X-Haythix-Signature</span> header.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Quiet hours */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5">
          <SectionHeader icon={Clock} title="Quiet Hours" subtitle="Mute non-critical alerts during the hours you don't want to be paged." />
          <Toggle enabled={!!channels.quietHours?.enabled} onChange={v => update('quietHours.enabled', v)} />
        </div>
        {channels.quietHours?.enabled && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Start hour (24h)</label>
                <NumberInput value={channels.quietHours?.startHour ?? 22} onChange={v => update('quietHours.startHour', v)} min={0} max={23} step={1} unit=":00" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">End hour (24h)</label>
                <NumberInput value={channels.quietHours?.endHour ?? 7} onChange={v => update('quietHours.endHour', v)} min={0} max={23} step={1} unit=":00" />
              </div>
            </div>
            <SettingRow label="Allow critical alerts during quiet hours" description="Recommended on — auto-halt and security incidents still page you.">
              <Toggle enabled={channels.quietHours?.allowCritical !== false} onChange={v => update('quietHours.allowCritical', v)} size="sm" />
            </SettingRow>
          </div>
        )}
      </div>

      {/* Severity routing matrix */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Sliders} title="Severity Routing" subtitle="Which channels fire at which severity. Channels above must be enabled too." />
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                <th className="py-2 pr-3 font-semibold">Severity</th>
                <th className="py-2 px-2 font-semibold text-center">Email</th>
                <th className="py-2 px-2 font-semibold text-center">Telegram</th>
                <th className="py-2 px-2 font-semibold text-center">Discord</th>
                <th className="py-2 px-2 font-semibold text-center">Webhook</th>
              </tr>
            </thead>
            <tbody>
              {['critical', 'warning', 'info'].map(lvl => {
                const m = SEVERITY_META[lvl];
                const labelColor = { red: 'text-red-300', amber: 'text-amber-300', emerald: 'text-emerald-300' }[m.color];
                return (
                  <tr key={lvl} className="border-t border-dark-700/50">
                    <td className="py-3 pr-3">
                      <div>
                        <span className={`text-xs font-bold ${labelColor}`}>{m.label}</span>
                        <p className="text-[10px] text-gray-500 mt-0.5">{m.desc}</p>
                      </div>
                    </td>
                    {['email', 'telegram', 'discord', 'webhook'].map(ch => (
                      <td key={ch} className="py-3 px-2 text-center">
                        <Toggle
                          enabled={!!channels.severity?.[lvl]?.[ch]}
                          onChange={v => update(`severity.${lvl}.${ch}`, v)}
                          size="sm" />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5">
          <label className="text-xs text-gray-500 block mb-1.5">Global cooldown — minutes between identical alert types</label>
          <NumberInput value={channels.globalCooldownMinutes ?? 5} onChange={v => update('globalCooldownMinutes', v)} min={0} max={1440} step={1} unit="min" />
          <p className="text-[10px] text-gray-600 mt-1">Prevents flooding when the same condition keeps firing. Test fires bypass this.</p>
        </div>
      </div>

      {/* Alert history */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <SectionHeader icon={Activity} title="Recent Alerts" subtitle="Everything fired in the last 7 days, with per-channel delivery status." />
          <div className="flex gap-1.5">
            {['', 'critical', 'warning', 'info'].map(s => (
              <button
                key={s || 'all'}
                onClick={() => { const f = { ...historyFilter, severity: s }; setHistoryFilter(f); reloadHistory(f); }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                  historyFilter.severity === s
                    ? 'bg-violet-500/20 text-violet-200 border-violet-400/50'
                    : 'bg-dark-700/40 text-gray-400 hover:text-gray-200 border-dark-600/60'
                }`}>
                {s ? s : 'All'}
              </button>
            ))}
          </div>
        </div>
        {(!history?.alerts || history.alerts.length === 0) ? (
          <div className="text-center py-10">
            <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No alerts in the last 7 days{historyFilter.severity ? ` at ${historyFilter.severity} severity` : ''}.</p>
            <p className="text-[10px] text-gray-600 mt-1">Real alerts (auto-halt, security events) will appear here as they fire.</p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
            {history.alerts.map((a, i) => {
              const sevColor = a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'amber' : 'emerald';
              const chip = COLOR_CHIP[sevColor] || COLOR_CHIP.slate;
              const delivered = Object.entries(a.delivered || {}).filter(([_, v]) => v?.sent).map(([k]) => k);
              return (
                <div key={i} className={`rounded-xl border ${chip.bg} ${chip.border} px-3 py-2.5`}>
                  <div className="flex items-start gap-3">
                    {a.severity === 'critical' ? <AlertTriangle className={`w-4 h-4 ${chip.icon} flex-shrink-0 mt-0.5`} />
                      : a.severity === 'warning' ? <AlertTriangle className={`w-4 h-4 ${chip.icon} flex-shrink-0 mt-0.5`} />
                      : <Bell className={`w-4 h-4 ${chip.icon} flex-shrink-0 mt-0.5`} />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white font-medium">{a.title || a.type}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${chip.bg} ${chip.text}`}>
                          {a.severity}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">{a.message}</p>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {timeAgoShort(a.ts)} ·{' '}
                        {delivered.length > 0 ? `Sent to ${delivered.join(', ')}` : (a.skipped?.length ? `Skipped (${a.skipped.join(', ')})` : 'No channels')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}

// ─── Connections (Exchanges, Wallets, RPC) ──────────────────────────────────
//
// Premium rebuild: per-user multi-wallet store, live RPC health pings, real
// per-exchange test buttons. Replaces the legacy ConnectionsTab where the
// wallet was localStorage-only, networks were a static UI hardcode, and the
// Custom RPC Override saved to /api/mgmt/config and got 403'd.

const WALLET_CHAIN_OPTIONS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'polygon',  label: 'Polygon' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'base',     label: 'Base' },
  { value: 'bsc',      label: 'BNB Chain' },
  { value: 'solana',   label: 'Solana' },
];

function ConnectionsTab({ onToast }) {
  const [apiStatus,   setApiStatus]   = useState({ state: 'checking', latencyMs: null });
  const [wallets,     setWallets]     = useState(null);
  const [chains,      setChains]      = useState(null);
  const [exchangeCount, setExchangeCount] = useState(null);
  const apiUrl = import.meta.env.VITE_API_URL || (window.location.origin + '/api');

  // 1) API status ping (kept from old version — it's real)
  useEffect(() => {
    let cancelled = false;
    async function ping() {
      const t0 = performance.now();
      try {
        const token = localStorage.getItem('authToken') || '';
        const r = await fetch(`${apiUrl}/health`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (cancelled) return;
        const latencyMs = Math.round(performance.now() - t0);
        if (r.ok || r.status === 401) setApiStatus({ state: 'online', latencyMs });
        else setApiStatus({ state: 'degraded', latencyMs, code: r.status });
      } catch (err) {
        if (!cancelled) setApiStatus({ state: 'offline', latencyMs: null, error: err.message });
      }
    }
    ping();
    return () => { cancelled = true; };
  }, [apiUrl]);

  // 2) Wallets + exchange count
  useEffect(() => {
    api.listMyWallets().then(d => setWallets(d?.wallets || [])).catch(() => setWallets([]));
    api.getExchangeConnections().then(d => setExchangeCount((d?.exchanges || []).filter(e => e.connected).length)).catch(() => setExchangeCount(0));
  }, []);

  const refreshWallets = useCallback(async () => {
    try { const d = await api.listMyWallets(); setWallets(d?.wallets || []); } catch {}
  }, []);

  return (
    <div className="space-y-6">

      {/* Premium violet hero */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Network className="w-5 h-5 text-violet-300" />
            <h2 className="text-xl font-bold text-white">Exchanges &amp; RPC</h2>
          </div>
          <p className="text-sm text-violet-200/80 mb-5">
            Manage every connected exchange, wallet, and chain in one place. All status indicators are real-time — no hardcoded "managed" badges.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Exchanges connected" value={exchangeCount ?? '—'} tone="cyan" />
            <KpiTile label="Wallets tracked"     value={wallets?.length ?? '—'} tone="violet" />
            <KpiTile label="Chains live"         value={chains ? `${chains.healthy}/${chains.total}` : '—'} tone={chains && chains.healthy === chains.total ? 'emerald' : 'amber'} />
            <KpiTile label="API"                 value={apiStatus.state === 'online' ? `${apiStatus.latencyMs}ms` : apiStatus.state} tone={apiStatus.state === 'online' ? 'emerald' : 'red'} />
          </div>
        </div>
      </div>

      {/* Wallets (multi-wallet, multi-chain, per-user storage) */}
      <WalletsCard wallets={wallets} onChange={refreshWallets} onToast={onToast} />

      {/* Live RPC health per chain (only chains we ACTUALLY support) */}
      <ChainsHealthCard onLoad={setChains} onToast={onToast} />

      {/* CEX Exchange Connections — unchanged core, now with Test buttons */}
      <ExchangeConnections onToast={onToast} />

      <p className="text-[10px] text-gray-600 text-center">
        Telegram lives in <span className="text-violet-300">Settings → My Alerts</span> — it's an alert channel, not a trading connection.
      </p>
    </div>
  );
}

function WalletsCard({ wallets, onChange, onToast }) {
  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  async function save() {
    if (!address.trim()) return;
    setSaving(true);
    try {
      await api.addMyWallet({ address: address.trim(), chain, label: label.trim() });
      onToast({ type: 'success', text: 'Wallet added' });
      setAddress(''); setLabel(''); setAdding(false);
      onChange();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally { setSaving(false); }
  }

  async function remove(id) {
    try {
      await api.deleteMyWallet(id);
      onToast({ type: 'success', text: 'Wallet removed' });
      onChange();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  async function saveLabel(id) {
    try {
      await api.updateMyWalletLabel(id, editLabel.trim() || '—');
      setEditingId(null);
      onChange();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <SectionHeader icon={Wallet} title="Wallets" subtitle="Per-user multi-chain wallet addresses for on-chain balance monitoring. Saved to your account — visible across devices." />
        <button onClick={() => setAdding(s => !s)}
          className="px-3 py-1.5 rounded-lg bg-violet-500/15 hover:bg-violet-500/25 text-violet-200 border border-violet-500/40 text-xs font-bold flex items-center gap-1.5">
          <Plus className="w-3 h-3" />
          {adding ? 'Cancel' : 'Add wallet'}
        </button>
      </div>

      {adding && (
        <div className="mb-4 p-4 rounded-xl bg-dark-700/40 border border-dark-600/50 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold block mb-1">Chain</label>
              <select value={chain} onChange={e => setChain(e.target.value)}
                className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:border-violet-500 focus:outline-none">
                {WALLET_CHAIN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold block mb-1">Label (optional)</label>
              <TextInput value={label} onChange={setLabel} placeholder="e.g. Main hot wallet" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold block mb-1">Address</label>
            <TextInput value={address} onChange={setAddress}
              placeholder={chain === 'solana' ? 'Solana public key (base58)' : '0x… (EVM address)'}
              monospace />
          </div>
          <button onClick={save} disabled={!address.trim() || saving}
            className="px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold disabled:opacity-40 flex items-center gap-2">
            {saving ? <Spinner size="sm" /> : <Check className="w-4 h-4" />}
            Save wallet
          </button>
        </div>
      )}

      {wallets === null ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : wallets.length === 0 ? (
        <div className="text-center py-8">
          <Wallet className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No wallets added yet</p>
          <p className="text-[11px] text-gray-600 mt-1">Add a wallet to see its balances on the Multi-Chain and Aggregate Holdings pages.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {wallets.map(w => (
            <div key={w.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-dark-700/30 border border-dark-700/50">
              <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                {editingId === w.id ? (
                  <div className="flex gap-1">
                    <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      className="flex-1 bg-dark-800 border border-violet-500/50 rounded px-2 py-1 text-white text-sm" />
                    <button onClick={() => saveLabel(w.id)} className="text-emerald-400 px-1.5 hover:bg-emerald-500/10 rounded"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingId(null)} className="text-gray-500 px-1.5 hover:bg-dark-600 rounded"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-white font-medium truncate">{w.label}</p>
                    <button onClick={() => { setEditingId(w.id); setEditLabel(w.label); }}
                      className="text-gray-500 hover:text-violet-300 text-[10px]">edit</button>
                  </div>
                )}
                <p className="text-[10px] text-gray-500 mt-0.5 font-mono truncate">
                  {WALLET_CHAIN_OPTIONS.find(c => c.value === w.chain)?.label || w.chain} · {w.address}
                </p>
              </div>
              <button onClick={() => remove(w.id)}
                className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-red-400 hover:bg-red-500/10 flex-shrink-0">
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChainsHealthCard({ onLoad, onToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getChainsHealth();
      setData(r);
      onLoad?.(r);
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally { setLoading(false); }
  }, [onLoad, onToast]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <SectionHeader icon={Network} title="Chain RPC Health" subtitle="Live ping per configured chain. We only list chains where we actually have RPC — no decorative badges." />
        <button onClick={refresh} disabled={loading}
          className="text-[11px] font-semibold text-violet-300 hover:text-violet-200 flex items-center gap-1 disabled:opacity-40">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {loading && !data ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : !data || !data.chains?.length ? (
        <p className="text-sm text-gray-500">No chains configured.</p>
      ) : (
        <div className="space-y-2">
          {data.chains.map(c => (
            <div key={c.key} className="flex items-center gap-3 bg-dark-700/30 rounded-xl px-4 py-3 border border-dark-700/50">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                c.ok && c.latencyMs < 800 ? 'bg-emerald-400'
                : c.ok ? 'bg-amber-400'
                : 'bg-red-400'
              }`} />
              <span className="text-white text-sm font-medium flex-1">{c.label}</span>
              {c.ok ? (
                <>
                  <span className="text-[10px] text-gray-500 font-mono">{c.latencyMs}ms</span>
                  {c.blockNumber && <span className="text-[10px] text-gray-600">block {c.blockNumber.toLocaleString()}</span>}
                  <span className="text-[10px] text-emerald-400 font-semibold px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">LIVE</span>
                </>
              ) : (
                <>
                  <span className="text-[10px] text-red-300/80">{c.reason || 'unreachable'}</span>
                  <span className="text-[10px] text-red-400 font-semibold px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20">DOWN</span>
                </>
              )}
            </div>
          ))}
          <p className="text-[10px] text-gray-600 mt-3">
            {data.healthy} of {data.total} chains responding. Pings the first public RPC per chain — the aggregator engine cycles through fallbacks if needed.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── CEX Exchange Connections ───────────────────────────────────────────────

// Hardcoded exchange list — always shows regardless of API status
const ALL_EXCHANGES = [
  // Binance global + US grouped to prevent the common "wrong endpoint" mistake.
  { exchangeId: 'binance',   name: 'Binance',        description: "World's largest crypto exchange by volume — NOT for US users (use Binance US below)", hasPassphrase: false, minTier: 'starter', supportsFutures: true },
  { exchangeId: 'binanceus', name: 'Binance US',     description: 'For US users — different keys than Binance global · spot only', hasPassphrase: false, minTier: 'pro', supportsFutures: false, regionalBadge: 'US' },
  { exchangeId: 'coinbase',  name: 'Coinbase',       description: 'Most trusted US exchange — 100M+ users', hasPassphrase: false, minTier: 'starter', supportsFutures: false, regionalBadge: 'US' },
  { exchangeId: 'kraken',    name: 'Kraken',         description: 'Highly regulated exchange with deep liquidity', hasPassphrase: false, minTier: 'starter', supportsFutures: true },
  { exchangeId: 'gemini',    name: 'Gemini',         description: 'US-regulated exchange by the Winklevoss twins', hasPassphrase: false, minTier: 'pro', supportsFutures: false, regionalBadge: 'US' },
  { exchangeId: 'bybit',     name: 'Bybit',          description: 'Top derivatives exchange with deep liquidity', hasPassphrase: false, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'okx',       name: 'OKX',            description: 'All-in-one exchange with advanced trading tools', hasPassphrase: true, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'kucoin',    name: 'KuCoin',         description: "The People's Exchange — 800+ trading pairs", hasPassphrase: true, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'gateio',    name: 'Gate.io',        description: 'Largest altcoin selection — 1,700+ pairs', hasPassphrase: false, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'bitget',    name: 'Bitget',         description: 'Fast-growing exchange with copy trading', hasPassphrase: true, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'htx',       name: 'HTX',            description: 'Major global exchange (formerly Huobi)', hasPassphrase: false, minTier: 'trader', supportsFutures: true },
  { exchangeId: 'mexc',      name: 'MEXC',           description: 'High-leverage exchange (up to 500x) — disclaimer required', hasPassphrase: false, minTier: 'pro', supportsFutures: true, requiresDisclaimer: true },
  { exchangeId: 'cryptocom', name: 'Crypto.com',     description: 'Popular consumer exchange — 80M+ users', hasPassphrase: false, minTier: 'pro', supportsFutures: false },
  { exchangeId: 'bitfinex',  name: 'Bitfinex',       description: 'Professional trading with advanced order types', hasPassphrase: false, minTier: 'pro', supportsFutures: false },
  { exchangeId: 'bitstamp',  name: 'Bitstamp',       description: "Europe's oldest crypto exchange — est. 2011", hasPassphrase: false, minTier: 'pro', supportsFutures: false },
  { exchangeId: 'poloniex',  name: 'Poloniex',       description: 'Established exchange with margin trading', hasPassphrase: false, minTier: 'pro', supportsFutures: false },
];

const TIER_RANK = { trial: 0, starter: 1, trader: 2, pro: 3, elite: 4, admin: 5 };

function ExchangeConnections({ onToast }) {
  const [connectedIds, setConnectedIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectForm, setConnectForm] = useState(null);
  const [formData, setFormData] = useState({ apiKey: '', secret: '', passphrase: '' });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [showMexcDisclaimer, setShowMexcDisclaimer] = useState(false);
  // Per-exchange health check state — { [exchangeId]: { testing, result, error, hint, latencyMs, checkedAt } }
  const [testResults, setTestResults] = useState({});
  const { user } = useAuth();
  const mexcDisclaimer = useMexcDisclaimer();

  async function handleTest(exchangeId) {
    setTestResults(prev => ({ ...prev, [exchangeId]: { ...prev[exchangeId], testing: true } }));
    try {
      const r = await api.testExchangeConnection(exchangeId);
      setTestResults(prev => ({ ...prev, [exchangeId]: { testing: false, ...r } }));
      onToast({
        type: r.ok ? 'success' : 'error',
        text: r.ok
          ? `${exchangeId}: ✓ ${r.latencyMs}ms · ${r.assetsFound} assets`
          : `${exchangeId}: ${r.error?.slice(0, 80) || 'failed'}`,
      });
    } catch (err) {
      setTestResults(prev => ({ ...prev, [exchangeId]: { testing: false, ok: false, error: err.message } }));
      onToast({ type: 'error', text: err.message });
    }
  }

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  async function loadConnectionStatus() {
    try {
      const data = await api.getExchangeConnections();
      const connected = {};
      (data.exchanges || []).forEach(ex => {
        if (ex.connected) connected[ex.exchangeId] = { connectedAt: ex.connectedAt, apiKeyPreview: ex.apiKeyPreview };
      });
      setConnectedIds(connected);
    } catch (err) {
      console.error('Failed to load exchange status:', err);
    } finally {
      setLoading(false);
    }
  }

  // Build display list from hardcoded + connection status
  const exchanges = ALL_EXCHANGES.map(ex => ({
    ...ex,
    connected: !!connectedIds[ex.exchangeId],
    connectedAt: connectedIds[ex.exchangeId]?.connectedAt,
    apiKeyPreview: connectedIds[ex.exchangeId]?.apiKeyPreview,
    accessible: (TIER_RANK[user?.tier] || 0) >= (TIER_RANK[ex.minTier] || 0) || user?.role === 'admin',
  }));

  async function handleConnect(exchangeId) {
    if (!formData.apiKey.trim() || !formData.secret.trim()) {
      onToast({ type: 'error', text: 'API key and secret are required' });
      return;
    }
    setConnecting(true);
    try {
      const result = await api.connectExchange(
        exchangeId,
        formData.apiKey.trim(),
        formData.secret.trim(),
        formData.passphrase.trim() || null
      );
      onToast({ type: 'success', text: `${result.name || exchangeId} connected! Found ${result.assetsFound} assets.` });
      setConnectForm(null);
      setFormData({ apiKey: '', secret: '', passphrase: '' });
      setShowSecret(false);
      await loadConnectionStatus();
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Connection failed' });
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect(exchangeId) {
    setDisconnecting(exchangeId);
    try {
      await api.disconnectExchange(exchangeId);
      onToast({ type: 'success', text: 'Exchange disconnected' });
      await loadConnectionStatus();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setDisconnecting(null);
    }
  }

  const EXCHANGE_ICONS = {
    binance: { emoji: '🟡', color: 'text-yellow-400' },
    bybit:   { emoji: '🟠', color: 'text-orange-400' },
    okx:     { emoji: '⚪', color: 'text-gray-300' },
  };

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
      <SectionHeader
        icon={Activity}
        title="Exchange Connections"
        subtitle="Connect your CEX accounts for unified trading — DEX + CEX in one platform"
      />

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="space-y-3">
          {exchanges.map(ex => {
            const iconData = EXCHANGE_ICONS[ex.exchangeId] || { emoji: '🔗', color: 'text-gray-400' };
            const isFormOpen = connectForm === ex.exchangeId;

            return (
              <div key={ex.exchangeId} className={`rounded-xl border p-4 transition-all ${
                ex.connected
                  ? 'bg-emerald-500/5 border-emerald-500/20'
                  : ex.accessible
                  ? 'bg-dark-700/30 border-dark-600/50 hover:border-dark-500'
                  : 'bg-dark-700/20 border-dark-700/30 opacity-60'
              }`}>
                {/* Exchange header */}
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{iconData.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold text-sm">{ex.name}</h4>
                      {ex.regionalBadge && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/25">
                          🇺🇸 {ex.regionalBadge}
                        </span>
                      )}
                      {ex.connected && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                          CONNECTED
                        </span>
                      )}
                      {!ex.accessible && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
                          {ex.minTier?.toUpperCase()}+ REQUIRED
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">{ex.description}</p>
                    {ex.connected && ex.apiKeyPreview && (
                      <p className="text-gray-600 text-xs mt-1 font-mono">Key: {ex.apiKeyPreview}</p>
                    )}
                  </div>

                  {ex.connected ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTest(ex.exchangeId)}
                        disabled={testResults[ex.exchangeId]?.testing}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-violet-300 bg-violet-500/10 border border-violet-500/30 hover:bg-violet-500/20 transition-all disabled:opacity-40 flex items-center gap-1.5"
                        title="Re-verify keys still work + measure exchange latency"
                      >
                        {testResults[ex.exchangeId]?.testing
                          ? <Spinner size="sm" />
                          : testResults[ex.exchangeId]?.ok === true ? <Check className="w-3 h-3" />
                          : testResults[ex.exchangeId]?.ok === false ? <AlertTriangle className="w-3 h-3" />
                          : <Activity className="w-3 h-3" />}
                        {testResults[ex.exchangeId]?.ok && testResults[ex.exchangeId]?.latencyMs != null
                          ? `${testResults[ex.exchangeId].latencyMs}ms`
                          : 'Test'}
                      </button>
                      <button
                        onClick={() => handleDisconnect(ex.exchangeId)}
                        disabled={disconnecting === ex.exchangeId}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-all disabled:opacity-40"
                      >
                        {disconnecting === ex.exchangeId ? 'Removing...' : 'Disconnect'}
                      </button>
                    </div>
                  ) : ex.accessible ? (
                    <button
                      onClick={() => {
                        // MEXC requires disclaimer ack before connect form opens
                        if (ex.requiresDisclaimer && mexcDisclaimer.needed) {
                          setShowMexcDisclaimer(true);
                          return;
                        }
                        setConnectForm(isFormOpen ? null : ex.exchangeId);
                        setFormData({ apiKey: '', secret: '', passphrase: '' });
                        setShowSecret(false);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        ex.requiresDisclaimer
                          ? 'text-violet-400 bg-violet-500/10 border border-violet-500/20 hover:bg-violet-500/20'
                          : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20'
                      }`}
                    >
                      {isFormOpen ? 'Cancel' : (ex.requiresDisclaimer && mexcDisclaimer.needed ? 'Read Disclaimer →' : 'Connect')}
                    </button>
                  ) : (
                    <Lock className="w-4 h-4 text-gray-600" />
                  )}
                </div>

                {/* Connection form */}
                {isFormOpen && (
                  <div className="mt-4 pt-4 border-t border-dark-600/50 space-y-3">
                    {/* Security warning */}
                    <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/15">
                      <p className="text-[10px] text-amber-400 font-bold mb-1.5 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> API Key Security
                      </p>
                      <ul className="text-[10px] text-gray-400 space-y-0.5">
                        <li>• Create API keys with <span className="text-emerald-400">Read</span> + <span className="text-emerald-400">Spot Trading</span> permissions only</li>
                        <li>• <span className="text-red-400 font-medium">NEVER enable Withdrawals or Transfers</span></li>
                        <li>• Keys are encrypted with AES-256 and never stored in plain text</li>
                        <li>• IP whitelist your API keys for maximum security</li>
                      </ul>
                    </div>

                    <div>
                      <label className="text-gray-500 text-xs block mb-1">API Key</label>
                      <input
                        type="text"
                        value={formData.apiKey}
                        onChange={e => setFormData({ ...formData, apiKey: e.target.value })}
                        placeholder="Paste your API key"
                        className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:border-emerald-500 focus:outline-none"
                      />
                    </div>

                    <div className="relative">
                      <label className="text-gray-500 text-xs block mb-1">Secret Key</label>
                      <input
                        type={showSecret ? 'text' : 'password'}
                        value={formData.secret}
                        onChange={e => setFormData({ ...formData, secret: e.target.value })}
                        placeholder="Paste your secret key"
                        className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 pr-10 text-white text-sm font-mono placeholder-gray-600 focus:border-emerald-500 focus:outline-none"
                      />
                      <button onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-7 text-gray-500 hover:text-gray-300">
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    {ex.hasPassphrase && (
                      <div>
                        <label className="text-gray-500 text-xs block mb-1">Passphrase (required for OKX)</label>
                        <input
                          type="password"
                          value={formData.passphrase}
                          onChange={e => setFormData({ ...formData, passphrase: e.target.value })}
                          placeholder="Your API passphrase"
                          className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:border-emerald-500 focus:outline-none"
                        />
                      </div>
                    )}

                    <button
                      onClick={() => handleConnect(ex.exchangeId)}
                      disabled={connecting || !formData.apiKey.trim() || !formData.secret.trim()}
                      className="w-full py-2.5 rounded-xl text-sm font-bold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                    >
                      {connecting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Verifying credentials...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Connect {ex.name}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-gray-600 text-[10px] mt-4 flex items-center gap-1">
        <Lock className="w-3 h-3" />
        API keys are encrypted with AES-256-GCM. We never store keys in plain text and never request withdrawal permissions.
      </p>

      {showMexcDisclaimer && (
        <MexcDisclaimerModal
          onConfirmed={async () => {
            setShowMexcDisclaimer(false);
            await mexcDisclaimer.refresh();
            // Auto-open the MEXC connect form after acknowledgment
            setConnectForm('mexc');
            setFormData({ apiKey: '', secret: '', passphrase: '' });
            setShowSecret(false);
          }}
          onCancel={() => setShowMexcDisclaimer(false)}
        />
      )}
    </div>
  );
}

function TelegramConnection({ onToast }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // null = loading
  const [linkData, setLinkData] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showManualCode, setShowManualCode] = useState(false);

  // Admin-only diagnostic state
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    api.getTelegramStatus().then(setStatus).catch(() => setStatus({ configured: false, linked: false }));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadWebhookInfo();
  }, [isAdmin]);

  async function loadWebhookInfo() {
    setWebhookLoading(true);
    try {
      const data = await api.getTelegramWebhookStatus();
      setWebhookInfo(data);
    } catch (err) {
      setWebhookInfo({ error: err.message });
    } finally {
      setWebhookLoading(false);
    }
  }

  async function registerWebhook() {
    setRegistering(true);
    try {
      const r = await api.registerTelegramWebhook();
      onToast({ type: 'success', text: `Webhook registered: ${r.webhookUrl}` });
      await loadWebhookInfo();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setRegistering(false);
    }
  }

  // After the user clicks the deep link, poll the status endpoint every 2s
  // for up to 2 minutes. The bot completes the link end-to-end when the
  // user opens it — no second step required. Polling auto-detects that.
  useEffect(() => {
    if (!polling) return;
    let ticks = 0;
    const id = setInterval(async () => {
      ticks++;
      try {
        const s = await api.getTelegramStatus();
        if (s?.linked) {
          setStatus(s);
          setLinkData(null);
          setPolling(false);
          setCode('');
          onToast({ type: 'success', text: 'Telegram linked!' });
          clearInterval(id);
        }
      } catch {}
      if (ticks >= 60) { setPolling(false); clearInterval(id); }
    }, 2000);
    return () => clearInterval(id);
  }, [polling, onToast]);

  async function generateLink() {
    try {
      const data = await api.generateTelegramLink();
      setLinkData(data);
      setPolling(true); // start watching for the bot to complete the link
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  // Manual fallback — used if the user initiated linking FROM Telegram first
  // (sent /link to the bot) and got a code there. They paste it here.
  async function verifyLink() {
    if (!code.trim()) return;
    setVerifying(true);
    try {
      await api.verifyTelegramLink(code.trim());
      onToast({ type: 'success', text: 'Telegram linked!' });
      setLinkData(null);
      setCode('');
      setPolling(false);
      const s = await api.getTelegramStatus();
      setStatus(s);
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setVerifying(false);
  }

  async function disconnect() {
    setDisconnecting(true);
    try {
      await api.disconnectTelegram();
      onToast({ type: 'success', text: 'Telegram disconnected' });
      setStatus({ configured: true, linked: false });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setDisconnecting(false);
  }

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
      <SectionHeader icon={MessageSquare} title="Telegram Bot" subtitle="Receive alerts and manage bots from Telegram" />

      {/* Admin-only diagnostic card. Lets the platform owner verify the bot's
          handle, the webhook registration with Telegram, and one-click
          register the webhook so they don't have to curl the Bot API. */}
      {isAdmin && (
        <div className="mb-5 p-4 rounded-xl bg-amber-500/5 border border-amber-500/30">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">Admin diagnostic</p>
              <p className="text-[11px] text-amber-200/80 mt-0.5">Bot handle, webhook status, and one-click webhook registration. Only visible to admin.</p>
            </div>
            <button onClick={loadWebhookInfo} disabled={webhookLoading}
              className="text-[11px] text-amber-300 hover:text-amber-200 flex items-center gap-1 disabled:opacity-40">
              <RefreshCw className={`w-3 h-3 ${webhookLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {webhookLoading && !webhookInfo ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : webhookInfo?.error ? (
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="font-semibold mb-1">Couldn't reach Telegram API</p>
              <p className="opacity-80">{webhookInfo.error}</p>
              <p className="text-[10px] text-red-300/60 mt-2">Most common cause: <span className="font-mono">TELEGRAM_BOT_TOKEN</span> env var isn't set on the haythix-func App Service.</p>
            </div>
          ) : webhookInfo ? (
            <div className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                <div className="bg-dark-800/60 rounded-lg px-3 py-2">
                  <p className="text-gray-500 uppercase tracking-wider text-[9px] font-semibold">Bot</p>
                  <p className="text-white font-mono">@{webhookInfo.bot?.username || '—'}</p>
                </div>
                <div className="bg-dark-800/60 rounded-lg px-3 py-2">
                  <p className="text-gray-500 uppercase tracking-wider text-[9px] font-semibold">Pending updates</p>
                  <p className="text-white font-mono">{webhookInfo.pendingUpdateCount}</p>
                </div>
                <div className="bg-dark-800/60 rounded-lg px-3 py-2 sm:col-span-2">
                  <p className="text-gray-500 uppercase tracking-wider text-[9px] font-semibold">Registered webhook URL</p>
                  <p className={`font-mono break-all text-[11px] ${webhookInfo.webhookUrl ? 'text-white' : 'text-red-300'}`}>
                    {webhookInfo.webhookUrl || '(not set — bot will not receive messages)'}
                  </p>
                </div>
                {webhookInfo.lastErrorMessage && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 sm:col-span-2">
                    <p className="text-red-300 uppercase tracking-wider text-[9px] font-semibold">Last delivery error</p>
                    <p className="text-red-200 text-[11px] mt-0.5">{webhookInfo.lastErrorMessage}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <div className="text-[11px]">
                  {webhookInfo.matchesExpected ? (
                    <span className="text-emerald-300 flex items-center gap-1.5">
                      <Check className="w-3 h-3" /> Webhook matches expected URL
                    </span>
                  ) : (
                    <span className="text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" />
                      Expected: <span className="font-mono">{webhookInfo.expectedUrl}</span>
                    </span>
                  )}
                </div>
                <button onClick={registerWebhook} disabled={registering}
                  className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-dark-900 text-xs font-bold flex items-center gap-2 disabled:opacity-40 transition-colors">
                  {registering ? <Spinner size="sm" /> : <RefreshCw className="w-3 h-3" />}
                  {webhookInfo.webhookUrl ? 'Re-register' : 'Register webhook'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {status?.linked ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-3">
            <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-emerald-400 font-medium">Telegram Connected</p>
              <p className="text-xs text-gray-500 mt-0.5">Alerts and trading commands available in Telegram</p>
            </div>
            <button onClick={disconnect} disabled={disconnecting}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors">
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>

          {/* Two-way command reference */}
          <details className="bg-dark-700/30 rounded-xl border border-dark-600/40 overflow-hidden">
            <summary className="cursor-pointer px-4 py-3 text-sm text-white font-medium hover:bg-dark-700/50 flex items-center justify-between">
              <span className="flex items-center gap-2"><Terminal className="w-4 h-4 text-accent-primary" /> Two-way commands</span>
              <span className="text-xs text-gray-500">15 commands available</span>
            </summary>
            <div className="px-4 pb-4 pt-1 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Read-only (all linked users)</p>
                <ul className="text-xs text-gray-300 space-y-1 font-mono">
                  <li><span className="text-accent-primary">/balance</span> — live balances across exchanges</li>
                  <li><span className="text-accent-primary">/bots</span> — list your bots, pause/resume inline</li>
                  <li><span className="text-accent-primary">/price BTC</span> — quick price check</li>
                  <li><span className="text-accent-primary">/positions</span> — open Smart Trade positions</li>
                  <li><span className="text-accent-primary">/grid</span> — running grid bots</li>
                  <li><span className="text-accent-primary">/signals</span> — latest AI signals (Trader+)</li>
                  <li><span className="text-accent-primary">/alerts</span> — your active alerts</li>
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-amber-400 mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Trade execution (Trader+ tier)
                </p>
                <ul className="text-xs text-gray-300 space-y-1 font-mono">
                  <li><span className="text-emerald-400">/buy</span> EXCHANGE SYM AMT</li>
                  <li><span className="text-red-400">/sell</span> EXCHANGE SYM AMT</li>
                  <li><span className="text-amber-400">/confirm</span> — execute pending trade (60s window)</li>
                  <li><span className="text-red-400">/closeall</span> — close all Smart Trade positions</li>
                  <li><span className="text-red-400">/stopgrid</span> BOTID — stop a grid bot</li>
                </ul>
                <p className="text-[10px] text-gray-500 mt-2">
                  Trading commands stage a confirmation. Reply <span className="font-mono">/confirm</span> within 60 seconds to execute. Real CCXT orders — no simulation.
                </p>
              </div>
            </div>
          </details>
        </div>
      ) : linkData ? (
        <div className="space-y-3">
          <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl p-4">
            <p className="text-sm text-white font-semibold mb-1">One tap to finish</p>
            <p className="text-xs text-gray-400 mb-3">
              Click the button below. Telegram will open to <span className="font-mono">@haythixBot</span> and link automatically — no code to copy.
            </p>
            <a href={linkData.deepLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-blue-500 text-white hover:bg-blue-400 transition-all">
              <MessageSquare className="w-4 h-4" /> Open @haythixBot in Telegram
            </a>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-blue-300">
              {polling ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                  Watching for the link to complete… (the page will update automatically)
                </>
              ) : (
                <span className="text-gray-500">Waiting…</span>
              )}
            </div>
          </div>

          <details className="bg-dark-700/30 rounded-xl border border-dark-600/40">
            <summary className="cursor-pointer px-4 py-3 text-xs text-gray-400 hover:text-white">
              Bot didn't auto-link, or you started linking inside Telegram? Enter the code manually
            </summary>
            <div className="px-4 pb-4 pt-1 space-y-2">
              <p className="text-[11px] text-gray-500">
                If you sent <span className="font-mono">/link</span> to <span className="font-mono">@haythixBot</span> inside Telegram first, paste the 6-character code the bot replied with here.
              </p>
              <div className="flex gap-2">
                <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                  placeholder="6-digit code" maxLength={6}
                  className="flex-1 bg-dark-800 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono tracking-wider placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
                <button onClick={verifyLink} disabled={code.length < 6 || verifying}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all">
                  {verifying ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </div>
          </details>

          <button onClick={() => { setLinkData(null); setPolling(false); setCode(''); }} className="text-xs text-gray-500 hover:text-gray-400 transition-colors">Cancel</button>
        </div>
      ) : (
        <button onClick={generateLink}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-blue-500 text-white hover:bg-blue-400 transition-all flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Connect Telegram
        </button>
      )}
    </div>
  );
}

function SecurityTab({ onToast }) {
  const { user } = useAuth();
  const isLegacyAdmin = user?.email === 'admin@haythix.local';

  const [currentPw,  setCurrentPw]  = useState('');
  const [newPw,      setNewPw]      = useState('');
  const [confirmPw,  setConfirmPw]  = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [lastPwChange, setLastPwChange] = useState(null); // populated from MFA status response

  // Admin setup state (only for legacy admin)
  const [adminSetupEmail,    setAdminSetupEmail]    = useState('');
  const [adminSetupName,     setAdminSetupName]      = useState('');
  const [adminSetupPassword, setAdminSetupPassword] = useState('');
  const [adminSetupSaving,   setAdminSetupSaving]   = useState(false);

  // MFA state
  const [mfaStatus,     setMfaStatus]     = useState(null);  // null = loading
  const [mfaSetupPhase, setMfaSetupPhase] = useState('idle'); // idle | setup | disable | regen
  const [mfaSecret,     setMfaSecret]     = useState('');
  const [mfaUri,        setMfaUri]        = useState('');
  const [mfaQr,         setMfaQr]         = useState('');    // data URL for QR image
  const [mfaCode,       setMfaCode]       = useState('');
  const [mfaSaving,     setMfaSaving]     = useState(false);
  // Backup codes — shown ONCE after setup/regen. Cleared when user dismisses.
  const [justGeneratedBackupCodes, setJustGeneratedBackupCodes] = useState(null);

  useEffect(() => {
    if (isLegacyAdmin) { setMfaStatus({ mfaEnabled: false, isLegacyAdmin: true }); return; }
    getMfaStatus().then(s => {
      setMfaStatus(s);
      if (s?.passwordChangedAt) setLastPwChange(s.passwordChangedAt);
    }).catch(() => setMfaStatus({ mfaEnabled: false }));
  }, [isLegacyAdmin]);

  // Generate QR data URL whenever the otpauth URI changes
  useEffect(() => {
    if (!mfaUri) return;
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(mfaUri, { width: 200, margin: 2, color: { dark: '#ffffff', light: '#1f2937' } })
        .then(setMfaQr)
        .catch(() => setMfaQr(''));
    });
  }, [mfaUri]);

  const handleAdminSetup = async () => {
    if (!adminSetupEmail || !adminSetupPassword) {
      onToast({ type: 'error', text: 'Email and password are required' }); return;
    }
    setAdminSetupSaving(true);
    try {
      const res = await setupAdmin({ email: adminSetupEmail, name: adminSetupName, password: adminSetupPassword });
      if (res.success) {
        onToast({ type: 'success', text: 'Admin account created — logging you in with new credentials' });
        setTimeout(() => window.location.href = '/', 1500);
      } else {
        onToast({ type: 'error', text: res.error?.message || 'Setup failed' });
      }
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setAdminSetupSaving(false);
    }
  };

  const handleMfaStartSetup = async () => {
    setMfaSaving(true);
    try {
      const data = await setupMfa();
      setMfaSecret(data.secret);
      setMfaUri(data.uri);
      setMfaSetupPhase('setup');
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setMfaSaving(false);
    }
  };

  const handleMfaConfirm = async () => {
    if (mfaCode.length !== 6) { onToast({ type: 'error', text: 'Enter the 6-digit code' }); return; }
    setMfaSaving(true);
    try {
      const result = await verifyMfaSetup({ secret: mfaSecret, code: mfaCode });
      // Backend returns 10 single-use backup codes ONCE — show them so
      // the user can save them before they're gone.
      if (Array.isArray(result?.backupCodes)) {
        setJustGeneratedBackupCodes(result.backupCodes);
      }
      // Refresh status to pick up backupCodesRemaining count
      try {
        const fresh = await getMfaStatus();
        setMfaStatus(fresh);
      } catch { setMfaStatus({ mfaEnabled: true, backupCodesRemaining: 10, backupCodesTotal: 10 }); }
      setMfaSetupPhase('idle');
      setMfaCode('');
      onToast({ type: 'success', text: 'Two-factor authentication enabled' });
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Invalid code — try again' });
    } finally {
      setMfaSaving(false);
    }
  };

  // Regenerate backup codes — requires current TOTP, returns new set ONCE.
  const handleRegenerateBackupCodes = async () => {
    if (mfaCode.length !== 6) { onToast({ type: 'error', text: 'Enter your current 6-digit code first' }); return; }
    setMfaSaving(true);
    try {
      const r = await api.regenerateMfaBackupCodes(mfaCode);
      if (Array.isArray(r?.backupCodes)) setJustGeneratedBackupCodes(r.backupCodes);
      try {
        const fresh = await getMfaStatus();
        setMfaStatus(fresh);
      } catch {}
      setMfaSetupPhase('idle');
      setMfaCode('');
      onToast({ type: 'success', text: 'Backup codes regenerated — old codes are now invalid' });
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Could not regenerate' });
    } finally {
      setMfaSaving(false);
    }
  };

  const handleMfaDisable = async () => {
    if (mfaCode.length !== 6) { onToast({ type: 'error', text: 'Enter your current authenticator code to disable MFA' }); return; }
    setMfaSaving(true);
    try {
      await disableMfa({ code: mfaCode });
      setMfaStatus({ mfaEnabled: false });
      setMfaSetupPhase('idle');
      setMfaCode('');
      onToast({ type: 'success', text: 'Two-factor authentication disabled' });
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Invalid code' });
    } finally {
      setMfaSaving(false);
    }
  };

  // Decode the JWT payload so we can show human-readable session metadata
  // (signed-in-at, expiring-in) instead of an opaque token preview.
  const sessionMeta = useMemo(() => {
    try {
      const tk = localStorage.getItem('authToken') || '';
      if (!tk || !tk.includes('.')) return null;
      const [payloadB64] = tk.split('.');
      const padLen = (4 - (payloadB64.length % 4)) % 4;
      const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padLen);
      const claims = JSON.parse(atob(b64));
      return {
        signedInAt: claims.iat ? new Date(claims.iat) : null,
        expiresAt:  claims.exp ? new Date(claims.exp) : null,
      };
    } catch { return null; }
  }, []);
  // Detect browser + OS from user-agent — same parser as login-history backend
  const deviceLabel = useMemo(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const browser =
      /Edg\/(\d+)/i.exec(ua) ? `Edge` :
      /Firefox\/(\d+)/i.exec(ua) ? `Firefox` :
      /Chrome\/(\d+)/i.exec(ua) ? `Chrome` :
      /Safari\/(\d+)/i.exec(ua) ? `Safari` :
      'Browser';
    const os =
      /Windows/i.test(ua) ? 'Windows' :
      /Mac OS X/i.test(ua) ? 'macOS' :
      /Android/i.test(ua) ? 'Android' :
      /iPhone|iPad|iOS/i.test(ua) ? 'iOS' :
      /Linux/i.test(ua) ? 'Linux' : 'Unknown';
    return `${browser} on ${os}`;
  }, []);
  const relativeAge = (d) => {
    if (!d) return '—';
    const s = (Date.now() - d.getTime()) / 1000;
    if (s < 60)    return 'just now';
    if (s < 3600)  return `${Math.round(s/60)}m ago`;
    if (s < 86400) return `${Math.round(s/3600)}h ago`;
    return `${Math.round(s/86400)}d ago`;
  };
  const expiresIn = (d) => {
    if (!d) return '—';
    const days = Math.max(0, Math.round((d.getTime() - Date.now()) / 86400000));
    return `${days} day${days === 1 ? '' : 's'}`;
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) {
      onToast({ type: 'error', text: 'All password fields are required' });
      return;
    }
    if (newPw !== confirmPw) {
      onToast({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPw.length < 8) {
      onToast({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }
    if (currentPw === newPw) {
      onToast({ type: 'error', text: 'New password must differ from current password' });
      return;
    }
    setSaving(true);
    try {
      const r = await changePassword({ currentPassword: currentPw, newPassword: newPw });
      // Backend returns a FRESH token (current session stays alive after the
      // passwordChangedAt bump) plus a timestamp for the UI to display.
      const data = r?.data || r;
      if (data?.token) {
        try { localStorage.setItem('authToken', data.token); } catch {}
      }
      if (data?.passwordChangedAt) setLastPwChange(data.passwordChangedAt);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      onToast({ type: 'success', text: 'Password updated. Other devices stay signed in until existing sessions expire.' });
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Could not update password' });
    } finally {
      setSaving(false);
    }
  };

  // Password strength scorer — 0..4. Looks at length, character classes,
  // and forbids the obvious "abc123" / repeated characters / matches the
  // email pattern. Sticks to local checks — no external lib needed.
  const passwordStrength = useMemo(() => {
    if (!newPw) return null;
    let score = 0;
    if (newPw.length >= 8) score++;
    if (newPw.length >= 12) score++;
    if (/[A-Z]/.test(newPw) && /[a-z]/.test(newPw)) score++;
    if (/[0-9]/.test(newPw) && /[^A-Za-z0-9]/.test(newPw)) score++;
    // Penalties
    if (/^(.)\1+$/.test(newPw)) score = 0;                          // all same char
    if (newPw.toLowerCase().includes(user?.email?.split('@')[0]?.toLowerCase() || '___nope___')) score = Math.min(score, 1);
    if (/^(?:password|qwerty|123456|abc123|letmein)/i.test(newPw)) score = 0;
    return Math.min(4, score);
  }, [newPw, user?.email]);

  const strengthLabel = ['Too weak', 'Weak', 'Fair', 'Strong', 'Excellent'];
  const strengthColor = ['bg-red-500', 'bg-red-400', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-500'];

  return (
    <div className="space-y-6">

      {/* This session — human-friendly replacement for the old token preview.
          Cosmetic: when full Active Sessions ships in Phase 2.5 this card
          will move there; for now it answers the "am I signed in?" question
          without leaking token internals. */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Key} title="This session" subtitle="The device you're using right now to access haythix." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 border border-dark-600/30">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Device</p>
            <p className="text-white text-sm font-medium mt-0.5">{deviceLabel}</p>
          </div>
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 border border-dark-600/30">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Signed in</p>
            <p className="text-white text-sm font-medium mt-0.5">{relativeAge(sessionMeta?.signedInAt)}</p>
          </div>
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 border border-dark-600/30">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Expires in</p>
            <p className="text-white text-sm font-medium mt-0.5">{expiresIn(sessionMeta?.expiresAt)}</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          Sessions last 30 days. Each visit refreshes the window, so active users rarely need to sign in again.
        </p>
      </div>

      {/* Change Password — hidden for legacy admin (must run Setup Admin first) */}
      {!isLegacyAdmin && (
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-1">
          <SectionHeader icon={Lock} title="Change Password" subtitle="Updating signs out other devices as their sessions expire." />
          {lastPwChange && (
            <p className="text-[10px] text-gray-500 whitespace-nowrap">
              Last changed: <span className="text-gray-300">{new Date(lastPwChange).toLocaleDateString()}</span>
            </p>
          )}
        </div>
        <div className="space-y-3 max-w-md">
          {/* Current password — show/hide */}
          <PwField label="Current Password" value={currentPw} onChange={setCurrentPw}
            show={showCurrent} onToggleShow={() => setShowCurrent(v => !v)}
            placeholder="Current password" autoComplete="current-password" />
          {/* New password — show/hide */}
          <PwField label="New Password" value={newPw} onChange={setNewPw}
            show={showNew} onToggleShow={() => setShowNew(v => !v)}
            placeholder="At least 8 characters" autoComplete="new-password" />
          {/* Strength meter (live) */}
          {newPw && (
            <div className="space-y-1">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className={`flex-1 h-1 rounded ${i < passwordStrength ? strengthColor[passwordStrength] : 'bg-dark-700'}`} />
                ))}
              </div>
              <p className="text-[10px] text-gray-500">
                Strength: <span className={`font-bold ${
                  passwordStrength <= 1 ? 'text-red-400' :
                  passwordStrength === 2 ? 'text-amber-400' :
                  'text-emerald-400'
                }`}>{strengthLabel[passwordStrength]}</span>
                <span className="ml-2 text-gray-600">
                  Tip: mix upper + lower + digits + symbols, 12+ characters.
                </span>
              </p>
            </div>
          )}
          {/* Confirm password — show/hide */}
          <PwField label="Confirm New Password" value={confirmPw} onChange={setConfirmPw}
            show={showConfirm} onToggleShow={() => setShowConfirm(v => !v)}
            placeholder="Repeat new password" autoComplete="new-password" />
          {newPw && confirmPw && newPw !== confirmPw && (
            <p className="text-red-400 text-xs flex items-center gap-1">
              <X className="w-3 h-3" /> Passwords do not match
            </p>
          )}
          <button onClick={handleChangePassword} disabled={saving || (newPw && passwordStrength < 2)}
            className="btn-primary text-sm flex items-center gap-2 mt-2 disabled:opacity-50"
            title={newPw && passwordStrength < 2 ? 'Choose a stronger password' : ''}>
            {saving ? <Spinner size="sm" /> : <Lock className="w-3.5 h-3.5" />}
            Update Password
          </button>
        </div>
      </div>
      )}

      {/* Admin Account Setup — only for legacy admin */}
      {isLegacyAdmin && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-amber-500/30">
          <SectionHeader icon={Crown} title="Create Admin Account" subtitle="Set up your personal email & password login — you are already authenticated as the platform owner" />
          <div className="space-y-3 max-w-md">
            <div>
              <label className="text-gray-500 text-xs block mb-1.5">Your Email</label>
              <input type="email" value={adminSetupEmail} onChange={e => setAdminSetupEmail(e.target.value)}
                placeholder="humayun@boraksolutions.com"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary" />
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1.5">Display Name (optional)</label>
              <input type="text" value={adminSetupName} onChange={e => setAdminSetupName(e.target.value)}
                placeholder="Humayun"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary" />
            </div>
            <div>
              <label className="text-gray-500 text-xs block mb-1.5">New Password</label>
              <input type="password" value={adminSetupPassword} onChange={e => setAdminSetupPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary" />
            </div>
            <button onClick={handleAdminSetup} disabled={adminSetupSaving}
              className="btn-primary text-sm flex items-center gap-2 mt-1">
              {adminSetupSaving ? <Spinner size="sm" /> : <BadgeCheck className="w-3.5 h-3.5" />}
              Create Admin Account
            </button>
          </div>
          <p className="mt-4 text-amber-400/70 text-xs">No old password needed — your active session proves your identity. After setup, log in with your email and password, then enable MFA for maximum security.</p>
        </div>
      )}

      {/* Two-Factor Authentication */}
      {!isLegacyAdmin && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <SectionHeader icon={ShieldCheck} title="Two-Factor Authentication" subtitle="Require an authenticator code on every login" />

          {mfaStatus === null && (
            <p className="text-gray-500 text-sm">Loading MFA status…</p>
          )}

          {mfaStatus && !mfaStatus.mfaEnabled && mfaSetupPhase === 'idle' && (
            <div className="flex items-center justify-between p-4 bg-dark-700/40 rounded-xl border border-dark-600/30">
              <div>
                <p className="text-white text-sm font-medium">MFA is disabled</p>
                <p className="text-gray-500 text-xs mt-0.5">Add a second factor using Google Authenticator, Authy, or any TOTP app</p>
              </div>
              <button onClick={handleMfaStartSetup} disabled={mfaSaving}
                className="flex items-center gap-2 px-4 py-2 bg-accent-primary/10 border border-accent-primary/30 text-accent-primary rounded-lg text-sm hover:bg-accent-primary/20 transition-colors">
                {mfaSaving ? <Spinner size="sm" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                Enable MFA
              </button>
            </div>
          )}

          {mfaSetupPhase === 'setup' && (
            <div className="space-y-5">
              <div className="p-4 bg-dark-700/40 rounded-xl border border-dark-600/30 space-y-4">
                <p className="text-white text-sm font-medium">Step 1 — Scan QR code with your authenticator app</p>

                {/* QR code */}
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  <div className="flex-shrink-0">
                    {mfaQr ? (
                      <img src={mfaQr} alt="MFA QR Code" className="w-44 h-44 rounded-xl border border-dark-500" />
                    ) : (
                      <div className="w-44 h-44 rounded-xl border border-dark-500 bg-dark-900 flex items-center justify-center">
                        <Spinner size="sm" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 flex-1">
                    <ol className="text-gray-400 text-xs space-y-2 list-decimal list-inside">
                      <li>Open <span className="text-white font-medium">Google Authenticator</span>, Authy, or Microsoft Authenticator</li>
                      <li>Tap <span className="text-white font-medium">+</span> → <span className="text-white font-medium">Scan a QR code</span></li>
                      <li>Point your camera at the QR code on the left</li>
                    </ol>
                    <p className="text-gray-500 text-xs">Can't scan? Use the setup key instead:</p>
                    <div className="flex items-center gap-2 bg-dark-900 rounded-lg px-3 py-2 border border-dark-600">
                      <code className="text-accent-primary font-mono text-xs flex-1 tracking-wider break-all">{mfaSecret}</code>
                      <button onClick={() => { navigator.clipboard.writeText(mfaSecret); onToast({ type: 'success', text: 'Key copied' }); }}
                        className="text-gray-400 hover:text-white flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-gray-500 text-xs block mb-1.5">Step 2 — Enter the 6-digit code shown in your app to confirm</label>
                <input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" autoFocus
                  className="w-44 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-white text-center text-xl font-mono tracking-[0.4em] focus:outline-none focus:border-accent-primary" />
              </div>

              <div className="flex items-center gap-3">
                <button onClick={handleMfaConfirm} disabled={mfaSaving || mfaCode.length !== 6}
                  className="btn-primary text-sm flex items-center gap-2">
                  {mfaSaving ? <Spinner size="sm" /> : <Check className="w-3.5 h-3.5" />}
                  Verify & Enable MFA
                </button>
                <button onClick={() => { setMfaSetupPhase('idle'); setMfaCode(''); setMfaQr(''); }} className="text-gray-400 text-sm hover:text-white">Cancel</button>
              </div>
            </div>
          )}

          {mfaStatus?.mfaEnabled && mfaSetupPhase === 'idle' && !justGeneratedBackupCodes && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/20">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-emerald-400 text-sm font-medium">MFA is enabled</p>
                  <p className="text-gray-500 text-xs mt-0.5">Every login requires your authenticator code or a backup code</p>
                </div>
                <button onClick={() => setMfaSetupPhase('disable')}
                  className="text-xs text-gray-500 hover:text-red-400 transition-colors">Disable</button>
              </div>

              {/* Backup codes status + regenerate */}
              <div className={`flex items-center gap-3 p-4 rounded-xl border ${
                (mfaStatus.backupCodesRemaining ?? 10) <= 2
                  ? 'bg-amber-500/5 border-amber-500/30'
                  : 'bg-dark-700/30 border-dark-600/30'
              }`}>
                <Key className={`w-4 h-4 flex-shrink-0 ${
                  (mfaStatus.backupCodesRemaining ?? 10) <= 2 ? 'text-amber-400' : 'text-gray-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">Backup codes</p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {(mfaStatus.backupCodesRemaining ?? 10)} of {(mfaStatus.backupCodesTotal ?? 10)} unused
                    {mfaStatus.backupCodesGeneratedAt && ` · generated ${new Date(mfaStatus.backupCodesGeneratedAt).toLocaleDateString()}`}
                    {(mfaStatus.backupCodesRemaining ?? 10) <= 2 && (
                      <span className="text-amber-400 ml-1">— running low, regenerate soon.</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setMfaSetupPhase('regen')}
                  className="text-xs text-violet-300 hover:text-violet-200 transition-colors font-bold">Regenerate</button>
              </div>
            </div>
          )}

          {/* Show-once panel: backup codes display after setup OR regenerate */}
          {justGeneratedBackupCodes && (
            <div className="bg-emerald-500/5 border-2 border-emerald-500/30 rounded-2xl p-5 space-y-3">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-emerald-300">Save these backup codes now</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Each code works exactly once if you lose access to your authenticator app. We'll never show them again — save to a password manager or print and store somewhere safe.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-dark-900 rounded-xl border border-emerald-500/30 p-3">
                {justGeneratedBackupCodes.map((c, i) => (
                  <code key={i} className="text-sm font-mono text-emerald-300 text-center py-1.5 rounded bg-emerald-500/5 select-all">{c}</code>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => {
                  navigator.clipboard.writeText(justGeneratedBackupCodes.join('\n'));
                  onToast({ type: 'success', text: 'Copied to clipboard' });
                }}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30 flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Copy all
                </button>
                <button onClick={() => {
                  const blob = new Blob([`haythix backup codes\nGenerated ${new Date().toLocaleString()}\n\n${justGeneratedBackupCodes.join('\n')}\n\nEach code works exactly once.`], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `haythix-backup-codes-${Date.now()}.txt`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-dark-700 text-gray-300 hover:bg-dark-600 border border-dark-600 flex items-center gap-1">
                  <HardDrive className="w-3.5 h-3.5" /> Download .txt
                </button>
                <button onClick={() => setJustGeneratedBackupCodes(null)}
                  className="text-xs font-bold text-violet-300 hover:text-violet-200 ml-auto">
                  I've saved them — close →
                </button>
              </div>
            </div>
          )}

          {/* Regenerate flow — requires current TOTP */}
          {mfaSetupPhase === 'regen' && (
            <div className="space-y-3">
              <p className="text-gray-400 text-sm">Enter your current 6-digit authenticator code to issue a fresh set of 10 backup codes. The current set will stop working immediately.</p>
              <input type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" autoFocus
                className="w-40 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-center text-lg font-mono tracking-widest focus:outline-none focus:border-accent-primary" />
              <div className="flex items-center gap-3">
                <button onClick={handleRegenerateBackupCodes} disabled={mfaSaving || mfaCode.length !== 6}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-500/15 border border-violet-500/30 text-violet-200 rounded-lg text-sm hover:bg-violet-500/25 transition-colors">
                  {mfaSaving ? <Spinner size="sm" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Regenerate codes
                </button>
                <button onClick={() => { setMfaSetupPhase('idle'); setMfaCode(''); }} className="text-gray-400 text-sm hover:text-white">Cancel</button>
              </div>
            </div>
          )}

          {mfaSetupPhase === 'disable' && (
            <div className="space-y-3">
              <p className="text-gray-400 text-sm">Enter your current 6-digit authenticator code <span className="text-gray-500">— or one of your 8-character backup codes</span> to confirm disabling MFA. This also revokes all your backup codes.</p>
              <input type="text" value={mfaCode}
                onChange={e => setMfaCode(e.target.value.toUpperCase().slice(0, 9))}
                placeholder="000000 or XXXX-XXXX"
                className="w-52 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-center text-lg font-mono tracking-widest focus:outline-none focus:border-accent-primary" />
              <div className="flex items-center gap-3">
                <button onClick={handleMfaDisable} disabled={mfaSaving || mfaCode.length < 6}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors">
                  {mfaSaving ? <Spinner size="sm" /> : <X className="w-3.5 h-3.5" />}
                  Disable MFA
                </button>
                <button onClick={() => { setMfaSetupPhase('idle'); setMfaCode(''); }} className="text-gray-400 text-sm hover:text-white">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Account Recovery — what to do if locked out ─────────────── */}
      {!isLegacyAdmin && (
        <div className="bg-gradient-to-br from-violet-500/8 via-dark-800/80 to-dark-900 rounded-2xl border border-violet-500/20 p-6">
          <SectionHeader icon={ShieldCheck} title="If you ever get locked out"
            subtitle="haythix doesn't store passwords in plaintext — we can't email yours back. Here's exactly how to recover." />
          <div className="space-y-3">
            <RecoveryStep n={1} title="Forgot password?"
              body={<>Go to <code className="bg-dark-700 px-1 rounded">/login</code> → <strong>Forgot password</strong>. We email a one-hour reset link to your account email. {mfaStatus?.mfaEnabled && <>Because MFA is on, the reset page will also ask for your authenticator code or one of your backup codes.</>}</>} />
            <RecoveryStep n={2} title="Lost your authenticator phone?"
              body={<>Use one of your 8-character backup codes wherever you'd normally enter the 6-digit code — login, password reset, or MFA disable. Each code works exactly once. You have <strong>{mfaStatus?.backupCodesRemaining ?? '—'}</strong> of <strong>{mfaStatus?.backupCodesTotal ?? '—'}</strong> remaining.</>} />
            <RecoveryStep n={3} title="Lost everything?"
              body={<>If you've lost access to your email <em>and</em> your authenticator <em>and</em> all your backup codes, contact <a href="mailto:support@haythix.com" className="text-violet-300 hover:text-violet-200 underline">support@haythix.com</a> with proof of identity (the email you used, your most recent invoice, a transaction hash from your connected wallet). Manual recovery takes 3–5 business days.</>} />
          </div>
        </div>
      )}

      {/* Danger Zone — Sign Out now hits the backend logout endpoint so
          the token entry is invalidated server-side, not just removed
          client-side. */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-red-500/20">
        <SectionHeader icon={AlertTriangle} title="Danger Zone" subtitle="Irreversible session actions" />
        <div className="flex items-center justify-between p-4 bg-red-500/5 rounded-xl border border-red-500/15">
          <div>
            <p className="text-white text-sm font-medium">Sign out of this device</p>
            <p className="text-gray-500 text-xs mt-0.5">Invalidates your session on this browser. Other devices stay signed in.</p>
          </div>
          <button
            onClick={async () => {
              try { await api.logout(); }
              catch {}  // api.logout always clears local token in finally
              window.location.href = '/login';
            }}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors">
            <Power className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Password-field helper with built-in show/hide ─────────────────────────
function PwField({ label, value, onChange, show, onToggleShow, placeholder, autoComplete }) {
  return (
    <div>
      <label className="text-gray-500 text-xs block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-accent-primary"
        />
        <button type="button" onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
          title={show ? 'Hide' : 'Show'}>
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function RecoveryStep({ n, title, body }) {
  return (
    <div className="flex items-start gap-3 bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
      <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white">{title}</p>
        <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ─── Subscription Tab ─────────────────────────────────────────────────────────

// Pricing: monthly rate (billed monthly) vs effective monthly rate when billed annually.
// Annual gives ~50% discount, but is paid as one annual sum.
const UPGRADE_TIERS = [
  { id: 'starter', label: 'Starter', monthlyPrice: 19.99,  annualPrice: 120,     annualEffectivePerMo: 10,    features: ['2 bots', '5 wallets per bot', 'Basic strategies', 'Ethereum', '14-day history', 'Email alerts'] },
  { id: 'trader',  label: 'Trader',  monthlyPrice: 65.99,  annualPrice: 599.88,  annualEffectivePerMo: 49.99, features: ['5 bots', '10 wallets per bot', 'Advanced strategies', '4 chains', '30-day history', 'Email + Telegram', 'Copy Trading'] },
  { id: 'pro',     label: 'Pro',     monthlyPrice: 99.99,  annualPrice: 911.88,  annualEffectivePerMo: 75.99, features: ['8 bots', '10 wallets per bot', 'All strategies', 'All chains', '90-day history', 'All alert types', 'AI Strategy Builder', 'Backtesting'] },
  { id: 'elite',   label: 'Elite',   monthlyPrice: 199.99, annualPrice: 1799.88, annualEffectivePerMo: 149.99, features: ['10 bots', '10 wallets per bot', 'All + Exclusive', 'All chains + future', '1-year history', 'All alerts + SMS + Discord', '6 Killer Features'] },
];

// ─── Payout Wallet Tab ──────────────────────────────────────────────────────

const PAYOUT_CHAINS = [
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', token: 'USDC (ERC-20)', placeholder: '0x...' },
  { id: 'base', name: 'Base', icon: '🔵', token: 'USDC (Base)', placeholder: '0x...' },
  { id: 'polygon', name: 'Polygon', icon: '🟣', token: 'USDC (Polygon)', placeholder: '0x...' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', token: 'USDC (Arbitrum)', placeholder: '0x...' },
  { id: 'solana', name: 'Solana', icon: '◎', token: 'USDC (SPL)', placeholder: 'So...' },
];

// ─── Payout Wallet ──────────────────────────────────────────────────────────
//
// Reads from the same wallets store as Exchanges & RPC. Choose-one-from-your-
// existing-wallets pattern (no duplicate manual input). Only chains the
// dispatcher actually pays out on (base/polygon/arbitrum) — Ethereum is
// excluded because gas eats the 20% margin, Solana is EVM-only-incompatible
// with the current contract dispatcher.

const PAYOUT_CHAIN_META = {
  base:     { label: 'Base',     icon: '🟦', token: 'USDC' },
  polygon:  { label: 'Polygon',  icon: '🟣', token: 'USDC' },
  arbitrum: { label: 'Arbitrum', icon: '🔷', token: 'USDC' },
};

function PayoutWalletTab({ onToast }) {
  const navigate = useNavigate();
  const [walletsDoc, setWalletsDoc] = useState(null);
  const [history, setHistory] = useState(null);
  const [updating, setUpdating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [w, h] = await Promise.all([
        api.listMyWallets(),
        api.getPayoutHistory().catch(() => null),
      ]);
      setWalletsDoc(w);
      setHistory(h);
    } catch (err) {
      onToast?.({ type: 'error', text: err.message });
    }
  }, [onToast]);

  useEffect(() => { refresh(); }, [refresh]);

  const wallets        = walletsDoc?.wallets || [];
  const payoutWallet   = walletsDoc?.payoutWallet || null;
  const eligibleWallets = wallets.filter(w => Object.keys(PAYOUT_CHAIN_META).includes(w.chain));

  // Identify the currently-set payout wallet by matching address+chain
  // (the stored payoutWallet doesn't carry the wallet id).
  const currentPayoutId = useMemo(() => {
    if (!payoutWallet) return null;
    const match = eligibleWallets.find(w =>
      w.address.toLowerCase() === payoutWallet.address?.toLowerCase() && w.chain === payoutWallet.chain
    );
    return match?.id || null;
  }, [eligibleWallets, payoutWallet]);

  async function selectPayout(walletId) {
    setUpdating(true);
    try {
      await api.setPayoutWallet(walletId);
      onToast({ type: 'success', text: walletId ? 'Payout wallet set' : 'Payout wallet cleared' });
      refresh();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setUpdating(false);
    }
  }

  if (walletsDoc === null) {
    return <div className="flex justify-center py-12"><Spinner className="text-emerald-400" /></div>;
  }

  const totals = history?.totals || { receivedUsd: 0, pendingUsd: 0, count: 0, confirmedCount: 0 };

  return (
    <div className="space-y-6">

      {/* Premium emerald hero — earnings tone, not violet (this is the $ page) */}
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-600/15 via-teal-600/10 to-cyan-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-5 h-5 text-emerald-300" />
            <h2 className="text-xl font-bold text-white">Payout Wallet</h2>
          </div>
          <p className="text-sm text-emerald-200/80 mb-5">
            Where USDC tips and your 80% share of paid-channel revenue land. Choose any wallet you've added in <button onClick={() => navigate('/settings?tab=connections')} className="underline text-emerald-100 hover:text-white">Exchanges &amp; RPC</button>.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Total received"   value={`$${totals.receivedUsd.toLocaleString()}`} tone="emerald" />
            <KpiTile label="Pending"          value={`$${totals.pendingUsd.toLocaleString()}`} tone={totals.pendingUsd > 0 ? 'amber' : 'slate'} />
            <KpiTile label="Tip count"        value={totals.confirmedCount}                    tone="cyan" />
            <KpiTile label="Eligible wallets" value={eligibleWallets.length}                   tone="violet" />
          </div>
        </div>
      </div>

      {/* Selection */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Wallet} title="Choose your payout wallet" subtitle="One of your existing wallets becomes the destination for USDC earnings. Only chains the dispatcher actually pays out on are eligible." />

        {eligibleWallets.length === 0 ? (
          <div className="text-center py-8">
            <Wallet className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No eligible wallets yet</p>
            <p className="text-[11px] text-gray-600 mt-1 mb-4">
              Add a wallet on Base, Polygon, or Arbitrum from Exchanges &amp; RPC. Ethereum mainnet, BSC, and Solana are not currently supported for payouts.
            </p>
            <button onClick={() => navigate('/settings?tab=connections')}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-dark-900 text-sm font-bold flex items-center gap-2 mx-auto">
              <Plus className="w-4 h-4" /> Add a wallet
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {eligibleWallets.map(w => {
              const meta = PAYOUT_CHAIN_META[w.chain];
              const selected = currentPayoutId === w.id;
              return (
                <button
                  key={w.id}
                  onClick={() => selectPayout(w.id)}
                  disabled={updating}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    selected
                      ? 'bg-emerald-500/10 border-emerald-500/50'
                      : 'bg-dark-700/30 border-dark-700/50 hover:border-dark-600'
                  }`}>
                  <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 ${
                    selected ? 'bg-emerald-500 border-emerald-400' : 'border-dark-600'
                  }`}>
                    {selected && <Check className="w-3 h-3 text-dark-900" />}
                  </div>
                  <span className="text-xl">{meta?.icon || '🔗'}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white font-medium truncate">{w.label}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                        selected ? 'bg-emerald-500 text-dark-900' : 'bg-dark-700 text-gray-400'
                      }`}>
                        {meta?.label || w.chain} · {meta?.token || 'USDC'}
                      </span>
                      {selected && <span className="text-[10px] font-bold text-emerald-300">★ PRIMARY PAYOUT</span>}
                    </div>
                    <p className="text-[10px] text-gray-500 font-mono mt-0.5 truncate">{w.address}</p>
                  </div>
                </button>
              );
            })}

            {currentPayoutId && (
              <button onClick={() => selectPayout(null)} disabled={updating}
                className="text-[11px] text-gray-500 hover:text-red-300 underline mt-1">
                Clear payout selection
              </button>
            )}
          </div>
        )}

        {wallets.length > eligibleWallets.length && (
          <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
            <p className="text-[11px] text-amber-200">
              <Info className="w-3 h-3 inline-block -mt-0.5 mr-1" />
              You have {wallets.length - eligibleWallets.length} wallet(s) on chains that don't currently support payouts (Ethereum, BSC, Solana). They stay tracked for balance monitoring but won't appear here.
            </p>
          </div>
        )}
      </div>

      {/* How payouts work */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Info} title="How payouts work" subtitle="Real flow, real fees, real cadence" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">Step 1</p>
            <p className="text-sm text-white font-medium">USDC arrives</p>
            <p className="text-[11px] text-gray-500 mt-1">Tipper or subscriber sends USDC to the treasury wallet on Base / Polygon / Arbitrum.</p>
          </div>
          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">Step 2</p>
            <p className="text-sm text-white font-medium">Platform takes 20%</p>
            <p className="text-[11px] text-gray-500 mt-1">Same-chain split. No cross-chain bridge — saves gas and avoids bridge risk.</p>
          </div>
          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">Step 3</p>
            <p className="text-sm text-white font-medium">80% sent to you</p>
            <p className="text-[11px] text-gray-500 mt-1">Dispatched the moment payment confirms — typically within 30 seconds, never more than the worker's hourly retry cycle.</p>
          </div>
        </div>
        <p className="text-[11px] text-gray-600 mt-3">
          <Info className="w-3 h-3 inline-block -mt-0.5 mr-1" />
          <span className="font-medium">Paid-channel creators:</span> you can override this default per-channel during channel application. Tips always route here.
        </p>
      </div>

      {/* Recent payouts */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <SectionHeader icon={Activity} title="Recent tips" subtitle="Last 50, newest first. Anonymous tips show as 'Anonymous'." />
          <ExpiredTipsClearButton history={history} onRefresh={refresh} onToast={onToast} />
        </div>
        {!history?.tips?.length ? (
          <div className="text-center py-8">
            <Activity className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No tips received yet</p>
            <p className="text-[11px] text-gray-600 mt-1">When someone tips your profile or content, it'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
            {history.tips.map(t => {
              const meta = PAYOUT_CHAIN_META[t.chain];
              const isExpired = t.status === 'expired';
              const statusColor = t.status === 'confirmed' || t.status === 'paid_out' ? 'emerald'
                                  : t.status === 'pending' ? 'amber'
                                  : 'slate'; // expired or unknown → muted
              const chip = COLOR_CHIP[statusColor] || COLOR_CHIP.slate;
              return (
                <div key={t.tipId} className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border ${chip.bg} ${chip.border} ${isExpired ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white font-medium truncate">From {t.tipperName || 'Anonymous'}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${chip.bg} ${chip.text}`}>
                        {t.status}
                      </span>
                      <span className="text-[10px] text-gray-500">{meta?.label || t.chain}</span>
                    </div>
                    {t.note && <p className="text-[11px] text-gray-400 mt-0.5 italic truncate">"{t.note}"</p>}
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {timeAgoShort(t.createdAt)}
                      {isExpired && <span className="ml-1.5 text-gray-600">· payment window passed, no funds incoming</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${isExpired ? 'text-gray-500 line-through' : 'text-emerald-300'}`}>
                      +${t.creatorPayout?.toFixed(2) ?? '0.00'}
                    </p>
                    <p className="text-[9px] text-gray-500">of ${t.amount?.toFixed(2)} tip</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Security notice */}
      <div className="bg-amber-500/5 rounded-2xl p-4 border border-amber-500/15">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-400 mb-1">Security</p>
            <ul className="text-[10px] text-gray-400 space-y-0.5">
              <li>• Double-check the selected address on the source-of-truth wallet — payments to wrong addresses cannot be recovered</li>
              <li>• Use a hardware wallet (Ledger, Trezor) for the receiving address whenever possible</li>
              <li>• haythix will never ask for your private keys or seed phrase</li>
              <li>• To change wallets safely, add the new one in Exchanges &amp; RPC FIRST, then switch the selection here</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
}

function ExpiredTipsClearButton({ history, onRefresh, onToast }) {
  const [clearing, setClearing] = useState(false);
  const expiredCount = (history?.tips || []).filter(t => t.status === 'expired').length;
  if (expiredCount === 0) return null;

  async function clearExpired() {
    if (!window.confirm(`Permanently delete ${expiredCount} expired tip record${expiredCount === 1 ? '' : 's'}? This can't be undone.`)) return;
    setClearing(true);
    try {
      const r = await api.clearExpiredPayouts();
      onToast({ type: 'success', text: `Cleared ${r.deleted} expired tip${r.deleted === 1 ? '' : 's'}` });
      onRefresh();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally { setClearing(false); }
  }

  return (
    <button onClick={clearExpired} disabled={clearing}
      className="text-[11px] font-semibold text-gray-400 hover:text-red-300 flex items-center gap-1 disabled:opacity-40">
      {clearing ? <Spinner size="sm" /> : <Trash2 className="w-3 h-3" />}
      Clear {expiredCount} expired
    </button>
  );
}

function SubscriptionTab() {
  const { user, refreshUser } = useAuth();
  const tierCfg = getTierConfig(user?.tier || 'starter');
  const [sub, setSub] = useState(null);
  const [billingCtx, setBillingCtx] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [cryptoTier, setCryptoTier] = useState(null);

  useEffect(() => {
    // Always refresh token on subscription tab load — picks up tier changes from Stripe webhook
    if (refreshUser) refreshUser();

    getSubscriptionStatus()
      .then(setSub)
      .catch(() => setSub(null))
      .finally(() => setLoadingSub(false));

    // Billing context (founders / payment method / invoices / upcoming) +
    // usage counts run in parallel with the main status — they're decorative,
    // never block the page.
    api.getBillingContext().then(setBillingCtx).catch(() => setBillingCtx(null));
    api.getAccountUsage().then(setUsage).catch(() => setUsage(null));

    // Auto-open crypto modal if URL has ?plan=X&path=usdc (post-register redirect)
    const params = new URLSearchParams(window.location.search);
    if (params.get('path') === 'usdc' && params.get('plan')) {
      setCryptoTier(params.get('plan'));
      // Clean the URL so refresh doesn't re-trigger
      window.history.replaceState({}, '', '/settings?tab=subscription');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpgrade = async (tier) => {
    setCheckoutLoading(tier);
    setError(null);
    try {
      // If user already has a Stripe subscription, use change-tier (proration)
      // Otherwise create new checkout
      const hasPaidSub = sub?.stripeSubscriptionId && sub?.status === 'active';

      if (hasPaidSub) {
        // Paid subscriber — go to Stripe Portal for upgrade/downgrade with proration
        try {
          const data = await getBillingPortal();
          if (data?.portalUrl) { window.location.href = data.portalUrl; return; }
        } catch {}
        setError('Could not open billing portal. Please try again.');
        setCheckoutLoading(null);
        return;
      }

      // Trial user or no subscription — go to Stripe Checkout (captures payment)
      const data = await createCheckoutSession(tier, billingPeriod);
      if (data?.checkoutUrl) window.location.href = data.checkoutUrl;
    } catch (err) {
      const msg = err.message || '';
      setError(
        msg.toLowerCase().includes('not configured') || msg.toLowerCase().includes('price id')
          ? 'Checkout is temporarily unavailable. Please contact support to complete your upgrade.'
          : `Could not process upgrade: ${msg}`
      );
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const data = await getBillingPortal();
      if (data?.portalUrl) window.location.href = data.portalUrl;
    } catch (err) {
      setError('Could not open billing portal. Please try again or contact support.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loadingSub) {
    return <div className="flex justify-center py-20"><Spinner className="text-accent-primary" /></div>;
  }

  const currentTier = user?.tier || 'starter';
  const isAdmin = user?.role === 'admin';

  // Trial countdown — only when trialEndsAt is in the future
  const trialDaysLeft = sub?.trialEndsAt
    ? Math.ceil((new Date(sub.trialEndsAt).getTime() - Date.now()) / 86400_000)
    : null;
  const onTrial = trialDaysLeft != null && trialDaysLeft > 0;

  const isFounder = !!billingCtx?.isFounder;
  const founderNumber = billingCtx?.founderNumber;
  const upcoming = billingCtx?.upcomingInvoice;
  const pm = billingCtx?.paymentMethod;
  const invoices = billingCtx?.recentInvoices || [];

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Premium violet hero with live KPIs */}
      <div className={`relative overflow-hidden rounded-2xl border p-6 ${
        isAdmin ? 'border-red-500/30 bg-gradient-to-br from-red-600/15 via-orange-600/10 to-amber-700/15' :
        sub?.status === 'past_due' ? 'border-red-500/40 bg-gradient-to-br from-red-600/20 via-red-700/10 to-red-900/15' :
        isFounder ? 'border-amber-500/30 bg-gradient-to-br from-amber-600/15 via-yellow-600/10 to-orange-700/15' :
        'border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15'
      }`}>
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Crown className={`w-5 h-5 ${isAdmin ? 'text-red-300' : isFounder ? 'text-amber-300' : 'text-violet-300'}`} />
                <h2 className="text-xl font-bold text-white">{isAdmin ? 'Control Room' : tierCfg.label}</h2>
                {isAdmin && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/30 text-red-200 border border-red-500/50 font-bold">ADMIN</span>}
                {sub?.status === 'active' && !isAdmin && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-200 border border-emerald-500/50 font-bold">ACTIVE</span>}
                {sub?.status === 'past_due' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/40 text-red-100 border border-red-400 font-bold animate-pulse">PAYMENT DUE</span>}
                {isFounder && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-200 border border-amber-400 font-bold">★ FOUNDER #{founderNumber}</span>}
                {onTrial && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/40 font-bold">TRIAL · {trialDaysLeft}d LEFT</span>}
              </div>
              <p className="text-sm text-violet-200/80">
                {isAdmin ? 'Full platform access. No billing.' :
                 sub?.status === 'past_due' ? 'Your payment failed. Update your card to keep your bots running.' :
                 isFounder ? `★ 50% off forever as one of the first 100 founders. Locked in for life.` :
                 onTrial ? `Your 14-day trial of all features ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}.` :
                 `Your active haythix subscription.`}
              </p>
            </div>
            {!isAdmin && sub?.stripeCustomerId && (
              <button
                onClick={handlePortal}
                disabled={portalLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold transition-colors">
                {portalLoading ? <Spinner size="sm" /> : <ExternalLink className="w-4 h-4" />}
                Manage Billing
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Plan"       value={isAdmin ? 'Admin' : tierCfg.label} tone={isFounder ? 'amber' : 'violet'} />
            <KpiTile label="Status"     value={sub?.status === 'active' ? 'Active' : sub?.status === 'past_due' ? 'Past due' : (onTrial ? 'Trial' : (sub?.status || '—'))} tone={sub?.status === 'active' ? 'emerald' : sub?.status === 'past_due' ? 'red' : 'cyan'} />
            <KpiTile label="Next bill"  value={upcoming ? `$${upcoming.amount.toFixed(2)}` : '—'} tone="violet" />
            <KpiTile label="Renews"     value={sub?.periodEnd ? new Date(sub.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (onTrial ? `${trialDaysLeft}d` : '—')} tone="cyan" />
          </div>
        </div>
      </div>

      {/* Past-due recovery CTA — only when needed */}
      {sub?.status === 'past_due' && !isAdmin && (
        <div className="rounded-2xl border-2 border-red-500/40 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-300">Payment failed</p>
              <p className="text-[12px] text-red-200/80 mt-1">Your card was declined. Bots and paid features will stop working soon if not resolved.</p>
              <button onClick={handlePortal} disabled={portalLoading}
                className="mt-3 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-bold flex items-center gap-2">
                {portalLoading ? <Spinner size="sm" /> : <CreditCard className="w-4 h-4" />}
                Update payment method
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annual savings nudge — only when on monthly */}
      {!isAdmin && sub?.status === 'active' && billingCtx?.billingPeriod === 'monthly' && (
        <button onClick={handlePortal} disabled={portalLoading}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/30 text-left transition-colors">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white">Switch to annual and save up to 50%</p>
              <p className="text-[11px] text-emerald-200/70 mt-0.5">Lock in lower per-month pricing for 12 months.</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-emerald-300" />
        </button>
      )}

      {/* Usage breakdown — shows live counts vs tier limits */}
      {!isAdmin && usage && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <SectionHeader icon={Activity} title="Plan Usage" subtitle="Live counts vs your current tier limits — drives upgrade decisions, not just bills." />
          <div className="space-y-4">
            <UsageBar
              label="Active bots"
              used={usage.bots?.total || 0}
              limit={tierCfg.maxBots}
              breakdown={[
                usage.bots?.smartTradesOpen   ? `${usage.bots.smartTradesOpen} smart trade${usage.bots.smartTradesOpen === 1 ? '' : 's'}` : null,
                usage.bots?.algoOrdersActive  ? `${usage.bots.algoOrdersActive} algo order${usage.bots.algoOrdersActive === 1 ? '' : 's'}` : null,
                usage.bots?.conditionalActive ? `${usage.bots.conditionalActive} conditional strateg${usage.bots.conditionalActive === 1 ? 'y' : 'ies'}` : null,
              ].filter(Boolean).join(' · ')} />
            <UsageBar label="Exchanges connected"  used={usage.exchangesConnected || 0} limit={null} />
            <UsageBar label="Wallets tracked"      used={usage.walletsTracked || 0}     limit={null} />
            <UsageBar label="Alert channels on"    used={usage.alertChannelsEnabled || 0} limit={4} />
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Upgrade options */}
      {!isAdmin && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold text-sm">
              {currentTier === 'elite' ? 'Your Features' : 'Upgrade Your Plan'}
            </h3>
            <div className="flex bg-dark-700 rounded-lg p-0.5">
              <button onClick={() => setBillingPeriod('monthly')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-accent-primary text-dark-900' : 'text-gray-400'}`}>
                Monthly
              </button>
              <button onClick={() => setBillingPeriod('annual')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${billingPeriod === 'annual' ? 'bg-accent-primary text-dark-900' : 'text-gray-400'}`}>
                Annual <span className="text-[8px] text-emerald-400 ml-0.5">Save up to 50%</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {UPGRADE_TIERS.map(tier => {
              const cfg = getTierConfig(tier.id);
              const isCurrent = tier.id === currentTier;
              const tierOrder = { starter: 1, trader: 2, pro: 3, elite: 4 };
              const isUpgrade = (tierOrder[tier.id] || 0) > (tierOrder[currentTier] || 0);

              return (
                <div
                  key={tier.id}
                  className={`rounded-xl p-4 border ${cfg.borderColor} bg-dark-800/60 ${isCurrent ? cfg.bgColor : ''} relative`}
                >
                  {isCurrent && (
                    <div className={`absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-xs font-bold ${cfg.badgeClass} border`}>
                      Current
                    </div>
                  )}
                  <div className={`font-bold text-sm mb-0.5 ${cfg.color}`}>{tier.label}</div>
                  <div className="mb-3">
                    {billingPeriod === 'annual' ? (
                      <>
                        <div className="text-white font-semibold text-lg">
                          ${tier.annualEffectivePerMo.toFixed(2)}<span className="text-gray-500 text-xs font-normal">/mo</span>
                        </div>
                        <div className="text-[10px] text-emerald-400">
                          ${tier.annualPrice}/yr · save ${(tier.monthlyPrice * 12 - tier.annualPrice).toFixed(2)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-white font-semibold text-lg">
                          ${tier.monthlyPrice.toFixed(2)}<span className="text-gray-500 text-xs font-normal">/mo</span>
                        </div>
                        <div className="text-[10px] text-gray-500">billed monthly</div>
                      </>
                    )}
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div className="w-full text-center py-2 text-xs text-gray-500">Current plan</div>
                  ) : (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => handleUpgrade(tier.id)}
                        disabled={!!checkoutLoading}
                        className={`w-full py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                          isUpgrade
                            ? 'bg-accent-primary text-dark-900 hover:bg-accent-primary/90'
                            : 'border border-dark-600 text-gray-400 hover:text-white hover:border-dark-500'
                        }`}
                      >
                        {checkoutLoading === tier.id ? <Spinner size="sm" /> : null}
                        💳 {isUpgrade ? `Upgrade to ${tier.label}` : `Switch to ${tier.label}`}
                      </button>
                      <button onClick={() => setCryptoTier(tier.id)}
                        className="w-full py-2 rounded-lg text-[11px] font-semibold text-emerald-300 hover:text-emerald-200 border border-emerald-500/20 hover:border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 transition-all flex items-center justify-center gap-1.5"
                      >
                        ⚡ Pay with Crypto (USDC)
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pay-with-Crypto modal */}
      {cryptoTier && (
        <PayTierWithCrypto
          tier={cryptoTier}
          period={billingPeriod}
          onSuccess={() => {
            setCryptoTier(null);
            if (refreshUser) setTimeout(() => refreshUser(), 1500);
          }}
          onClose={() => setCryptoTier(null)}
        />
      )}


      {/* What's included */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700">
        <SectionHeader icon={BadgeCheck} title="What's Included in Your Plan" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: `${tierCfg.maxBots} trading bot${tierCfg.maxBots > 1 ? 's' : ''}`, included: true },
            { label: `${tierCfg.maxWalletsPerBot} wallets per bot`, included: true },
            { label: `${tierCfg.strategyCount === 'all' ? 'All' : tierCfg.strategyCount} strategies`, included: true },
            { label: `${tierCfg.historyDays}-day trade history`, included: true },
            { label: `Chains: ${(tierCfg.chains || []).join(', ') || '—'}`, included: true },
            { label: `Alerts: ${(tierCfg.alertTypes || []).length ? (tierCfg.alertTypes || []).join(', ') : 'None'}`, included: true },
            { label: 'Gas price optimizer', included: tierCfg.features.gasMonitor },
            { label: 'Bot cloning across chains', included: tierCfg.features.botCloning },
            { label: 'Cross-chain arbitrage detector', included: tierCfg.features.arbitrageDetector },
            { label: 'Portfolio rebalancing', included: tierCfg.features.portfolioRebalancing },
            { label: 'Backtesting engine', included: tierCfg.features.backtesting },
            { label: 'Advanced AI signals', included: tierCfg.features.advancedSignals },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              {item.included
                ? <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                : <X className="w-4 h-4 text-dark-500 flex-shrink-0" />
              }
              <span className={`text-sm ${item.included ? 'text-gray-300' : 'text-dark-500'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment method + invoices — premium SaaS standard */}
      {!isAdmin && billingCtx && (pm || invoices.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pm && (
            <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
              <SectionHeader icon={CreditCard} title="Payment Method" />
              <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-dark-700/40 border border-dark-700/50">
                <div>
                  <p className="text-sm text-white font-mono uppercase">{pm.brand} •••• {pm.last4}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">Expires {String(pm.expMonth).padStart(2, '0')}/{String(pm.expYear).slice(-2)}</p>
                </div>
                <button onClick={handlePortal} disabled={portalLoading}
                  className="text-[11px] text-violet-300 hover:text-violet-200 font-semibold flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Change
                </button>
              </div>
            </div>
          )}
          {invoices.length > 0 && (
            <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
              <SectionHeader icon={HardDrive} title="Recent Invoices" />
              <div className="space-y-1.5">
                {invoices.slice(0, 3).map(inv => (
                  <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-dark-700/30 border border-dark-700/50">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white">${inv.amountPaid.toFixed(2)} {inv.currency}</p>
                      <p className="text-[10px] text-gray-500">{inv.created ? new Date(inv.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} · <span className={
                        inv.status === 'paid' ? 'text-emerald-400' :
                        inv.status === 'open' ? 'text-amber-400' :
                        inv.status === 'void' ? 'text-gray-500' :
                        'text-gray-400'
                      }>{inv.status}</span></p>
                    </div>
                    {inv.invoicePdf && (
                      <a href={inv.invoicePdf} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-violet-300 hover:text-violet-200 font-semibold">PDF</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cancel link — transparent, trust-building */}
      {!isAdmin && sub?.stripeSubscriptionId && sub?.status === 'active' && (
        <div className="text-center pt-4">
          <button onClick={handlePortal} disabled={portalLoading}
            className="text-[11px] text-gray-500 hover:text-red-300 underline">
            Cancel subscription
          </button>
          <p className="text-[10px] text-gray-600 mt-1">
            Opens the Stripe portal. You keep paid features until the end of the current billing period.
          </p>
        </div>
      )}
    </div>
  );
}

function UsageBar({ label, used, limit, breakdown }) {
  const hasLimit = limit != null && limit !== 'all' && Number.isFinite(limit);
  const pct = hasLimit && limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="text-xs text-gray-500 font-mono">
          <span className="text-white font-bold">{used}</span>{hasLimit ? <> / {limit}</> : null}
        </p>
      </div>
      {hasLimit && (
        <div className="h-1.5 rounded-full bg-dark-700 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {breakdown && <p className="text-[10px] text-gray-500 mt-1">{breakdown}</p>}
    </div>
  );
}

// (Team Plans UI removed — feature paused, will revisit later)

// ═══════════════════════════════════════════════════════════════════════════
// Admin sub-sections — extracted from the old GeneralTab so the admin stuff
// can live in its own admin-only group instead of being mixed in with
// user-facing settings.
// ═══════════════════════════════════════════════════════════════════════════

function formatUptime(seconds) {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatStorage(kb) {
  if (!kb) return '0 KB';
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function IntegrationDot({ ok, label, detail }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-dark-700/40 border border-dark-600/30">
      <div className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-gray-500'}`}
        style={ok ? { boxShadow: '0 0 6px #34d399' } : undefined} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white truncate">{label}</p>
        {detail && <p className="text-[10px] text-gray-500 truncate">{detail}</p>}
      </div>
      <span className={`text-[10px] font-bold uppercase ${ok ? 'text-emerald-400' : 'text-gray-500'}`}>
        {ok ? 'Connected' : 'Not configured'}
      </span>
    </div>
  );
}

function SystemHealthSection({ health, loading, refresh }) {
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await getSystemOverview();
      setOverview(data);
    } catch (err) {
      setOverviewError(err?.message || 'Could not load system overview');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const refreshAll = useCallback(() => { refresh?.(); loadOverview(); }, [refresh, loadOverview]);

  const statusColor =
    health?.status === 'healthy' ? 'text-emerald-400' :
    health?.status === 'warning' ? 'text-amber-400'   : 'text-red-400';
  const statusBg =
    health?.status === 'healthy' ? 'bg-emerald-500/10 border-emerald-500/20' :
    health?.status === 'warning' ? 'bg-amber-500/10 border-amber-500/20'     : 'bg-red-500/10 border-red-500/20';

  // Critical operator-visible warnings: simulation mode flipped on, or
  // treasury wallet not configured (server-signed payouts will silently fail).
  const simBanner = overview && (overview.simulationMode === 'true' || overview.dexSimulationMode === 'true');
  const treasuryWarn = overview && overview.integrations?.stripe?.configured && !overview.treasury?.configured;
  const stripeTestBanner = overview && overview.integrations?.stripe?.mode === 'test';

  const anyLoading = loading || overviewLoading;

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <SectionHeader icon={Activity} title="System Health" subtitle="Live platform telemetry — refreshed on demand" />
        <button onClick={refreshAll} disabled={anyLoading}
          className="btn-ghost p-1.5 -mt-1 flex items-center gap-1.5 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${anyLoading ? 'animate-spin' : ''}`} />
          <span>{anyLoading ? 'Refreshing…' : 'Refresh'}</span>
        </button>
      </div>

      {/* Operator alarms — surfaced before anything else */}
      {simBanner && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-300">Simulation mode is ON</p>
            <p className="text-xs text-red-300/80 mt-0.5">
              {overview.simulationMode === 'true' ? 'SIMULATION_MODE=true' : ''}
              {overview.simulationMode === 'true' && overview.dexSimulationMode === 'true' ? ' · ' : ''}
              {overview.dexSimulationMode === 'true' ? 'DEX_SIMULATION_MODE=true' : ''}
              {' '}— no real trades will execute. Flip these off in App Settings to go live.
            </p>
          </div>
        </div>
      )}
      {stripeTestBanner && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-300">Stripe is in TEST mode</p>
            <p className="text-xs text-amber-300/80 mt-0.5">Live charges are not being processed. Set STRIPE_TEST_MODE=false to switch to live billing.</p>
          </div>
        </div>
      )}
      {treasuryWarn && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-300">Treasury hot wallet not configured</p>
            <p className="text-xs text-amber-300/80 mt-0.5">Server-signed creator payouts and channel renewals will fail silently until TREASURY_HOT_PK is set.</p>
          </div>
        </div>
      )}

      {/* Overall bot status pill — kept from old design */}
      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="text-accent-primary" /></div>
      ) : health ? (
        <div className={`flex items-center justify-between p-4 rounded-xl border ${statusBg}`}>
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${
              health.status === 'healthy' ? 'bg-emerald-400' :
              health.status === 'warning' ? 'bg-amber-400'   : 'bg-red-400'
            }`} style={health.status === 'healthy' ? { boxShadow: '0 0 8px #34d399' } : undefined} />
            <div>
              <p className="text-white font-medium text-sm">Bot Fleet Status</p>
              <p className="text-gray-500 text-xs">Last checked: {new Date(health.timestamp).toLocaleTimeString()}</p>
            </div>
          </div>
          <span className={`text-xs font-bold uppercase tracking-widest ${statusColor}`}>{health.status}</span>
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4 text-sm">Unable to load bot fleet health</p>
      )}

      {/* Bot counters */}
      {health && (
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Bots</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Total"   value={health.bots?.total   || 0} />
            <StatPill label="Healthy" value={health.bots?.healthy || 0} color="text-emerald-400" />
            <StatPill label="Warning" value={health.bots?.warning || 0} color="text-amber-400"   />
            <StatPill label="Paused"  value={health.bots?.paused  || 0} color="text-gray-400"    />
          </div>
        </div>
      )}

      {/* Platform stats from /mgmt/system-overview */}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Platform usage</p>
        {overviewError ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-300">{overviewError}</p>
          </div>
        ) : overview ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Users"          value={overview.totalUsers || 0} />
            <StatPill label="Trades today"   value={overview.totalTrades || 0} />
            <StatPill label="Sessions"       value={overview.activeSessions || 0} />
            <StatPill label="Bots"           value={overview.botCount || 0} />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-pulse">
            {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl bg-dark-700/40" />)}
          </div>
        )}
      </div>

      {/* Infrastructure runtime info */}
      {overview && (
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Runtime</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Uptime"      value={formatUptime(overview.uptime)} />
            <StatPill label="Functions"   value={overview.functionList?.length || 0} />
            <StatPill label="Storage"     value={formatStorage(overview.storageStats?.totalSizeKb)} />
            <StatPill label="Files"       value={overview.storageStats?.totalFiles || 0} />
          </div>
        </div>
      )}

      {/* Integration presence */}
      {overview && overview.integrations && (
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Integrations</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <IntegrationDot ok={overview.integrations.stripe?.configured}
              label="Stripe billing"
              detail={overview.integrations.stripe?.configured ? `${overview.integrations.stripe.mode} mode` : 'Stripe key not set'} />
            <IntegrationDot ok={overview.integrations.email?.configured}
              label="Transactional email"
              detail={overview.integrations.email?.configured ? 'Connection string present' : 'Email connection not set'} />
            <IntegrationDot ok={overview.integrations.treasury?.configured}
              label="Treasury hot wallet"
              detail={overview.treasury?.address ? `Address ${overview.treasury.address}` : 'TREASURY_HOT_PK not set'} />
            <IntegrationDot ok={overview.integrations.subSpender?.configured}
              label="Subscription auto-renew"
              detail={overview.integrations.subSpender?.configured ? 'SUBSCRIPTION_SPENDER_PK present' : 'Auto-renew paused'} />
          </div>
        </div>
      )}
    </div>
  );
}

// Confirmation modal — all destructive/expensive ops route through this so
// the operator can't fat-finger a renewal sweep or a verification re-send.
function OpsConfirmModal({ open, op, onCancel, onConfirm, running }) {
  if (!open || !op) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${op.warn ? 'bg-amber-500/15 text-amber-400' : 'bg-accent-primary/15 text-accent-primary'}`}>
            {op.warn ? <AlertTriangle className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Confirm: {op.label}</p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">{op.confirmText}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={running} className="btn-ghost text-xs">Cancel</button>
          <button onClick={onConfirm} disabled={running} className="btn-primary text-xs flex items-center gap-1.5">
            {running ? <><Spinner size="sm" /> Running…</> : <><Zap className="w-3 h-3" /> Run now</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpCard({ icon: Icon, accent, label, description, onClick, running, disabled }) {
  return (
    <div className="bg-dark-700/60 rounded-xl p-5 border border-dark-600/30">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="text-white font-medium text-sm">{label}</p>
          <p className="text-gray-500 text-xs mt-1 leading-relaxed">{description}</p>
          <button onClick={onClick} disabled={disabled}
            className="btn-secondary text-xs flex items-center gap-1.5 mt-4">
            {running ? <><Spinner size="sm" /> Running…</> : <><Zap className="w-3 h-3" /> Trigger</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditLogRow({ entry }) {
  const dt = entry.timestamp ? new Date(entry.timestamp) : null;
  const okColor = entry.outcome === 'success' ? 'text-emerald-400' : 'text-red-400';
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-lg bg-dark-700/40 border border-dark-600/30">
      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${entry.outcome === 'success' ? 'bg-emerald-400' : 'bg-red-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-white truncate">{entry.action}</p>
          <span className={`text-[10px] font-bold uppercase ${okColor}`}>{entry.outcome}</span>
        </div>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {dt ? dt.toLocaleString() : entry.timestamp} · {entry.actorEmail || entry.actorUserId || 'unknown'}
        </p>
        {entry.details && (
          <p className="text-[10px] text-gray-600 font-mono mt-0.5 break-all">
            {typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}
          </p>
        )}
      </div>
    </div>
  );
}

function ManualOpsSection({ onToast, refresh }) {
  const [running, setRunning] = useState(null);
  const [confirmOp, setConfirmOp] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await getMgmtAuditLog();
      setAudit(data?.entries || []);
    } catch (err) {
      onToast?.({ type: 'error', text: err.message });
    } finally {
      setAuditLoading(false);
    }
  }, [onToast]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  // Each op declares its own runner so the confirm modal stays generic.
  // Trade Orchestrator was removed when MCN volume-keeper was retired — the
  // backend route no longer exists, so the button would 404. Re-add here if
  // a replacement orchestrator endpoint ships.
  const ops = {
    health: {
      id: 'health', label: 'System Health Check', icon: Activity, accent: 'bg-emerald-500/15 text-emerald-400',
      description: 'Run a full platform diagnostic and refresh the health summary above.',
      confirmText: 'Runs a fleet-wide health probe. Safe — read-only.',
      warn: false,
      run: async () => { await triggerHealthCheck(); refresh?.(); },
    },
    forecast: {
      id: 'forecast', label: 'Resolve Pending Forecasts', icon: Clock, accent: 'bg-violet-500/15 text-violet-400',
      description: 'Run the forecast resolver pass — normally runs every 15 min. Resolves up to 50 pending entries per call.',
      confirmText: 'Resolves pending forecasts using real OHLCV. Never fabricates an outcome.',
      warn: false,
      run: async () => { await mgmtTriggerForecastResolver(); },
    },
    refreshCcxt: {
      id: 'refreshCcxt', label: 'Refresh CCXT Metadata', icon: RefreshCw, accent: 'bg-cyan-500/15 text-cyan-400',
      description: 'Flag intent to invalidate CCXT exchange metadata. Today this is a no-op + audit entry — CCXT loads fresh per request.',
      confirmText: 'Records the intent in the audit log. No live cache to invalidate today.',
      warn: false,
      run: async () => { await mgmtRefreshCcxt(); },
    },
  };

  const requestRun = (opId) => setConfirmOp(ops[opId]);
  const cancelConfirm = () => setConfirmOp(null);

  const confirmRun = async () => {
    if (!confirmOp) return;
    setRunning(confirmOp.id);
    try {
      await confirmOp.run();
      onToast?.({ type: 'success', text: `${confirmOp.label} completed` });
      loadAudit();
    } catch (err) {
      onToast?.({ type: 'error', text: err.message });
    } finally {
      setRunning(null);
      setConfirmOp(null);
    }
  };

  const resendVerification = async () => {
    const email = resendEmail.trim().toLowerCase();
    if (!email) { onToast?.({ type: 'error', text: 'Enter an email first' }); return; }
    setRunning('resend');
    try {
      const result = await mgmtResendVerification(email);
      onToast?.({
        type: 'success',
        text: result?.alreadyVerified ? 'User is already verified' : 'Verification email re-sent',
      });
      setResendEmail('');
      loadAudit();
    } catch (err) {
      onToast?.({ type: 'error', text: err.message });
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <OpsConfirmModal open={!!confirmOp} op={confirmOp}
        onCancel={cancelConfirm} onConfirm={confirmRun}
        running={running === confirmOp?.id} />

      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50 space-y-5">
        <SectionHeader icon={Terminal} title="Manual Operations" subtitle="Run scheduled jobs on-demand. Every action is audit-logged." />

        {/* Action cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <OpCard {...ops.health}
            onClick={() => requestRun('health')}
            running={running === 'health'}
            disabled={!!running} />
          <OpCard {...ops.forecast}
            onClick={() => requestRun('forecast')}
            running={running === 'forecast'}
            disabled={!!running} />
          <OpCard {...ops.refreshCcxt}
            onClick={() => requestRun('refreshCcxt')}
            running={running === 'refreshCcxt'}
            disabled={!!running} />
        </div>

        {/* Resend verification — inline form because it takes input */}
        <div className="bg-dark-700/60 rounded-xl p-5 border border-dark-600/30">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-pink-500/15 text-pink-400 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Re-send Email Verification</p>
              <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                For users stuck on the verify-email step. Mints a fresh 24-hour token and re-sends the verification email.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
                <button onClick={resendVerification} disabled={!!running || !resendEmail.trim()}
                  className="btn-primary text-xs flex items-center gap-1.5 px-3 py-2">
                  {running === 'resend' ? <><Spinner size="sm" /> Sending…</> : <><Mail className="w-3 h-3" /> Send</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Audit log */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Audit log — last {audit.length || 0}</p>
            <button onClick={loadAudit} disabled={auditLoading}
              className="btn-ghost p-1.5 flex items-center gap-1 text-xs">
              <RefreshCw className={`w-3 h-3 ${auditLoading ? 'animate-spin' : ''}`} />
              <span>{auditLoading ? 'Loading…' : 'Refresh'}</span>
            </button>
          </div>
          {audit.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-4">No admin actions recorded yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {audit.map(entry => <AuditLogRow key={entry.id} entry={entry} />)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Build-time injected by vite.config.js — never hand-edit these constants.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
const GIT_SHA     = typeof __GIT_SHA__     !== 'undefined' ? __GIT_SHA__     : 'dev';
const GIT_BRANCH  = typeof __GIT_BRANCH__  !== 'undefined' ? __GIT_BRANCH__  : 'unknown';

// Platform coverage — kept in sync with MultiChainPage SUPPORTED_CHAINS
// and the ALL_EXCHANGES list further up this file. About surfaces COUNTS
// (not the full lists) because the admin already sees them on those pages.
const PLATFORM_COVERAGE = {
  chains: [
    'Ethereum', 'BSC', 'Polygon', 'Arbitrum', 'Base', 'Avalanche', 'Optimism',
    'Solana', 'Cronos', 'Fantom', 'zkSync Era', 'Mantle', 'Linea', 'Scroll', 'Blast',
  ],
  exchangeCount: 16,
};

function AboutSection() {
  const env = import.meta.env.MODE === 'production' ? 'Production' : 'Development';
  const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : 'dev';

  // Resource shortcuts the admin/operator reaches for. Internal app routes
  // are SPA-routed; the legal/external ones open in a new tab.
  const RESOURCES = [
    { label: 'Help Center',       to: '/help-center',       external: false, icon: Info },
    { label: 'Learning Paths',    to: '/learning-paths',    external: false, icon: Sparkles },
    { label: 'Learning Library',  to: '/learning-library',  external: false, icon: BarChart3 },
    { label: 'Pricing',           to: '/pricing',           external: false, icon: CreditCard },
    { label: 'Features',          to: '/features',          external: false, icon: BadgeCheck },
    { label: 'Comparison',        to: '/comparison',        external: false, icon: Layers },
  ];

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50 space-y-5">
      <SectionHeader icon={Info} title="About Platform" subtitle="Build metadata, deployment context, and platform coverage at a glance" />

      {/* Build metadata — pulled from package.json + git at build time */}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Build metadata</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: 'Version',     value: `v${APP_VERSION}` },
            { label: 'Environment', value: env, accent: env === 'Production' ? 'text-emerald-400' : 'text-amber-400' },
            { label: 'Build date',  value: buildDate },
            { label: 'Commit',      value: GIT_SHA,    mono: true },
            { label: 'Branch',      value: GIT_BRANCH, mono: true },
            { label: 'Runtime',     value: 'Browser' },
          ].map(({ label, value, accent, mono }) => (
            <div key={label} className="bg-dark-700/50 rounded-xl p-4 border border-dark-600/30">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider">{label}</p>
              <p className={`${accent || 'text-white'} ${mono ? 'font-mono' : 'font-medium'} text-sm mt-1 truncate`}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Platform coverage — counts, not feature-flag lists. No infra leaks. */}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Platform coverage</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-dark-700/50 rounded-xl p-4 border border-dark-600/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white text-sm font-semibold">Supported chains</p>
              <span className="text-2xl font-bold text-accent-primary">{PLATFORM_COVERAGE.chains.length}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PLATFORM_COVERAGE.chains.map(c => (
                <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-dark-600/60 text-gray-300 border border-dark-500/30">{c}</span>
              ))}
            </div>
          </div>
          <div className="bg-dark-700/50 rounded-xl p-4 border border-dark-600/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white text-sm font-semibold">CEX integrations</p>
              <span className="text-2xl font-bold text-accent-primary">{PLATFORM_COVERAGE.exchangeCount}</span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Spot + futures connectivity across 16 centralised exchanges via the unified CCXT adapter.
              Full per-exchange detail in <span className="text-accent-primary">Settings → Connections</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Resource shortcuts — jump-off for the user-facing docs surface */}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Resources</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {RESOURCES.map(({ label, to, external, icon: Icon }) => {
            const className = "flex items-center gap-2 px-3 py-2.5 rounded-lg bg-dark-700/40 hover:bg-dark-700 border border-dark-600/30 hover:border-accent-primary/30 transition-all text-sm text-gray-300 hover:text-white";
            return external ? (
              <a key={label} href={to} target="_blank" rel="noopener noreferrer" className={className}>
                <Icon className="w-3.5 h-3.5 text-accent-primary" />
                <span>{label}</span>
                <ExternalLink className="w-3 h-3 ml-auto text-gray-500" />
              </a>
            ) : (
              <a key={label} href={to} className={className}>
                <Icon className="w-3.5 h-3.5 text-accent-primary" />
                <span>{label}</span>
                <ChevronRight className="w-3 h-3 ml-auto text-gray-500" />
              </a>
            );
          })}
        </div>
      </div>

      {/* Footer — what the platform IS, no specific framework/library names */}
      <div className="pt-3 border-t border-dark-700/50">
        <p className="text-gray-500 text-xs leading-relaxed">
          <span className="text-white font-semibold">haythix</span> — multi-chain crypto trading and analytics platform.
          Spot + perp futures, DEX + CEX execution, AI signals, paper trading, copy trading, and a creator monetisation layer.
          All claims backed by verifiable on-chain data and a transparent forecast-accuracy track record.
        </p>
      </div>
    </div>
  );
}

function ComingSoonSection({ phase, title, description }) {
  return (
    <div className="bg-gradient-to-br from-violet-500/8 via-dark-800/80 to-dark-900 rounded-2xl border border-violet-500/20 p-8 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-violet-500/30 to-purple-500/30 flex items-center justify-center mb-4">
        <Clock className="w-7 h-7 text-violet-300" />
      </div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 max-w-md mx-auto mb-4">{description}</p>
      <span className="inline-block text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
        Coming in {phase}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 2: Privacy, Data Export, Account Deletion, Login History
// ═══════════════════════════════════════════════════════════════════════════

// `notYet` marks toggles whose preference is saved but not yet enforced by
// any feature — the underlying feature is still in the roadmap. Telling
// users this up-front is more honest than silently storing the flag.
const PRIVACY_OPTIONS = [
  { key: 'profilePublic',       label: 'Public profile',          desc: 'Allow anyone to view your community profile. When off, only your name and tier are visible to strangers.' },
  { key: 'showInLeaderboards',  label: 'Show in leaderboards',    desc: 'Include your rank on community leaderboards (Most Active, Top Callers, etc).' },
  { key: 'allowFollows',        label: 'Allow others to follow',  desc: 'Other users can follow your profile. Disabling hides the Follow button and blocks new followers.' },
  { key: 'allowTradeSharing',   label: 'Allow trade sharing',     desc: 'Let your closed trades be shared as snapshots in chat rooms.' },
  { key: 'allowDirectMessages', label: 'Allow direct messages',   desc: 'Other community members can send you direct messages.',                                       notYet: 'DMs coming soon' },
  { key: 'marketingEmails',     label: 'Marketing emails',        desc: 'Product updates, feature launches, and educational content (separate from transactional alerts).', notYet: 'Mailing list coming soon' },
];

function PrivacyDataSection({ onToast }) {
  const [privacy, setPrivacy] = useState(null);
  const [saving, setSaving] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    api.getAccountPrivacy().then(d => setPrivacy(d?.privacy || {})).catch(() => setPrivacy({}));
    api.getAccountDeletionStatus().then(d => setDeletionStatus(d || { pending: false })).catch(() => setDeletionStatus({ pending: false }));
  }, []);

  async function togglePrivacy(key, value) {
    setSaving(key);
    try {
      const updated = await api.updateAccountPrivacy({ [key]: value });
      setPrivacy(updated?.privacy || { ...privacy, [key]: value });
      onToast({ type: 'success', text: 'Privacy setting updated' });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setSaving(null);
  }

  async function downloadExport() {
    setExporting(true);
    try {
      const blob = await api.exportAccountData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `haythix-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onToast({ type: 'success', text: 'Your data export downloaded' });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setExporting(false);
  }

  async function requestDelete() {
    if (deleteConfirm !== 'DELETE MY ACCOUNT') return;
    setDeleteBusy(true);
    try {
      const res = await api.requestAccountDeletion(deleteConfirm, deleteReason);
      setDeletionStatus({ pending: true, ...res, daysLeft: 7 });
      setDeleteOpen(false);
      setDeleteConfirm('');
      setDeleteReason('');
      onToast({ type: 'success', text: 'Deletion requested. You have 7 days to cancel.' });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setDeleteBusy(false);
  }

  async function cancelDelete() {
    if (!confirm('Cancel the deletion request and keep your account?')) return;
    setDeleteBusy(true);
    try {
      await api.cancelAccountDeletion();
      setDeletionStatus({ pending: false });
      onToast({ type: 'success', text: 'Deletion cancelled. Your account is safe.' });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setDeleteBusy(false);
  }

  if (!privacy) return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;

  return (
    <div className="space-y-6">

      {/* Privacy toggles */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Eye} title="Privacy" subtitle="Control what other users can see and do with your profile" />
        <div className="space-y-0">
          {PRIVACY_OPTIONS.map(opt => (
            <SettingRow key={opt.key}
              label={
                <span className="flex items-center gap-2">
                  {opt.label}
                  {opt.notYet && (
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400 border border-gray-500/30">
                      {opt.notYet}
                    </span>
                  )}
                </span>
              }
              description={opt.desc}>
              <div className="flex items-center gap-2">
                {saving === opt.key && <Spinner size="sm" />}
                <Toggle enabled={!!privacy[opt.key]} onChange={(v) => togglePrivacy(opt.key, v)} size="sm" />
              </div>
            </SettingRow>
          ))}
        </div>
      </div>

      {/* Data export */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={HardDrive} title="Download your data"
          subtitle="Get a single JSON file with everything haythix stores about you — profile, trades, strategies, alerts, privacy settings, login history. GDPR-friendly." />
        <button onClick={downloadExport} disabled={exporting}
          className="px-4 py-2 rounded-xl bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 border border-violet-500/30 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {exporting ? <Spinner size="sm" /> : <HardDrive className="w-4 h-4" />}
          {exporting ? 'Preparing export…' : 'Download my data'}
        </button>
        <p className="text-[10px] text-gray-600 mt-3">
          File is generated on-demand and never stored. Re-run anytime to get a fresh snapshot.
        </p>
      </div>

      {/* Account deletion */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-red-500/20">
        <SectionHeader icon={AlertTriangle} title="Delete account"
          subtitle="Permanently remove your account and all associated data. 7-day grace period to change your mind." />

        {deletionStatus?.pending ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-300 font-bold">Deletion scheduled in {deletionStatus.daysLeft} day{deletionStatus.daysLeft !== 1 ? 's' : ''}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Requested {new Date(deletionStatus.requestedAt).toLocaleString()}. Will permanently delete on {new Date(deletionStatus.scheduledAt).toLocaleString()}.
                </p>
              </div>
              <button onClick={cancelDelete} disabled={deleteBusy}
                className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30 text-xs font-bold flex-shrink-0">
                Cancel deletion
              </button>
            </div>
          </div>
        ) : !deleteOpen ? (
          <button onClick={() => setDeleteOpen(true)}
            className="px-4 py-2 rounded-xl bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30 text-sm font-bold flex items-center gap-2">
            <X className="w-4 h-4" /> Request account deletion
          </button>
        ) : (
          <div className="space-y-3 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Why are you leaving? (optional)</label>
              <textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} maxLength={500} rows={2}
                placeholder="Helps us improve…"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-red-400 focus:outline-none resize-none" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                Type <span className="font-mono font-bold text-red-300">DELETE MY ACCOUNT</span> to confirm
              </label>
              <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm font-mono focus:border-red-400 focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={requestDelete} disabled={deleteBusy || deleteConfirm !== 'DELETE MY ACCOUNT'}
                className="px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-400 text-sm font-bold disabled:opacity-40 flex items-center gap-2">
                {deleteBusy ? <Spinner size="sm" /> : <X className="w-4 h-4" />}
                Request deletion
              </button>
              <button onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }} disabled={deleteBusy}
                className="px-4 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-bold border border-dark-600">
                Keep my account
              </button>
            </div>
            <p className="text-[10px] text-gray-500">
              You'll have 7 days to change your mind before any data is actually deleted. Subscriptions will be cancelled at the time of request.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Login History / Security Activity ──────────────────────────────────────
//
// Premium activity log: not just sign-ins, but the full security audit trail
// (password changes, MFA changes, session revocations, API key lifecycle,
// account deletion, privacy changes). Filter pills + KPI hero + CSV export.

const ACTIVITY_FILTERS = [
  { id: 'all',       label: 'All',                  params: {} },
  { id: 'logins',    label: 'Logins',               params: { category: 'login' } },
  { id: 'security',  label: 'Security changes',     params: { category: 'security' } },
  { id: 'failures',  label: 'Failures only',        params: { ok: false } },
  { id: 'recent',    label: 'Last 24h',             params: { sinceHours: 24 } },
];

// Color + icon mapping per event type. Falls back to neutral.
function eventVisual(ev) {
  const t = ev.type || '';
  if (t.startsWith('login.')) {
    return ev.ok === false
      ? { Icon: AlertTriangle, color: 'red',     label: 'Failed login' }
      : { Icon: LogIn,         color: 'emerald', label: 'Sign-in' };
  }
  if (t.startsWith('password.'))   return { Icon: Lock,    color: 'amber',   label: t === 'password.reset' ? 'Password reset' : 'Password changed' };
  if (t.startsWith('mfa.'))        return { Icon: Shield,  color: 'violet',  label:
    t === 'mfa.enabled'  ? 'Two-factor enabled' :
    t === 'mfa.disabled' ? 'Two-factor disabled' :
    'Backup codes regenerated' };
  if (t.startsWith('session.'))    return { Icon: Power,   color: 'rose',    label:
    t === 'session.revoked_all_others' ? 'All other sessions signed out' : 'Session revoked' };
  if (t.startsWith('api_key.'))    return { Icon: Key,     color: 'cyan',    label: t === 'api_key.created' ? 'API key created' : 'API key revoked' };
  if (t.startsWith('account.'))    return { Icon: Trash2,  color: 'orange',  label:
    t === 'account.deletion_requested' ? 'Account deletion requested' :
    t === 'account.deletion_cancelled' ? 'Account deletion cancelled' : 'Account event' };
  if (t.startsWith('privacy.'))    return { Icon: Eye,     color: 'slate',   label: 'Privacy settings changed' };
  return { Icon: Activity, color: 'slate', label: t || 'Event' };
}

// Tailwind palette per badge color (kept narrow so the cascade-based light
// theme overrides still apply via [data-theme="light"]).
const COLOR_CHIP = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300', icon: 'text-emerald-400' },
  red:     { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-300',     icon: 'text-red-400' },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-300',   icon: 'text-amber-400' },
  violet:  { bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  text: 'text-violet-300',  icon: 'text-violet-400' },
  rose:    { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-300',    icon: 'text-rose-400' },
  cyan:    { bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30',    text: 'text-cyan-300',    icon: 'text-cyan-400' },
  orange:  { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-300',  icon: 'text-orange-400' },
  slate:   { bg: 'bg-dark-700/40',    border: 'border-dark-600/50',    text: 'text-gray-300',    icon: 'text-gray-400' },
};

// Collapse consecutive failed logins from the same IP into a single grouped
// item so brute-force noise doesn't drown out the real signal.
function groupConsecutiveFailures(events) {
  const out = [];
  let i = 0;
  while (i < events.length) {
    const ev = events[i];
    const isFailedLogin = ev.type === 'login.failed';
    if (isFailedLogin) {
      const ip = ev.ip || 'unknown';
      let j = i + 1;
      while (j < events.length && events[j].type === 'login.failed' && (events[j].ip || 'unknown') === ip) j++;
      const count = j - i;
      if (count >= 2) {
        out.push({ __group: true, count, ip, first: events[j - 1], last: ev, items: events.slice(i, j) });
        i = j;
        continue;
      }
    }
    out.push(ev);
    i++;
  }
  return out;
}

function LoginHistorySection({ onToast }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);          // { events, totals }
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const fetchHistory = useCallback(async (filterId) => {
    setLoading(true);
    try {
      const params = ACTIVITY_FILTERS.find(f => f.id === filterId)?.params || {};
      const d = await api.getAccountLoginHistory(params);
      setData({ events: d?.events || [], totals: d?.totals || {} });
    } catch (err) {
      setData({ events: [], totals: {} });
      onToast({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => { fetchHistory(filter); }, [fetchHistory, filter]);

  async function downloadCsv() {
    setExporting(true);
    try {
      const blob = await api.exportLoginHistoryCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `haythix-security-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onToast({ type: 'success', text: 'CSV downloaded' });
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    } finally {
      setExporting(false);
    }
  }

  const events = data?.events || [];
  const totals = data?.totals || {};
  const grouped = useMemo(() => groupConsecutiveFailures(events), [events]);

  return (
    <div className="space-y-6">
      {/* Premium violet hero with KPIs */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-violet-300" />
              <h2 className="text-xl font-bold text-white">Security Activity</h2>
            </div>
            <p className="text-sm text-violet-200/80">
              Every sign-in, password change, MFA update, session revocation, and API key event on your account.
            </p>
          </div>
          <button
            onClick={downloadCsv}
            disabled={exporting || (totals.all || 0) === 0}
            className="px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-100 text-sm font-bold flex items-center gap-2 disabled:opacity-40 transition-colors">
            {exporting ? <Spinner size="sm" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiTile label="Total events"   value={totals.all || 0}          tone="violet" />
          <KpiTile label="Successful 7d"  value={totals.successful7d || 0} tone="emerald" />
          <KpiTile label="Failed 7d"      value={totals.failed7d || 0}     tone={(totals.failed7d || 0) > 0 ? 'red' : 'slate'} />
          <KpiTile label="Unique devices" value={totals.uniqueDevices || 0} tone="cyan" />
        </div>
      </div>

      {/* Filter pills + list */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mr-1">
            <Filter className="w-3.5 h-3.5" />
            Filter
          </div>
          {ACTIVITY_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                filter === f.id
                  ? 'bg-violet-500/20 text-violet-200 border-violet-400/50'
                  : 'bg-dark-700/40 text-gray-400 hover:text-gray-200 border-dark-600/60 hover:border-dark-500'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No events match this filter yet</p>
            <p className="text-[10px] text-gray-600 mt-1">
              Sign-ins and security changes will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
            {grouped.map((item, i) => {
              if (item.__group) {
                const groupKey = `${item.first.ts}-${item.ip}-${item.count}`;
                const open = !!expandedGroups[groupKey];
                const chip = COLOR_CHIP.red;
                return (
                  <div key={i} className={`rounded-xl border ${chip.bg} ${chip.border}`}>
                    <button
                      onClick={() => setExpandedGroups(s => ({ ...s, [groupKey]: !open }))}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <AlertTriangle className={`w-4 h-4 ${chip.icon} flex-shrink-0`} />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium truncate">
                            {item.count} failed sign-ins from {item.ip}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {timeAgoShort(item.last.ts)} → {timeAgoShort(item.first.ts)} ·{' '}
                            {item.first.userAgent || 'Unknown device'}
                          </p>
                        </div>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div className="border-t border-red-500/20 px-3 py-2 space-y-1">
                        {item.items.map((ev, k) => (
                          <ActivityRow key={k} ev={ev} compact />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return <ActivityRow key={i} ev={item} />;
            })}
          </div>
        )}

        {/* "Don't recognize this?" CTA */}
        {events.length > 0 && (
          <button
            onClick={() => navigate('/settings?tab=security&section=two-factor', { replace: false })}
            className="mt-4 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/30 text-left transition-colors">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">Don't recognize one of these?</p>
                <p className="text-[11px] text-amber-200/70 mt-0.5">
                  Change your password and enable two-factor authentication to lock things down.
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-300 flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone = 'violet' }) {
  const toneClass = {
    violet:  'text-violet-200',
    emerald: 'text-emerald-300',
    red:     'text-red-300',
    cyan:    'text-cyan-300',
    slate:   'text-gray-300',
  }[tone];
  return (
    <div className="bg-black/30 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/5">
      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}

function ActivityRow({ ev, compact = false }) {
  const { Icon, color, label } = eventVisual(ev);
  const chip = COLOR_CHIP[color] || COLOR_CHIP.slate;
  return (
    <div className={`flex items-center justify-between gap-3 px-3 ${compact ? 'py-1.5' : 'py-2.5'} rounded-xl border ${
      ev.ok === false ? 'bg-red-500/5 border-red-500/20' : 'bg-dark-700/30 border-dark-700/50'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${chip.bg} ${chip.border} border flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${chip.icon}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-white font-medium truncate">{label}</p>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${chip.bg} ${chip.text}`}>
              {ev.ok === false ? 'failed' : ev.category || 'event'}
            </span>
            {ev.reason && (
              <span className="text-[10px] text-red-300/80">· {ev.reason}</span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mt-0.5 truncate">
            {timeAgoShort(ev.ts)} · {ev.userAgent || 'Unknown device'} · IP {ev.ip || '—'}
            {ev.meta?.count != null && ` · ${ev.meta.count} sessions`}
            {ev.meta?.name && ` · "${ev.meta.name}"`}
            {ev.meta?.device && ` · ${ev.meta.device}`}
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 3: Language & Region, Appearance, Beta Features
// ═══════════════════════════════════════════════════════════════════════════

const COMMON_TIMEZONES = [
  { value: 'auto',                  label: 'Auto-detect (use browser timezone)' },
  { value: 'UTC',                   label: 'UTC' },
  { value: 'America/New_York',      label: 'New York · ET' },
  { value: 'America/Chicago',       label: 'Chicago · CT' },
  { value: 'America/Denver',        label: 'Denver · MT' },
  { value: 'America/Los_Angeles',   label: 'Los Angeles · PT' },
  { value: 'Europe/London',         label: 'London · UK' },
  { value: 'Europe/Berlin',         label: 'Berlin / Paris · CET' },
  { value: 'Europe/Istanbul',       label: 'Istanbul · TRT' },
  { value: 'Asia/Dubai',            label: 'Dubai · GST' },
  { value: 'Asia/Karachi',          label: 'Islamabad / Karachi · PKT' },
  { value: 'Asia/Kolkata',          label: 'Mumbai · IST' },
  { value: 'Asia/Singapore',        label: 'Singapore · SGT' },
  { value: 'Asia/Tokyo',            label: 'Tokyo · JST' },
  { value: 'Asia/Shanghai',         label: 'Shanghai · CST' },
  { value: 'Australia/Sydney',      label: 'Sydney · AEDT' },
];

const NUMBER_FORMATS = [
  { value: 'auto',  label: 'Auto (follows browser locale)', sample: (1234567.89).toLocaleString() },
  { value: 'en-US', label: '1,234,567.89 (US)',     sample: '1,234,567.89' },
  { value: 'en-EU', label: '1 234 567,89 (EU)',     sample: '1 234 567,89' },
  { value: 'en-IN', label: '12,34,567.89 (India)',  sample: '12,34,567.89' },
  { value: 'en-PK', label: '1,234,567.89 (Pakistan)', sample: (1234567.89).toLocaleString('en-PK') },
  { value: 'compact', label: '1.23M (compact)',     sample: '1.23M' },
];

const STORAGE_KEYS = {
  timezone:   'haythix.displayTimezone',
  number:     'haythix.numberFormat',
  density:    'haythix.density',
  theme:      'haythix.theme',
};

// Applies the chosen theme to the document. 'auto' uses prefers-color-scheme.
// Idempotent — safe to call on every page load.
export function applyTheme(theme) {
  try {
    let resolved = theme;
    if (theme === 'auto') {
      const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
      resolved = prefersDark ? 'dark' : 'light';
    }
    document.documentElement.setAttribute('data-theme', resolved);
  } catch {}
}

function LanguageRegionSection({ onToast }) {
  const { currency, setCurrency, supportedCurrencies, loading: fxLoading, refreshRates } = useCurrency();

  const browserTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
  }, []);

  const [tz, setTz] = useState(() => localStorage.getItem(STORAGE_KEYS.timezone) || 'auto');
  const [numFmt, setNumFmt] = useState(() => localStorage.getItem(STORAGE_KEYS.number) || 'auto');

  function saveTz(v) {
    setTz(v);
    try {
      localStorage.setItem(STORAGE_KEYS.timezone, v);
      // Live-broadcast so useFormatters() in every mounted page picks it up.
      window.dispatchEvent(new CustomEvent('haythix:timezone', { detail: v }));
    } catch {}
    onToast({ type: 'success', text: 'Timezone preference saved' });
  }

  function saveNumFmt(v) {
    setNumFmt(v);
    try {
      localStorage.setItem(STORAGE_KEYS.number, v);
      window.dispatchEvent(new CustomEvent('haythix:number-format', { detail: v }));
    } catch {}
    onToast({ type: 'success', text: 'Number format saved' });
  }

  // Live preview of a sample amount in chosen currency/format
  const sampleAmount = 12345.67;
  const fmt = NUMBER_FORMATS.find(n => n.value === numFmt) || NUMBER_FORMATS[0];

  return (
    <div className="space-y-6">

      {/* Currency */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
          <SectionHeader icon={Globe} title="Display Currency"
            subtitle="Converts USD values across the platform. FX rates refresh hourly." />
          <button onClick={refreshRates} disabled={fxLoading}
            className="text-[11px] font-semibold text-violet-300 hover:text-violet-200 flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${fxLoading ? 'animate-spin' : ''}`} />
            Refresh rates
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {supportedCurrencies.map(c => {
            const isActive = currency === c;
            return (
              <button key={c} onClick={() => { setCurrency(c); onToast({ type: 'success', text: `Currency set to ${c}` }); }}
                className={`px-3 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                  isActive
                    ? 'bg-violet-500/15 text-violet-200 border-violet-500/40'
                    : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
                }`}>
                {c}
              </button>
            );
          })}
        </div>
        <div className="mt-4 bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Sample (10 USD)</p>
          <p className="text-lg font-bold text-white">
            {(() => {
              const symbols = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', CNY: '¥', BRL: 'R$', AUD: 'A$', CAD: 'C$', CHF: 'CHF', KRW: '₩', MXN: 'MX$', SGD: 'S$', HKD: 'HK$', TRY: '₺', ZAR: 'R' };
              return `${symbols[currency] || currency + ' '}10.00`;
            })()}
          </p>
        </div>
      </div>

      {/* Timezone */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Clock} title="Timezone"
          subtitle={`Browser-detected: ${browserTz}`} />
        <select value={tz} onChange={e => saveTz(e.target.value)}
          className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:border-violet-400 focus:outline-none">
          {COMMON_TIMEZONES.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
        </select>
        <p className="text-[10px] text-gray-600 mt-2">
          Affects how trade times, alert timestamps, and chart annotations are displayed.
          Cloud-side operations (trading windows, schedules) always run in UTC.
        </p>
      </div>

      {/* Number format */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={BarChart3} title="Number Format"
          subtitle="How large numbers (volumes, P&L, balances) are displayed." />
        <div className="space-y-2">
          {NUMBER_FORMATS.map(opt => (
            <button key={opt.value} onClick={() => saveNumFmt(opt.value)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                numFmt === opt.value
                  ? 'bg-violet-500/10 text-violet-200 border-violet-500/30'
                  : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
              }`}>
              <span className="text-sm font-medium">{opt.label}</span>
              <span className="text-sm font-mono text-gray-500">{opt.sample}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Sample (12,345.67)</p>
          <p className="text-lg font-mono font-bold text-white">{fmt.sample}</p>
        </div>
        <p className="text-[10px] text-gray-600 mt-2">
          Applies live across the platform. Pages that still show fixed formats
          will pick this up on their next visit as we keep migrating them to
          <code className="bg-dark-700 px-1 rounded ml-1">useFormatters()</code>.
        </p>
      </div>
    </div>
  );
}

function AppearanceSection({ onToast }) {
  const [density, setDensity] = useState(() => localStorage.getItem(STORAGE_KEYS.density) || 'comfortable');
  const [theme, setTheme] = useState(() => localStorage.getItem(STORAGE_KEYS.theme) || 'dark');

  function saveTheme(v) {
    setTheme(v);
    try {
      localStorage.setItem(STORAGE_KEYS.theme, v);
      applyTheme(v);
    } catch {}
    onToast({ type: 'success', text: `Theme set to ${v}` });
  }

  function saveDensity(v) {
    setDensity(v);
    try {
      localStorage.setItem(STORAGE_KEYS.density, v);
      document.documentElement.setAttribute('data-density', v);
      window.dispatchEvent(new CustomEvent('haythix:density-change', { detail: v }));
    } catch {}
    onToast({ type: 'success', text: `Density set to ${v}` });
  }

  // Detect what 'auto' currently resolves to so we can show users what
  // picking it would actually do right now.
  const systemPrefersDark = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  const systemResolved = systemPrefersDark ? 'Dark' : 'Light';

  const themes = [
    { value: 'dark',  label: 'Dark',         icon: '🌙', desc: 'Default — easier on the eyes during long trading sessions' },
    { value: 'light', label: 'Light',        icon: '☀️', desc: 'Soft off-white palette — better in well-lit rooms or daytime use' },
    { value: 'auto',  label: 'Match System', icon: '🌗',
      desc: `Follows your computer's dark / light mode. If your OS toggles theme during the day (most do), haythix will follow along. Right now your system is in ${systemResolved} mode.` },
  ];

  return (
    <div className="space-y-6">

      {/* Theme */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Eye} title="Theme"
          subtitle="Choose how the platform looks. Applies immediately." />
        <div className="grid grid-cols-3 gap-2">
          {themes.map(t => {
            const isActive = theme === t.value;
            return (
              <button key={t.value} onClick={() => saveTheme(t.value)}
                className={`relative rounded-xl p-4 text-center border transition-all ${
                  isActive
                    ? 'bg-violet-500/10 border-violet-500/40 ring-2 ring-violet-500/50'
                    : 'bg-dark-700 border-dark-600 hover:border-dark-500'
                }`}>
                <div className="text-2xl mb-1">{t.icon}</div>
                <p className={`text-sm font-bold ${isActive ? 'text-violet-200' : 'text-gray-300'}`}>{t.label}</p>
                {isActive && <p className="text-[9px] text-violet-300 mt-1 font-bold uppercase">Active</p>}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          {themes.find(t => t.value === theme)?.desc}
        </p>
        {theme === 'light' && (
          <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
            <p className="text-[11px] text-amber-300">
              <strong>Heads up:</strong> light mode is freshly shipped via a CSS-cascade overlay.
              Most surfaces are clean, but you may notice rough edges on a few colored gradients —
              we'll polish them per-page over the coming weeks.
            </p>
          </div>
        )}
      </div>

      {/* Density */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <SectionHeader icon={Sliders} title="Density"
          subtitle="Trade off whitespace vs information density. Persists locally." />
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'comfortable', label: 'Comfortable', desc: 'Default — easier to scan, more breathing room' },
            { value: 'compact',     label: 'Compact',     desc: 'Pro — more rows per screen, tighter padding' },
          ].map(opt => (
            <button key={opt.value} onClick={() => saveDensity(opt.value)}
              className={`text-left p-4 rounded-xl border transition-all ${
                density === opt.value
                  ? 'bg-violet-500/10 text-violet-200 border-violet-500/30'
                  : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
              }`}>
              <p className="text-sm font-bold">{opt.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          Density preference is stored in <code className="bg-dark-700 px-1 rounded">data-density</code> on the root element.
          Pages that opt in to compact mode tighten their spacing accordingly.
        </p>
      </div>
    </div>
  );
}

// ─── Beta Features ──────────────────────────────────────────────────────────
//
// Premium rebuild that closes the legal-risk gap (acknowledging without
// shown text), the loading bug (wrong response key), and the property
// mismatch (wrong field names from registry).

const BADGE_COLOR_MAP = {
  amber:   'bg-amber-500/15 text-amber-300 border-amber-500/40',
  violet:  'bg-violet-500/15 text-violet-300 border-violet-500/40',
  red:     'bg-red-500/15 text-red-300 border-red-500/40',
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  cyan:    'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',
};

const TIER_RANK_BETA = { trial: 0, starter: 1, trader: 2, pro: 3, elite: 4, admin: 5 };

function BetaFeaturesSection({ onToast }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [features, setFeatures]   = useState(null);
  const [acks, setAcks]           = useState({});
  const [feedback, setFeedback]   = useState({});
  const [filter, setFilter]       = useState('all');
  const [disclaimerFor, setDisclaimerFor] = useState(null);  // feature being read
  const [feedbackFor, setFeedbackFor]     = useState(null);  // feature being rated

  const loadAll = useCallback(async () => {
    const [f, a, fb] = await Promise.all([
      api.getBetaFeatures().catch(() => ({ features: [] })),
      api.getMyAcknowledgments().catch(() => ({ acknowledgments: {} })),
      api.getMyBetaFeedback().catch(() => ({ feedback: {} })),
    ]);
    setFeatures(f?.features || []);
    // FIX bug 1: backend returns { acknowledgments: {id: true/false} } not { acks }
    setAcks(a?.acknowledgments || {});
    setFeedback(fb?.feedback || {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleAcknowledge(featureId) {
    try {
      await api.acknowledgeBetaFeature(featureId);
      onToast({ type: 'success', text: 'Acknowledged — you can now use this feature' });
      setDisclaimerFor(null);
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  async function handleFeedback(featureId, rating, comment) {
    try {
      await api.submitBetaFeedback(featureId, rating, comment);
      onToast({ type: 'success', text: 'Feedback submitted — thank you' });
      setFeedbackFor(null);
      loadAll();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  if (features === null) return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;

  const total = features.length;
  // FIX bug 3: acks object is keyed by registry id (lowercase) with boolean
  const ackedCount = features.filter(f => acks[f.id] === true).length;
  const feedbackCount = features.filter(f => !!feedback[f.id]).length;
  const pendingCount = total - ackedCount;

  // Apply filter
  let visible = features;
  if (filter === 'pending')      visible = features.filter(f => !acks[f.id]);
  if (filter === 'acknowledged') visible = features.filter(f => !!acks[f.id]);

  const myTierRank = TIER_RANK_BETA[user?.tier] ?? 0;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">

      {/* Premium violet hero */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-fuchsia-600/10 to-indigo-700/15 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <BadgeCheck className="w-5 h-5 text-violet-300" />
            <h2 className="text-xl font-bold text-white">Beta Features</h2>
          </div>
          <p className="text-sm text-violet-200/80 mb-5">
            Pre-release features that need your acknowledgment before use. Review the disclaimer, then rate them — your feedback drives the roadmap.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Total beta"        value={total}         tone="violet" />
            <KpiTile label="Pending"           value={pendingCount}  tone={pendingCount > 0 ? 'amber' : 'slate'} />
            <KpiTile label="Acknowledged"      value={ackedCount}    tone="emerald" />
            <KpiTile label="Feedback given"    value={feedbackCount} tone="cyan" />
          </div>
        </div>
      </div>

      {/* Filter pills */}
      {total > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-gray-500" />
          {[
            { id: 'all', label: `All (${total})` },
            { id: 'pending', label: `Pending (${pendingCount})` },
            { id: 'acknowledged', label: `Acknowledged (${ackedCount})` },
          ].map(p => (
            <button key={p.id} onClick={() => setFilter(p.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
                filter === p.id
                  ? 'bg-violet-500/20 text-violet-200 border-violet-400/50'
                  : 'bg-dark-700/40 text-gray-400 hover:text-gray-200 border-dark-600/60'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Feature list */}
      {total === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <BadgeCheck className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No beta features active</p>
          <p className="text-[10px] text-gray-600 mt-1">When new features enter beta, they'll appear here for you to review and acknowledge.</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-8 border border-dark-700/50 text-center">
          <p className="text-sm text-gray-400">No features in this filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(f => {
            const isAcked = acks[f.id] === true;
            const fb = feedback[f.id];
            const reqRank = f.requiresTier ? (TIER_RANK_BETA[f.requiresTier] ?? 99) : 0;
            const tierLocked = !isAdmin && reqRank > myTierRank;
            const badgeClass = BADGE_COLOR_MAP[f.badgeColor] || BADGE_COLOR_MAP.amber;

            return (
              <div key={f.id} className={`bg-dark-800 rounded-2xl border p-5 ${isAcked ? 'border-emerald-500/20' : 'border-dark-700/50'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isAcked ? 'bg-emerald-500/15 text-emerald-300' : 'bg-violet-500/15 text-violet-300'
                  }`}>
                    {isAcked ? <Check className="w-5 h-5" /> : <BadgeCheck className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="text-sm font-bold text-white">{f.name || f.id}</h4>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${badgeClass}`}>{f.badge || 'BETA'}</span>
                      {isAcked && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Acknowledged</span>}
                      {tierLocked && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Requires {f.requiresTier?.toUpperCase()}</span>}
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">{f.description}</p>

                    {/* Where to find it */}
                    {Array.isArray(f.pages) && f.pages.length > 0 && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Find at:</span>
                        {f.pages.slice(0, 3).map(p => (
                          <button key={p} onClick={() => navigate(p)}
                            className="text-[10px] font-mono text-violet-300 hover:text-violet-200 underline">
                            {p}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Feedback display if submitted */}
                    {fb && (
                      <div className="mt-3 text-[11px] text-gray-500 flex items-center gap-1.5">
                        <span className="text-amber-300">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                        <span>·</span>
                        <span>Submitted {timeAgoShort(fb.submittedAt)}</span>
                        {fb.comment && <span className="italic truncate">— "{fb.comment.slice(0, 60)}{fb.comment.length > 60 ? '…' : ''}"</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons row */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {!isAcked && (
                    <button onClick={() => setDisclaimerFor(f)}
                      disabled={tierLocked}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-40 flex items-center gap-1.5">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      Read &amp; acknowledge
                    </button>
                  )}
                  {isAcked && f.feedbackEnabled !== false && (
                    <button onClick={() => setFeedbackFor(f)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-dark-700 hover:bg-dark-600 text-gray-300 border border-dark-600 flex items-center gap-1.5">
                      ★ {fb ? 'Edit feedback' : 'Submit feedback'}
                    </button>
                  )}
                  {f.helpArticleId && (
                    <button onClick={() => navigate(`/help-center?article=${f.helpArticleId}`)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-dark-700 hover:bg-dark-600 text-gray-300 border border-dark-600 flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Read full guide
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer modal */}
      {disclaimerFor && (
        <BetaDisclaimerModal
          feature={disclaimerFor}
          onAcknowledge={() => handleAcknowledge(disclaimerFor.id)}
          onCancel={() => setDisclaimerFor(null)}
        />
      )}

      {/* Feedback modal */}
      {feedbackFor && (
        <BetaFeedbackModal
          feature={feedbackFor}
          existing={feedback[feedbackFor.id]}
          onSubmit={(rating, comment) => handleFeedback(feedbackFor.id, rating, comment)}
          onCancel={() => setFeedbackFor(null)}
        />
      )}
    </div>
  );
}

function BetaDisclaimerModal({ feature, onAcknowledge, onCancel }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [iUnderstand, setIUnderstand]           = useState(false);
  const scrollRef = useRef(null);
  const fullText = feature.disclaimer?.full || feature.disclaimer?.short || 'No additional disclaimer provided.';

  // If the disclaimer text fits without scrolling, the onScroll event will
  // never fire and the user would be locked out. Detect that on mount and
  // immediately satisfy the gate. Same applies if the window resizes.
  useEffect(() => {
    function check() {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 1) setScrolledToBottom(true);
    }
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [fullText]);

  function onScroll(e) {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolledToBottom(true);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-dark-800 rounded-2xl border border-violet-500/30 max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-dark-700">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-violet-300" />
            <h3 className="text-lg font-bold text-white">{feature.name}</h3>
          </div>
          <p className="text-xs text-gray-400">Read the full disclaimer below — scroll to the bottom before you can acknowledge.</p>
        </div>
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-6">
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{fullText}</p>
        </div>
        <div className="p-6 border-t border-dark-700 space-y-3">
          <label className={`flex items-start gap-2.5 text-xs cursor-pointer ${!scrolledToBottom ? 'opacity-40' : ''}`}>
            <input type="checkbox" disabled={!scrolledToBottom}
              checked={iUnderstand} onChange={e => setIUnderstand(e.target.checked)}
              className="mt-0.5 flex-shrink-0 w-4 h-4 rounded border-dark-600" />
            <span className="text-gray-300 leading-relaxed">
              I have read the full disclaimer above. I understand this is a <span className="font-semibold text-violet-300">beta feature</span> and that I am responsible for my own trading decisions.
            </span>
          </label>
          <div className="flex gap-2">
            <button onClick={onAcknowledge} disabled={!scrolledToBottom || !iUnderstand}
              className="flex-1 px-4 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold disabled:opacity-40">
              I acknowledge
            </button>
            <button onClick={onCancel} className="px-4 py-2.5 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-semibold border border-dark-600">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BetaFeedbackModal({ feature, existing, onSubmit, onCancel }) {
  const [rating, setRating]   = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || '');
  const [hover, setHover]     = useState(0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-dark-800 rounded-2xl border border-violet-500/30 max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-1">
          <BadgeCheck className="w-5 h-5 text-violet-300" />
          <h3 className="text-lg font-bold text-white">Feedback: {feature.name}</h3>
        </div>
        <p className="text-xs text-gray-400 mb-5">Your rating + comments go directly to the team building this feature.</p>

        <div className="flex items-center gap-2 mb-5">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              className={`text-3xl transition-transform hover:scale-110 ${
                (hover || rating) >= n ? 'text-amber-400' : 'text-gray-600'
              }`}>
              ★
            </button>
          ))}
          {rating > 0 && <span className="text-xs text-gray-400 ml-2">{rating} of 5</span>}
        </div>

        <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold block mb-1.5">Comment (optional)</label>
        <textarea value={comment} onChange={e => setComment(e.target.value)}
          rows={4} maxLength={1000}
          placeholder="What works well? What's confusing? Bugs?"
          className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-violet-500 focus:outline-none resize-none" />
        <p className="text-[10px] text-gray-600 mt-1 text-right">{comment.length} / 1000</p>

        <div className="flex gap-2 mt-4">
          <button onClick={() => onSubmit(rating, comment)} disabled={rating === 0}
            className="flex-1 px-4 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold disabled:opacity-40">
            Submit feedback
          </button>
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-semibold border border-dark-600">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 4: API Keys
// ═══════════════════════════════════════════════════════════════════════════

const SCOPE_DEFS = [
  { id: 'read',  label: 'Read',  desc: 'View account data, balances, portfolio, alerts, trade history',  color: 'cyan',   icon: Eye },
  { id: 'trade', label: 'Trade', desc: 'Place and cancel orders, execute Smart Trades, manage bots',     color: 'amber',  icon: Zap },
];

const EXPIRY_DEFS = [
  { value: 7,    label: '7 days',   desc: 'Short-lived — best for one-off scripts' },
  { value: 30,   label: '30 days',  desc: 'Common for personal tooling' },
  { value: 90,   label: '90 days',  desc: 'Quarterly rotation pattern' },
  { value: 365,  label: '1 year',   desc: 'Long-lived production keys' },
  { value: null, label: 'Never',    desc: "No expiry — revoke manually when you're done" },
];

// Rotating placeholder examples to guide naming
const NAME_EXAMPLES = [
  'e.g. Personal trading bot',
  'e.g. Portfolio sync (TradingView)',
  'e.g. Macbook Pro — local scripts',
  'e.g. Daily P&L report generator',
  'e.g. Zapier integration',
  'e.g. Read-only dashboard widget',
];

function ApiKeysSection({ onToast }) {
  const [keys, setKeys] = useState(null);
  const [max, setMax] = useState(10);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState(['read']);
  const [newExpiry, setNewExpiry] = useState(90); // sensible default
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  // Rotate the placeholder example every 4s to give users naming ideas.
  const [examplePlaceholder, setExamplePlaceholder] = useState(NAME_EXAMPLES[0]);
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % NAME_EXAMPLES.length;
      setExamplePlaceholder(NAME_EXAMPLES[i]);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  function reload() {
    api.listApiKeys()
      .then(d => { setKeys(d?.keys || []); if (d?.max) setMax(d.max); })
      .catch(err => { setKeys([]); onToast({ type: 'error', text: err.message }); });
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleScope(s) {
    setNewScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  async function createKey() {
    if (!newName.trim() || newScopes.length === 0) return;
    setCreating(true);
    try {
      const result = await api.createApiKey(newName.trim(), newScopes, newExpiry);
      setJustCreated(result);
      setTestResult(null);
      setShowCreate(false);
      setNewName('');
      setNewScopes(['read']);
      setNewExpiry(90);
      reload();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
    setCreating(false);
  }

  async function revokeKey(id, name) {
    if (!confirm(`Revoke "${name}"? Any script using this key will immediately stop working.`)) return;
    try {
      await api.revokeApiKey(id);
      onToast({ type: 'success', text: 'Key revoked' });
      reload();
    } catch (err) {
      onToast({ type: 'error', text: err.message });
    }
  }

  function copyKey() {
    if (!justCreated?.key) return;
    navigator.clipboard.writeText(justCreated.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function testJustCreatedKey() {
    if (!justCreated?.key) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.testApiKey(justCreated.key);
      setTestResult({ ok: true, msg: r?.message || 'Key works.' });
    } catch (err) {
      setTestResult({ ok: false, msg: err.message || 'Test failed' });
    }
    setTesting(false);
  }

  // Days until a key expires (or null if it never expires / already expired).
  function daysToExpiry(expiresAt) {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 86400000);
  }

  if (keys === null) return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Hero strip */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">API Keys</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {keys.length} of {max} keys · Use to authenticate scripts and third-party tools via the <code className="bg-dark-700 px-1 rounded">X-API-Key</code> header.
              </p>
            </div>
          </div>
          {keys.length < max && !showCreate && !justCreated && (
            <button onClick={() => setShowCreate(true)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white text-sm font-bold flex items-center gap-2 shadow-lg shadow-violet-500/20">
              <Plus className="w-4 h-4" /> Generate New Key
            </button>
          )}
        </div>
      </div>

      {/* Just-created key (show-once) — now with inline "Test this key" */}
      {justCreated && (
        <div className="bg-emerald-500/5 border-2 border-emerald-500/30 rounded-2xl p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-300">Your new key — copy it now</p>
              <p className="text-[11px] text-gray-400 mt-1">
                You won't be able to see it again. Save it to a password manager or encrypted notes.
              </p>
            </div>
          </div>
          <div className="bg-dark-900 rounded-xl border border-emerald-500/30 p-3 flex items-center gap-3">
            <code className="flex-1 text-xs font-mono text-emerald-300 break-all">{justCreated.key}</code>
            <button onClick={copyKey}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30 flex items-center gap-1 flex-shrink-0">
              <Copy className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* Test button — hits /account/api-keys/test with the raw key so the
              user can verify the key actually works before they leave the page. */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={testJustCreatedKey} disabled={testing}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 border border-violet-500/30 flex items-center gap-1.5 disabled:opacity-50">
              {testing ? <Spinner size="sm" /> : <Zap className="w-3.5 h-3.5" />}
              {testing ? 'Testing…' : 'Test this key'}
            </button>
            {testResult && (
              <span className={`text-[11px] font-bold inline-flex items-center gap-1 ${
                testResult.ok ? 'text-emerald-300' : 'text-red-400'
              }`}>
                {testResult.ok
                  ? <><Check className="w-3 h-3" /> {testResult.msg}</>
                  : <><X className="w-3 h-3" /> {testResult.msg}</>}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-[10px] text-gray-500 flex-wrap">
            <span>Name: <span className="text-white font-bold">{justCreated.name}</span></span>
            <span>·</span>
            <span>Scopes:</span>
            {justCreated.scopes.map(s => {
              const def = SCOPE_DEFS.find(x => x.id === s);
              const color = def?.color === 'amber' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30';
              return <span key={s} className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border ${color}`}>{s}</span>;
            })}
            <span>·</span>
            <span>Expires: <span className="text-white font-bold">
              {justCreated.expiresAt ? new Date(justCreated.expiresAt).toLocaleDateString() : 'Never'}
            </span></span>
          </div>
          <button onClick={() => { setJustCreated(null); setTestResult(null); }}
            className="text-xs font-bold text-violet-300 hover:text-violet-200">
            I've saved it — close this →
          </button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="bg-dark-800 rounded-2xl p-5 border border-violet-500/30">
          <SectionHeader icon={Plus} title="Generate New API Key" subtitle="Choose a name you'll recognize and the scopes the key should have." />
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                Key Name <span className="text-gray-600">— what will this key be used for?</span>
              </label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder={examplePlaceholder}
                maxLength={80}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-violet-400 focus:outline-none" />
              <p className="text-[10px] text-gray-600 mt-1">Pick something you'll recognize 6 months from now.</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-2">Scopes</label>
              <div className="space-y-2">
                {SCOPE_DEFS.map(s => {
                  const checked = newScopes.includes(s.id);
                  const Icon = s.icon;
                  const activeBg = s.color === 'amber'
                    ? 'bg-amber-500/10 text-amber-200 border-amber-500/30'
                    : 'bg-cyan-500/10 text-cyan-200 border-cyan-500/30';
                  const iconColor = s.color === 'amber' ? 'text-amber-300' : 'text-cyan-300';
                  return (
                    <button key={s.id} type="button" onClick={() => toggleScope(s.id)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        checked ? activeBg : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
                      }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 rounded mt-0.5 border ${
                          checked
                            ? (s.color === 'amber' ? 'bg-amber-500 border-amber-500' : 'bg-cyan-500 border-cyan-500')
                            : 'border-dark-500'
                        } flex items-center justify-center flex-shrink-0`}>
                          {checked && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${checked ? iconColor : 'text-gray-500'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-bold uppercase tracking-wider">{s.label}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5">{s.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Expiry */}
            <div>
              <label className="text-xs text-gray-400 block mb-2">Expires</label>
              <div className="grid grid-cols-5 gap-1.5">
                {EXPIRY_DEFS.map(opt => {
                  const isActive = newExpiry === opt.value;
                  return (
                    <button key={String(opt.value)} type="button"
                      onClick={() => setNewExpiry(opt.value)}
                      className={`px-2 py-2 rounded-lg text-xs font-bold transition-all border ${
                        isActive
                          ? 'bg-violet-500/15 text-violet-200 border-violet-500/40'
                          : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
                      }`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-600 mt-1.5">
                {EXPIRY_DEFS.find(o => o.value === newExpiry)?.desc}
              </p>
            </div>

            <div className="flex gap-2">
              <button onClick={createKey} disabled={creating || !newName.trim() || newScopes.length === 0}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-bold disabled:opacity-40 flex items-center gap-2">
                {creating ? <Spinner size="sm" /> : <Key className="w-4 h-4" />}
                Generate key
              </button>
              <button onClick={() => { setShowCreate(false); setNewName(''); setNewScopes(['read']); setNewExpiry(90); }}
                className="px-4 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-bold border border-dark-600">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing keys */}
      {keys.length === 0 && !showCreate ? (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <Key className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-white font-bold mb-1">No API keys yet</h3>
          <p className="text-sm text-gray-500 mb-4">Generate a key to use haythix from scripts, bots, or third-party tools.</p>
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Generate first key
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map(k => {
            const days = daysToExpiry(k.expiresAt);
            const expirySoon = days !== null && days <= 7 && days > 0;
            const expired = k.expired || (days === 0);
            return (
              <div key={k.id}
                className={`bg-dark-800 rounded-2xl border p-4 ${
                  expired ? 'border-red-500/30' : expirySoon ? 'border-amber-500/30' : 'border-dark-700/50'
                }`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="w-9 h-9 rounded-xl bg-violet-500/15 text-violet-300 flex items-center justify-center flex-shrink-0">
                    <Key className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-white truncate">{k.name}</p>
                      <code className="text-[10px] font-mono text-gray-500 bg-dark-700 px-1.5 py-0.5 rounded">{k.preview}</code>
                      {expired && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">
                          EXPIRED
                        </span>
                      )}
                      {expirySoon && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          Expires in {days}d
                        </span>
                      )}
                    </div>
                    {/* Scope badges (now visually distinct) */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {(k.scopes || []).map(s => {
                        const def = SCOPE_DEFS.find(x => x.id === s);
                        const color = def?.color === 'amber'
                          ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                          : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30';
                        const Icon = def?.icon || Key;
                        return (
                          <span key={s} className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${color}`}>
                            <Icon className="w-3 h-3" /> {s}
                          </span>
                        );
                      })}
                    </div>
                    {/* Metadata row */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px] text-gray-500">
                      <span>Created {new Date(k.createdAt).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>
                        {k.expiresAt
                          ? <>Expires {new Date(k.expiresAt).toLocaleDateString()}</>
                          : <span className="text-gray-400">Never expires</span>}
                      </span>
                      <span>·</span>
                      {k.lastUsedAt ? (
                        <>
                          <span className="text-emerald-400">Last used {timeAgoShort(k.lastUsedAt)}</span>
                          {k.lastUsedIp && <span className="text-gray-600">from {k.lastUsedIp}</span>}
                        </>
                      ) : (
                        <span className="text-gray-600">Never used</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => revokeKey(k.id, k.name)}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30 flex-shrink-0 flex items-center gap-1">
                    <X className="w-3 h-3" /> Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expandable usage docs — curl, Python, Node */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
        <button onClick={() => setShowDocs(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-dark-700/30 transition-colors">
          <div className="flex items-center gap-2.5">
            <Terminal className="w-4 h-4 text-violet-300" />
            <span className="text-sm font-bold text-white">How to use your API key</span>
          </div>
          <span className="text-xs text-gray-500">{showDocs ? '▾ Hide examples' : '▸ Show examples'}</span>
        </button>
        {showDocs && (
          <div className="px-5 pb-5 space-y-4">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">curl</p>
              <pre className="bg-dark-900 rounded-xl p-3 text-xs text-emerald-300 font-mono overflow-x-auto border border-dark-700/50">
{`curl https://api.haythix.com/api/account/api-keys/test \\
  -H "X-API-Key: hyx_..."`}
              </pre>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Python (requests)</p>
              <pre className="bg-dark-900 rounded-xl p-3 text-xs text-cyan-300 font-mono overflow-x-auto border border-dark-700/50">
{`import requests
r = requests.get(
  "https://api.haythix.com/api/account/export",
  headers={"X-API-Key": "hyx_..."}
)
data = r.json()`}
              </pre>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Node.js (fetch)</p>
              <pre className="bg-dark-900 rounded-xl p-3 text-xs text-amber-300 font-mono overflow-x-auto border border-dark-700/50">
{`const res = await fetch(
  "https://api.haythix.com/api/account/export",
  { method: "POST", headers: { "X-API-Key": "hyx_..." } }
);
const data = await res.json();`}
              </pre>
            </div>
            <div className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30 space-y-2">
              <p className="text-[11px] font-bold text-white">Endpoints that accept API key auth</p>
              <ul className="text-[10px] text-gray-400 space-y-0.5 list-disc list-inside">
                <li><code className="text-emerald-300">GET /account/api-keys/test</code> — confirm a key works (open to all scopes)</li>
                <li><code className="text-cyan-300">GET /account/privacy</code> — your privacy settings (needs <code>read</code>)</li>
                <li><code className="text-cyan-300">GET /account/login-history</code> — last 50 sign-in events (needs <code>read</code>)</li>
                <li><code className="text-cyan-300">POST /account/export</code> — download your full data dump (needs <code>read</code>)</li>
              </ul>
              <p className="text-[10px] text-gray-600">More endpoints will accept API keys over time — every read endpoint will eventually be scriptable.</p>
            </div>
            <p className="text-[10px] text-gray-500">
              Treat keys like passwords. Anyone with the raw key can act as you (within the scopes you granted).
              Revoke instantly above if a key is ever exposed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgoShort(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function SessionsPlaceholderSection() {
  // Renamed from the old placeholder — now a real ActiveSessionsSection.
  return <ActiveSessionsSection />;
}

function ActiveSessionsSection() {
  const [sessions, setSessions] = useState(null);
  const [revoking, setRevoking] = useState(null);     // jti being revoked
  const [revokingAll, setRevokingAll] = useState(false);
  const [toast, setToast] = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  function load() {
    api.listActiveSessions()
      .then(d => setSessions(d?.sessions || []))
      .catch(() => setSessions([]));
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRevoke(jti, isCurrent, label) {
    const confirmMsg = isCurrent
      ? `Sign out THIS device? You'll be sent back to the login screen.`
      : `Sign out "${label}"? Any open haythix tab on that device will need to sign in again.`;
    if (!confirm(confirmMsg)) return;
    setRevoking(jti);
    try {
      const r = await api.revokeSession(jti);
      showToast(isCurrent ? 'Signed out — redirecting…' : 'Session revoked');
      if (r?.wasCurrent || isCurrent) {
        // Wipe local token + bounce to login
        try { localStorage.removeItem('authToken'); } catch {}
        setTimeout(() => { window.location.href = '/login'; }, 600);
        return;
      }
      load();
    } catch (err) {
      showToast(err.message || 'Could not revoke', 'error');
    }
    setRevoking(null);
  }

  async function handleRevokeAll() {
    if (!confirm("Sign out every other device? You'll stay signed in here.")) return;
    setRevokingAll(true);
    try {
      const r = await api.revokeAllOtherSessions();
      const n = r?.revoked ?? 0;
      showToast(n > 0 ? `Signed out ${n} other device${n === 1 ? '' : 's'}` : 'No other devices to sign out');
      load();
    } catch (err) {
      showToast(err.message || 'Could not revoke', 'error');
    }
    setRevokingAll(false);
  }

  if (sessions === null) {
    return <div className="flex justify-center py-12"><Spinner className="text-violet-400" /></div>;
  }

  const currentCount = sessions.filter(s => s.isCurrent).length;
  const otherCount = sessions.length - currentCount;

  return (
    <div className="space-y-6">
      {/* Hero strip */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Active sessions</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {sessions.length} device{sessions.length === 1 ? '' : 's'} signed in
                {otherCount > 0 && <> · {otherCount} other than this one</>}
              </p>
            </div>
          </div>
          {otherCount > 0 && (
            <button onClick={handleRevokeAll} disabled={revokingAll}
              className="px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm font-bold border border-red-500/30 flex items-center gap-2 disabled:opacity-50">
              {revokingAll ? <Spinner size="sm" /> : <Power className="w-4 h-4" />}
              Sign out all other devices
            </button>
          )}
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <Activity className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-white font-bold mb-1">No tracked sessions yet</h3>
          <p className="text-sm text-gray-500">
            Session tracking started after the latest update. Sign out and back in once and your sessions will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => {
            const isStale = (s.inactiveDays ?? 0) > 7;
            return (
              <div key={s.jti}
                className={`bg-dark-800 rounded-2xl border p-4 ${
                  s.isCurrent ? 'border-emerald-500/30' : isStale ? 'border-amber-500/20' : 'border-dark-700/50'
                }`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    s.isCurrent ? 'bg-emerald-500/15 text-emerald-300' : 'bg-violet-500/15 text-violet-300'
                  }`}>
                    {s.userAgent?.includes('iOS') || s.userAgent?.includes('Android')
                      ? <MessageSquare className="w-5 h-5" />
                      : <Activity className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-white truncate">{s.userAgent || 'Unknown device'}</p>
                      {s.isCurrent && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                          <Check className="w-2.5 h-2.5" /> THIS DEVICE
                        </span>
                      )}
                      {isStale && !s.isCurrent && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          Inactive {s.inactiveDays}d
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px] text-gray-500">
                      <span>Signed in {new Date(s.createdAt).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>Last seen {s.lastSeenAt ? timeAgoShort(s.lastSeenAt) : '—'}</span>
                      {s.lastSeenIp && (
                        <>
                          <span>·</span>
                          <span className="text-gray-600">from {s.lastSeenIp}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleRevoke(s.jti, s.isCurrent, s.userAgent)} disabled={revoking === s.jti}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 flex items-center gap-1 disabled:opacity-50 ${
                      s.isCurrent
                        ? 'bg-dark-700 text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-dark-600'
                        : 'bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30'
                    }`}>
                    {revoking === s.jti ? <Spinner size="sm" /> : <Power className="w-3 h-3" />}
                    {s.isCurrent ? 'Sign out' : 'Revoke'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium border ${
          toast.type === 'error'
            ? 'bg-red-500/15 text-red-300 border-red-500/30'
            : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Help blurb */}
      <div className="bg-dark-800 rounded-2xl p-5 border border-dark-700/50">
        <p className="text-xs text-gray-400">
          <strong className="text-white">See a session you don't recognize?</strong> Revoke it immediately, then
          change your password in Two-Factor & Password. Consider enabling MFA if you haven't already.
        </p>
        <p className="text-[10px] text-gray-600 mt-2">
          Sessions are tracked from the moment Active Sessions shipped. Tokens issued earlier won't appear here
          and will expire naturally within 30 days. "Sign out all other devices" only affects tracked sessions.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Page — premium 2-column shell with grouped left-rail navigation.
// Replaces the previous single-row horizontal tab strip. Sections are
// grouped into 6 domains, admin sections are hidden for non-admins.
// ═══════════════════════════════════════════════════════════════════════════

// Each section carries `keywords` for the fuzzy search. Searching for
// "telegram" should land you on Notifications even though "telegram"
// isn't in the label. Add aliases users might actually type.
const GROUPS = [
  {
    id: 'account', label: 'Account', icon: UserIcon,
    sections: [
      { id: 'profile',     label: 'Profile',            icon: UserIcon, keywords: 'name avatar bio handle display' },
      { id: 'privacy',     label: 'Privacy & Data',     icon: Eye,      keywords: 'privacy gdpr export delete account followers leaderboard marketing dms' },
      { id: 'language',    label: 'Language & Region',  icon: Globe,    keywords: 'currency timezone tz locale number format usd eur gbp' },
      { id: 'appearance',  label: 'Appearance',         icon: Sliders,  keywords: 'theme dark light density compact comfortable display' },
    ],
  },
  {
    id: 'security', label: 'Security', icon: ShieldCheck,
    sections: [
      { id: 'two-factor', label: 'Two-Factor & Password', icon: Lock,     keywords: '2fa mfa totp authenticator password security' },
      { id: 'api-keys',   label: 'API Keys',              icon: Key,      keywords: 'api keys integrations scripts python rest' },
      { id: 'sessions',   label: 'Active Sessions',       icon: Activity, keywords: 'devices logged in sign out remote sessions' },
      { id: 'login-log',  label: 'Login History',         icon: Clock,    keywords: 'login history activity audit ip device sign-in' },
    ],
  },
  {
    id: 'trading', label: 'Trading', icon: Zap,
    sections: [
      { id: 'trading', label: 'Trading Defaults', icon: Sliders, keywords: 'slippage gas window mev smart routing aggregator' },
      { id: 'risk',    label: 'Risk Rules',       icon: Shield,  keywords: 'risk daily loss stop drawdown trailing halt position' },
    ],
  },
  {
    id: 'notif', label: 'Notifications & Wallets', icon: Bell,
    sections: [
      { id: 'notifications', label: 'My Alerts', icon: Bell,    keywords: 'alerts my notifications email telegram discord webhook pnl trade' },
      { id: 'connections',   label: 'Exchanges & RPC',   icon: Network, keywords: 'exchange binance coinbase kraken bybit api keys rpc' },
      { id: 'wallet',        label: 'Payout Wallet',     icon: Wallet,  keywords: 'wallet payout address tips usdc creator earnings' },
    ],
  },
  {
    id: 'billing', label: 'Billing', icon: CreditCard,
    sections: [
      { id: 'subscription', label: 'Subscription', icon: CreditCard, keywords: 'plan tier upgrade billing invoice stripe cancel' },
      { id: 'tax-export',   label: 'Tax Export',   icon: BarChart3,  keywords: 'tax export csv accountant irs' },
    ],
  },
  {
    id: 'beta', label: 'Labs', icon: BadgeCheck,
    sections: [
      { id: 'beta-features', label: 'Beta Features', icon: BadgeCheck, keywords: 'beta preview experimental early access labs' },
    ],
  },
  {
    id: 'admin', label: 'Admin', icon: Terminal, adminOnly: true,
    sections: [
      { id: 'admin-health', label: 'System Health',  icon: Activity, keywords: 'admin health uptime status' },
      { id: 'admin-ops',    label: 'Operations',     icon: Terminal, keywords: 'admin operations orchestrator manual trigger' },
      { id: 'admin-about',  label: 'About Platform', icon: Info,     keywords: 'admin about version build environment' },
    ],
  },
];

// Legacy tab id → new section id mapping so deep-links like
// /settings?tab=subscription keep working.
const LEGACY_TAB_MAP = {
  general:       'profile',
  trading:       'trading',
  risk:          'risk',
  notifications: 'notifications',
  connections:   'connections',
  security:      'two-factor',
  wallet:        'wallet',
  subscription:  'subscription',
};

// ─── Setup-completion checklist ──────────────────────────────────────────────
// Drives the hero progress bar and the green/amber check marks in the rail.
// Returns { done, total, items: [{key,label,done,sectionId,group}] }.
function computeSetupChecklist({ user, cloudConfig, mfaStatus, hasPayoutWallet }) {
  const items = [
    { key: 'name',     label: 'Set your display name', done: !!user?.name,                       sectionId: 'profile',    group: 'account'  },
    { key: 'mfa',      label: 'Enable two-factor auth', done: !!mfaStatus?.mfaEnabled,            sectionId: 'two-factor', group: 'security' },
    { key: 'email',    label: 'Confirm notification email', done: !!cloudConfig?.NOTIF_EMAIL_ADDR, sectionId: 'notifications', group: 'notif' },
    { key: 'risk',     label: 'Set a daily-loss limit', done: !!cloudConfig?.MAX_DAILY_LOSS_PCT,  sectionId: 'risk',       group: 'trading'  },
    { key: 'wallet',   label: 'Add a payout wallet',    done: !!hasPayoutWallet,                  sectionId: 'wallet',     group: 'notif'    },
  ];
  return { items, done: items.filter(i => i.done).length, total: items.length };
}

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  // /api/health is admin-only — only poll when the current user is admin,
  // otherwise non-admins would burn quota on guaranteed 403s.
  const { health, loading: healthLoading, refresh } = useSystemHealth(isAdmin);

  // Sections the current user is allowed to see. Admin group filtered out
  // for non-admins. Each section keeps `comingSoon` metadata so we can
  // render the placeholder card consistently.
  const visibleGroups = GROUPS.filter(g => !g.adminOnly || isAdmin);
  const allSections = visibleGroups.flatMap(g => g.sections.map(s => ({ ...s, group: g.id })));

  // Resolve the initial section from ?tab=... (mapping legacy ids).
  const initialFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('tab') || params.get('section');
    if (!raw) return 'profile';
    return LEGACY_TAB_MAP[raw] || (allSections.some(s => s.id === raw) ? raw : 'profile');
  };
  const [activeSection, setActiveSection] = useState(initialFromUrl);

  // Respond to URL changes (e.g. clicking "Enable MFA now →" elsewhere).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('tab') || params.get('section');
    if (raw) {
      const mapped = LEGACY_TAB_MAP[raw] || raw;
      if (allSections.some(s => s.id === mapped)) setActiveSection(mapped);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const [toast, setToast] = useState(null);
  const [cloudConfig, setCloudConfig] = useState({});
  const [configLoading, setConfigLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [hasPayoutWallet, setHasPayoutWallet] = useState(false);
  const [search, setSearch] = useState('');
  // Read the user's density preference (set in Appearance section) and
  // apply it to the document root so per-page CSS can tighten or relax
  // spacing. The Settings page itself uses tighter section gaps when
  // compact mode is active — proof-of-concept for the density system.
  const [density, setDensity] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEYS.density) || 'comfortable';
      document.documentElement.setAttribute('data-density', v);
      return v;
    } catch { return 'comfortable'; }
  });
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEYS.density) {
        setDensity(e.newValue || 'comfortable');
        document.documentElement.setAttribute('data-density', e.newValue || 'comfortable');
      }
    }
    function onCustom(e) {
      if (e.detail) {
        setDensity(e.detail);
        document.documentElement.setAttribute('data-density', e.detail);
      }
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('haythix:density-change', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('haythix:density-change', onCustom);
    };
  }, []);

  // Load cloud config on mount + handle Stripe-checkout success redirect
  useEffect(() => {
    loadCloudSettings().then(cfg => {
      setCloudConfig(cfg);
      setConfigLoading(false);
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      window.history.replaceState({}, '', '/settings?section=subscription');
      setActiveSection('subscription');
      setTimeout(() => setToast('Subscription activated! Your new plan is now active.'), 500);
      setTimeout(() => refreshUser(), 4000);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort fetch of MFA + payout-wallet status so the checklist is accurate.
  useEffect(() => {
    getMfaStatus().then(setMfaStatus).catch(() => setMfaStatus({ mfaEnabled: false }));
    // /wallet/mine returns the user's connected chains; treat presence of
    // any connected address OR a primary payoutWallet as "wallet set."
    (async () => {
      try {
        const apiBase = import.meta.env.VITE_API_URL || '/api';
        const r = await fetch(`${apiBase}/wallet/mine`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        const d = await r.json();
        const ok = !!(d?.data?.payoutWallet?.address) ||
                   !!(d?.data?.connected?.length);
        setHasPayoutWallet(ok);
      } catch {}
    })();
  }, []);

  const showToast = useCallback((msg) => {
    setToast(typeof msg === 'string' ? msg : msg?.text || '');
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Reflect section changes in the URL so links and refresh work.
  function selectSection(id) {
    setActiveSection(id);
    const params = new URLSearchParams(location.search);
    params.set('section', id);
    params.delete('tab');
    navigate(`/settings?${params.toString()}`, { replace: true });
  }

  const checklist = computeSetupChecklist({ user, cloudConfig, mfaStatus, hasPayoutWallet });
  const pct = checklist.total ? Math.round((checklist.done / checklist.total) * 100) : 0;

  // Search across labels + keyword index. Splitting the query on whitespace
  // and requiring every token to match somewhere gives us multi-word search
  // ("daily loss" → matches Risk Rules) without needing a real indexer.
  const q = search.toLowerCase().trim();
  const tokens = q ? q.split(/\s+/) : [];
  const matchesSearch = (s) => {
    if (!q) return true;
    const haystack = `${s.label} ${s.id} ${s.keywords || ''}`.toLowerCase();
    return tokens.every(t => haystack.includes(t));
  };

  const cfgProps = { cloudConfig, onToast: showToast };
  const tabProps = { health, loading: healthLoading, refresh, onToast: showToast };

  // Render the active section. If marked coming-soon, render placeholder.
  function renderActive() {
    const meta = allSections.find(s => s.id === activeSection);
    if (meta?.comingSoon) {
      return <ComingSoonSection phase={meta.comingSoon} title={meta.label} description={meta.desc} />;
    }
    if (configLoading && ['trading', 'risk', 'notifications'].includes(activeSection)) {
      return <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>;
    }
    switch (activeSection) {
      case 'profile':        return <ProfileSection onToast={showToast} />;
      case 'privacy':        return <PrivacyDataSection onToast={showToast} />;
      case 'language':       return <LanguageRegionSection onToast={showToast} />;
      case 'appearance':     return <AppearanceSection onToast={showToast} />;
      case 'two-factor':     return <SecurityTab onToast={showToast} />;
      case 'api-keys':       return <ApiKeysSection onToast={showToast} />;
      case 'sessions':       return <SessionsPlaceholderSection />;
      case 'login-log':      return <LoginHistorySection onToast={showToast} />;
      case 'trading':        return <TradingTab {...cfgProps} />;
      case 'risk':           return <RiskTab {...cfgProps} />;
      case 'notifications':  return <NotificationsTab {...cfgProps} />;
      case 'connections':    return <ConnectionsTab onToast={showToast} />;
      case 'wallet':         return <PayoutWalletTab onToast={showToast} />;
      case 'subscription':   return <SubscriptionTab />;
      case 'tax-export':     return <TaxExportSection onToast={showToast} />;
      case 'beta-features':  return <BetaFeaturesSection onToast={showToast} />;
      case 'admin-health':   return isAdmin ? <SystemHealthSection {...tabProps} /> : null;
      case 'admin-ops':      return isAdmin ? <ManualOpsSection onToast={showToast} refresh={refresh} /> : null;
      case 'admin-about':    return isAdmin ? <AboutSection /> : null;
      default:               return <ProfileSection onToast={showToast} />;
    }
  }

  // Settings page tightens section gaps in compact mode.
  const pageSpacing = density === 'compact' ? 'space-y-4' : 'space-y-6';

  return (
    <div className={`${pageSpacing} animate-fade-in max-w-7xl`}>

      {/* ─── Premium hero ──────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-violet-500/30">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Settings
                {isAdmin && (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    Admin
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Account, security, trading defaults, and integrations — all in one place.
              </p>
            </div>
          </div>

          {/* Setup completion */}
          <div className="bg-dark-700/40 border border-violet-500/20 rounded-2xl px-5 py-3 min-w-[260px] max-w-xs">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Account setup</p>
              <p className="text-xs font-bold bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{pct}%</p>
            </div>
            <div className="w-full h-1.5 bg-dark-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[10px] text-gray-500 mt-1.5">{checklist.done} of {checklist.total} essentials done</p>
          </div>
        </div>

        {/* Quick actions — only show incomplete items */}
        {checklist.done < checklist.total && (
          <div className="flex flex-wrap gap-2 mt-5">
            {checklist.items.filter(i => !i.done).slice(0, 4).map(item => (
              <button key={item.key} onClick={() => selectSection(item.sectionId)}
                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-violet-500/10 text-violet-300 border border-violet-500/30 hover:bg-violet-500/20 transition-all flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ─── 2-column layout ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">

        {/* Left rail */}
        <aside className="lg:sticky lg:top-4 lg:self-start space-y-4">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search settings…"
              className="w-full bg-dark-800 border border-dark-700/50 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
          </div>

          {/* Groups */}
          <nav className="space-y-4">
            {visibleGroups.map(group => {
              const sectionsToShow = group.sections.filter(matchesSearch);
              if (sectionsToShow.length === 0) return null;
              const GroupIcon = group.icon;
              return (
                <div key={group.id}>
                  <p className="px-3 mb-1.5 text-[10px] uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1.5">
                    <GroupIcon className="w-3 h-3" /> {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {sectionsToShow.map(section => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.id;
                      const checklistItem = checklist.items.find(i => i.sectionId === section.id);
                      return (
                        <button key={section.id} onClick={() => selectSection(section.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            isActive
                              ? 'bg-violet-500/15 text-violet-200 border border-violet-500/30'
                              : 'text-gray-400 hover:text-white hover:bg-dark-700/50 border border-transparent'
                          }`}>
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="flex-1 text-left truncate">{section.label}</span>
                          {section.comingSoon && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-dark-700 text-gray-500">SOON</span>
                          )}
                          {checklistItem && (
                            checklistItem.done
                              ? <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                              : <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main pane */}
        <main className="min-w-0">
          {renderActive()}
        </main>
      </div>

      <Toast message={toast} onClose={() => setToast(null)} />
    </div>
  );
}
